/* -------------------------- LICENSES -------------------------- */

/*!
Do not copy, modify, and/or redistribute. The content presented herein may not, under any circumstances, 
be reproduced in whole or in any part or form without written permission from LearnHaus.
Copyright LearnHaus
*/

/* -------------------------- MAIN COMMENTS -------------------------- */

// Created By: Tibu
// Updated: 5/11/26
// Purpose: Handles epsilon demo results for event

/*
IMPORTANT NOTES:
-setCommonRedirectPaths dependent on navbar structure of member home - MIMICS redirects.js function in /member/learn
*/

/* -------------------------- KEY VARIABLES -------------------------- */

/* ------------- IMPORTED HELPERS ------------- */

// helper functions for page elements
import {
    getSameOriginReferrer,
    timestampToFormalDate,
    callParallelFunctions,
    scrollToElement,
    capFirstLetter,
    dateToDueFormat,
    convertToRGBA,
    scoreLvl,
    generateRadarChart,
    //setRankColorIcon,
    delay,
} from '../utils/canvas-helpers.js';

// helper functions for common popup elements
import {
    manageCornerPopup,
    manageErrorPopup,
    attachHelpText,
    processElementTreeWithHelp,
} from '../utils/popup-helpers.js';

// update vars: roleTranslation, rankThemes
import {
    setRoleTranslation,
    setRankThemes,
} from '../utils/update-common-vars.js';

// helper functions for api calls
import {
    fetchGET,
    fetchPOST,
} from '../utils/api-helpers.js';

// helper functions for epsilon demo action buttons
import {
    EPSILON_ACTION_ICONS,
    buildScoreShareLine,
    canUsePromptShareChoices,
    copyTextToClipboard,
    createPromptShareState,
    createShareChoiceButton,
    finishSentence,
    getFirstSentence,
    getPromptApiAvailability,
    getPromptShareLoadingText as getPromptShareLoadingTextForState,
    getScoreEmojiBar,
    getTopTakeawayMetricText as getTopTakeawayMetricTextForCanvas,
    hasContent,
    hasUserActivationForPromptApi,
    lowerFirst as lowerFirstRaw,
    resetShareChoiceState,
    resolveTextWithFallback,
    runPromptPipeline,
    setButtonState,
    setPromptShareStatus as setPromptShareStatusForState,
    setShareChoiceState,
    setupButton,
    shareOrCopyText,
    toPlainText,
    truncateTextAtBoundary as truncateText,
} from '../utils/demo-epsilon-action-helpers.js';

// helper functions for local storage (pageInfo)
import {
    getLocalStorageItem,
    isJsonStringObj,
    updateLocalStorageItem,
    ensureJSONString,
    updatePageInfo,
    setNestedProperty,
    updatePageInfoRedirects,
} from '../utils/local-storage-helpers.js';

// helper functions for page load
import {
    domReady,
    afterLoad,
} from '../utils/page-load-helpers.js';

// css animations
import '../styles/extra-animations.css';

// graph styling
import '../styles/graphs.css';

// SPEECH-TO-TEXT (ADD)
import SpeechToText from '../features/speech-to-text.js';

// PROGRESS-NAVBAR (ADD)
import ProgressNavbar from '../features/progress-navbar.js';

/* ------------- NECESSARY VARS ------------- */

// TEMPORARY - EXAMPLE DATA
let redirectPaths = {
    // .auth and .common created by setCommonRedirectPaths

    // redirect variables for in-page interactions
    pageElements: {},

    // redirect variables for page interactions - updated with page - TEMPORARY EXAMPLE DATA
    error: `${window.location.origin}/`,

};

/**
 * data about the user
 */
let userData = {
    // set on login - stored in local storage
    langRole: NaN, // 0 = edu student language (eg. class), 1 = prof business language (eg. team) (type: num, default: 0)
    activeRole: NaN, // 0 = stu, 1 = inst, 2 = org admin (type: num)
    profileInitials: ``, // user's name (type: str)
    timeZone: NaN, // user's timezone offset from UTC in min (type: num)

    // set on auth validation
    notifNum: NaN, // num of notifs (type: num)
    onboarding: {
        overall: NaN, // overall onboarding stage (type: num)
        page: NaN, // page onboarding stage (type: num, NaN-able)
    },
};

// student role language to professional role language - set in util
let roleTranslation = {};

// rank themes - set in util
let rankThemes = {};


/* ------------- PAGE UNIQUE VARS ------------- */

/**
 * contains all dynamic page data (current and future states)
 */
let allPageData = {
    // nav bar
    navBar: {
        shards: NaN, // current user's shard count (type: num)
        streaks: NaN, // current user's streak (type: num)
    },

    // canvas 
    canvas: {
        /** basic event/completion info (across multiple elements) */
        basic: {
            /** @type {String} admin name */
            name: ``,

            progress: {
                /** @type {Number} completed forms (0-n) */
                completed: NaN,

                /** @type {Number} expected forms (1-n) */
                expected: NaN,
            },

            form: {
                /** @type {String} URL path to event form (``-able) */
                link: ``,

                /** @type {String} event form pw (``-able) */
                pw: ``,
            },

            /** @type {String} event title */
            title: ``,

            /** @type {String} event date (in UNIX ms) */
            date: NaN,
        },

        /** optional share popup */
        share: {
            /** @type {Boolean} whether to show share section/popup */
            show: NaN,
        },

        /** optional update popup */
        update: {
            /** @type {Boolean} whether to show update section/popup */
            show: NaN,
        },

        /** takeaways section */
        takeaways: {
            /** @type {Array} data tiles */
            data: [
                {
                    /** @type {String} data title - largest info */
                    main: ``,

                    /** @type {String} data title - title */
                    label: ``,

                    /** @type {String} data title - sub text info */
                    subText: ``,

                    /** @type {String} data title - extra explanation */
                    help: ``,
                },
            ],

            /** @type {String} bottom line */
            bottomLine: ``,
        },

        /** brain stats section */
        scores: {
            c0: {
                /** @type {Number} construct id */
                id: 0, 

                /** @type {String} tabler svg code for icon */
                icon: ``,

                /** @type {String} construct title (default: Neural Power, ``-able) */
                title: ``,

                /** @type {Object} scores */
                num: {
                    /** @type {Number} lower limit raw score */
                    low: NaN,

                    /** @type {Number} most likely raw score */
                    mid: NaN,

                    /** @type {Number} upper limit raw score */
                    high: NaN,
                },

                /** @type {Object} theme items */
                theme: {
                    /** @type {Object} thematic colorings */
                    color: {
                        /** @type {String} hex color for icon/level */
                        text: ``,

                        /** @type {String} hex color for radar/theme */
                        highlight: ``,

                        /** @type {String} hex color for contrast to highlight */
                        contrast: ``,
                    },
                },

                /** @type {Object} construct description */
                description: {
                    /** @type {Object} score meaning */
                    meaning: {
                        /** @type {String} meaning content */
                        content: ``,
                    },

                    /** @type {Object} score compliments */
                    compliments: {
                        /** @type {String} compliment content */
                        content: ``,
                    },

                    /** @type {Object} score criticisms */
                    criticisms: {
                        /** @type {String} criticism content */
                        content: ``,
                    },

                    /** @type {Object} construct definition */
                    definition: {
                        /** @type {String} construct definition content */
                        content: ``,
                    },
                },

                /** @type {Array} construct scores distribution */
                distribution: [
                    {
                        /** @type {String} dist item name */
                        name: ``,

                        /** @type {Number} dist item number */
                        number: NaN,
                    },
                ],

                /** @type {Array} sub constructs - can be empty */
                subConstructs: [
                    {
                        /** @type {Number} construct id */
                        id: 1, 

                        /** @type {String} tabler svg code for icon */
                        icon: ``,

                        /** @type {String} construct title (default: Neural Power, ``-able) */
                        title: ``,

                        /** @type {Object} scores */
                        num: {
                            /** @type {Number} lower limit raw score */
                            low: NaN,

                            /** @type {Number} most likely raw score */
                            mid: NaN,

                            /** @type {Number} upper limit raw score */
                            high: NaN,
                        },

                        /** @type {Object} theme items */
                        theme: {
                            /** @type {Object} thematic colorings */
                            color: {
                                /** @type {String} hex color for icon/level */
                                text: ``,

                                /** @type {String} hex color for radar/theme */
                                highlight: ``,

                                /** @type {String} hex color for contrast to highlight */
                                contrast: ``,
                            },
                        },

                        /** @type {Object} construct description */
                        description: {
                            /** @type {Object} score meaning */
                            meaning: {
                                /** @type {String} meaning content */
                                content: ``,
                            },

                            /** @type {Object} score compliments */
                            compliments: {
                                /** @type {String} compliment content */
                                content: ``,
                            },

                            /** @type {Object} score criticisms */
                            criticisms: {
                                /** @type {String} criticism content */
                                content: ``,
                            },

                            /** @type {Object} construct definition */
                            definition: {
                                /** @type {String} construct definition content */
                                content: ``,
                            },
                        },

                        /** @type {Array} construct scores distribution */
                        distribution: [
                            {
                                /** @type {String} dist item name */
                                name: ``,

                                /** @type {Number} dist item number */
                                number: NaN,
                            },
                        ],

                        /** @type {Array} sub constructs - can be empty */
                        subConstructs: [],
                    },
                    {
                        /** @type {Number} construct id */
                        id: 2, 

                        /** @type {String} tabler svg code for icon */
                        icon: ``,

                        /** @type {String} construct title (default: Neural Power, ``-able) */
                        title: ``,

                        /** @type {Object} scores */
                        num: {
                            /** @type {Number} lower limit raw score */
                            low: NaN,

                            /** @type {Number} most likely raw score */
                            mid: NaN,

                            /** @type {Number} upper limit raw score */
                            high: NaN,
                        },

                        /** @type {Object} theme items */
                        theme: {
                            /** @type {Object} thematic colorings */
                            color: {
                                /** @type {String} hex color for icon/level */
                                text: ``,

                                /** @type {String} hex color for radar/theme */
                                highlight: ``,

                                /** @type {String} hex color for contrast to highlight */
                                contrast: ``,
                            },
                        },

                        /** @type {Object} construct description */
                        description: {
                            /** @type {Object} score meaning */
                            meaning: {
                                /** @type {String} meaning content */
                                content: ``,
                            },

                            /** @type {Object} score compliments */
                            compliments: {
                                /** @type {String} compliment content */
                                content: ``,
                            },

                            /** @type {Object} score criticisms */
                            criticisms: {
                                /** @type {String} criticism content */
                                content: ``,
                            },

                            /** @type {Object} construct definition */
                            definition: {
                                /** @type {String} construct definition content */
                                content: ``,
                            },
                        },

                        /** @type {Array} construct scores distribution */
                        distribution: [
                            {
                                /** @type {String} dist item name */
                                name: ``,

                                /** @type {Number} dist item number */
                                number: NaN,
                            },
                        ],

                        /** @type {Array} sub constructs - can be empty */
                        subConstructs: [],
                    },
                    {
                        /** @type {Number} construct id */
                        id: 3, 

                        /** @type {String} tabler svg code for icon */
                        icon: ``,

                        /** @type {String} construct title (default: Neural Power, ``-able) */
                        title: ``,

                        /** @type {Object} scores */
                        num: {
                            /** @type {Number} lower limit raw score */
                            low: NaN,

                            /** @type {Number} most likely raw score */
                            mid: NaN,

                            /** @type {Number} upper limit raw score */
                            high: NaN,
                        },

                        /** @type {Object} theme items */
                        theme: {
                            /** @type {Object} thematic colorings */
                            color: {
                                /** @type {String} hex color for icon/level */
                                text: ``,

                                /** @type {String} hex color for radar/theme */
                                highlight: ``,

                                /** @type {String} hex color for contrast to highlight */
                                contrast: ``,
                            },
                        },

                        /** @type {Object} construct description */
                        description: {
                            /** @type {Object} score meaning */
                            meaning: {
                                /** @type {String} meaning content */
                                content: ``,
                            },

                            /** @type {Object} score compliments */
                            compliments: {
                                /** @type {String} compliment content */
                                content: ``,
                            },

                            /** @type {Object} score criticisms */
                            criticisms: {
                                /** @type {String} criticism content */
                                content: ``,
                            },

                            /** @type {Object} construct definition */
                            definition: {
                                /** @type {String} construct definition content */
                                content: ``,
                            },
                        },

                        /** @type {Array} construct scores distribution */
                        distribution: [
                            {
                                /** @type {String} dist item name */
                                name: ``,

                                /** @type {Number} dist item number */
                                number: NaN,
                            },
                        ],

                        /** @type {Array} sub constructs - can be empty */
                        subConstructs: [],
                    },
                    {
                        /** @type {Number} construct id */
                        id: 4, 

                        /** @type {String} tabler svg code for icon */
                        icon: ``,

                        /** @type {String} construct title (default: Neural Power, ``-able) */
                        title: ``,

                        /** @type {Object} scores */
                        num: {
                            /** @type {Number} lower limit raw score */
                            low: NaN,

                            /** @type {Number} most likely raw score */
                            mid: NaN,

                            /** @type {Number} upper limit raw score */
                            high: NaN,
                        },

                        /** @type {Object} theme items */
                        theme: {
                            /** @type {Object} thematic colorings */
                            color: {
                                /** @type {String} hex color for icon/level */
                                text: ``,

                                /** @type {String} hex color for radar/theme */
                                highlight: ``,

                                /** @type {String} hex color for contrast to highlight */
                                contrast: ``,
                            },
                        },

                        /** @type {Object} construct description */
                        description: {
                            /** @type {Object} score meaning */
                            meaning: {
                                /** @type {String} meaning content */
                                content: ``,
                            },

                            /** @type {Object} score compliments */
                            compliments: {
                                /** @type {String} compliment content */
                                content: ``,
                            },

                            /** @type {Object} score criticisms */
                            criticisms: {
                                /** @type {String} criticism content */
                                content: ``,
                            },

                            /** @type {Object} construct definition */
                            definition: {
                                /** @type {String} construct definition content */
                                content: ``,
                            },
                        },

                        /** @type {Array} construct scores distribution */
                        distribution: [
                            {
                                /** @type {String} dist item name */
                                name: ``,

                                /** @type {Number} dist item number */
                                number: NaN,
                            },
                        ],

                        /** @type {Array} sub constructs - can be empty */
                        subConstructs: [],
                    },
                ],
            },
        },

        /** common themes section */
        themes: {
            /** @type {Array} common themes data (length: 3) */
            data: [
                {
                    /** @type {String} theme/idea title */
                    title: ``,

                    /** @type {String} theme/idea detail */
                    detail: ``,

                    /** @type {Number} theme/idea frequency (0 to 1) */
                    frequency: NaN,

                    /** @type {String} theme/idea evidence snippets */
                    evidence: ``,
                },
            ],
        },

        /** event comments section */
        comments: {
            /** @type {Object} compliments */
            compliments: {
                /** @type {Array} compliment tiles (length: 1-n) */
                tiles: [
                    {
                        /** @type {String} tile title */
                        title: ``,

                        /** @type {String} tile detail */
                        detail: ``,
                    },
                ],
            },
            /** @type {Object} criticisms */
            criticisms: {
                /** @type {Array} criticism tiles (length: 1-n) */
                tiles: [
                    {
                        /** @type {String} tile title */
                        title: ``,

                        /** @type {String} tile detail */
                        detail: ``,
                    },
                ],
            },
        },

        /** notable ideas section */
        review: {
            /** @type {Object} tiles management */
            manage: {
                /** @type {Number} current review tile (1-n) */
                selection: 1,
            },

            /** @type {Array} tiles data (length: 1-n) */
            tiles: [
                {
                    /** @type {String} item title */
                    title: ``,

                    /** @type {String} item evidence (no html/quotes) */
                    evidence: ``,

                    /** @type {String} item importance */
                    importance: ``,
                },
            ],
        },

        /** next steps section */
        next: {
            /** @type {Object} tiles management */
            manage: {
                /** @type {Number} current tile (1-n) */
                selection: 1,
            },

            /** @type {Array} next steps tiles data */
            tiles: [
                {
                    /** @type {String} tile tag */
                    tag: ``,

                    /** @type {String} tile title */
                    title: ``,

                    /** @type {String} tile content text */
                    content: ``,
                },
            ],
        },
    },

    // popup 
    popups: {
        // TEMPORARY
        error: {
            pic: NaN, // 0 = warning (type: num)
            mainText: ``, // warning header (type: string)
            subText: ``, // warning details (type: string)
            action: {
                show: true, // show action button (type: boolean)
                text: ``, // action button text (type: string)
            },
            exit: {
                show: true, // show exit button (type: boolean)
            },
        },
    },
};

/* -------------------------- COMMON FUNCTIONS -------------------------- */

/* ------------- HANDLES PRE-RENDER ACTIONS ------------- */

/**
 * updates .auth and .common elements (member top nav and member left nav for learn section)
 * @param {object} redirectPaths  redirectPaths global page var
 * @returns {object} redirectPaths
 */
function setCommonRedirectPaths(redirectPaths) {
    let waitlist = `${window.location.origin}/`;

    // redirect variables for auth - static
    redirectPaths.auth = {
        login: `${window.location.origin}/`, // to login - relative
        redirectLogin: `${window.location.origin}/`, // to login redirect - relative
    };

    // redirect variables for page interactions (member learn) - static
    redirectPaths.commonElements = {
        topNav: {
            home: {
                div: waitlist,
            },
            social: {
                div: waitlist,
            },
            streaks: {
                div: waitlist,
            },
            shards: {
                div: waitlist,
            },
            notifs: {
                div: waitlist,
            },
            help: {
                div: waitlist,
            },
            profile: {
                div: waitlist,
            },
        },
        leftNav: {
            sections: {
                home: {
                   div: waitlist,
                },
                activeTime: {
                    div: waitlist,
                },
                pastScans: {
                    div: waitlist,
                },
                score: {
                    div: waitlist,
                },
                monthlyActivity: {
                    div: waitlist,
                },
            },
            actions: {
                scan: {
                    div: waitlist,
                },
                feedback: {
                    div: waitlist,
                },
            },
        },
    };

    return redirectPaths;
}

/**
 * checks to run with url/pageInfo before proceeding with viz
 * @returns {boolean} true if passed
 */
function runPageChecks() {
    // get url params
    let urlParams = new URLSearchParams(window.location.search);
    let id = urlParams.get('id'); // string

    // check for id
    if (id) {
        return true;
    } else { // on error
        console.error(`Unspecified Result`);
        let url = new URL(redirectPaths.error, window.location.origin); // handles both absolute and relative URLs
        let fullUrl = url.toString(); // get full URL string
        window.location.href = fullUrl; // navigate
        return false;
    }
}

/**
 * sets and checks user data before proceeding with viz
 * @returns {boolean} true if passed
 */
function runUserChecks() {
    if (userData.activeRole == 0) {
        return true;
    } else {
        return false;
    }
}

/**
 * calls page data and sets global var
 * @returns true/false depending on success
 */
async function calcPageData() {
    // error flag
    let errorF = false;

    // success or error handling
    let handleSuccess = (data) => { // success callback
        // set page data
        allPageData = data;
    };
    let handleError = (data) => { // error callback
        // error flag
        errorF = true;

        // log
        console.error(data);

        // popup
        manageErrorPopup(true, false, true, 0, 1, `Unable to access result.`, `Reload`);
    };

    // get result id and update/new bool
    let urlParams = new URLSearchParams(window.location.search);
    let id = urlParams.get('id'); // string
    let update = urlParams.get('new'); // string
    if (!id) {
        handleError('No linked result');
        return; // dont proceed
    }

    // get result data
    let awaitResult = fetchPOST(
        `https://xeda-uxmj-5kez.n7c.xano.io/api:Td_2JDsb/Demo_Epsilon_Event_Result`,
        {
            id: id,
            update: update === `1`
                ? 1
                : 0,
        },
        [],
        handleSuccess,
        handleError,
        {
            timeout: update === `1` ? 240000 : 45000,
        },
    );

    // remove update/new from URL
    if (urlParams.has(`new`)) {
        let url = new URL(window.location.href);
        url.searchParams.delete(`new`);
        window.history.replaceState(window.history.state, document.title, url.toString());
    }

    await awaitResult;

    // check flag
    if (errorF) return false;

    // Xano API: fetch and set page data
    // TEMPORARY - EXAMPLE DATA: 
    // await delay(Math.random() * 1500 + 2000);
    // allPageData = (() => ({
    //     // nav bar
    //     navBar: {
    //         shards: Math.round(Math.random() * 500), // current user's shard count (type: num)
    //         streaks: Math.round(Math.random() * 500), // current user's streak (type: num)
    //     },

    //     // canvas 
    //     canvas: {
    //         /** basic event/completion info (across multiple elements) */
    //         basic: {
    //             /** @type {String} admin name */
    //             name: `Pasteroulle`,

    //             progress: {
    //                 /** @type {Number} completed forms (0-n) */
    //                 completed: Math.round(Math.random() * 10),

    //                 /** @type {Number} expected forms (1-n) */
    //                 expected: Math.round(Math.random() * 50 + 3),
    //             },

    //             form: {
    //                 /** @type {String} URL path to event form (``-able) */
    //                 link: `https://forms.learnhaus.ai/r2-example`,

    //                 /** @type {String} event form pw (``-able) */
    //                 pw: Math.random() > 0.5 ? `guest123` : ``,
    //             },

    //             /** @type {String} event title */
    //             title: Math.random() > 0.5 ? `WBD Spring Workshop` : `SpringX May Training`,

    //             /** @type {String} event date (in UNIX ms) */
    //             date: Date.now() - (86400000 * Math.round(Math.random() * 10 + 1)),
    //         },

    //         /** optional share popup */
    //         share: {
    //             /** @type {Boolean} whether to show share section/popup */
    //             show: Math.random() > 0.5 ? true : false,
    //         },

    //         /** optional update popup */
    //         update: {
    //             /** @type {Boolean} whether to show update section/popup */
    //             show: Math.random() > 0.5 ? true : false,
    //         },

    //         /** takeaways section */
    //         takeaways: {
    //             /** @type {Array} data tiles */
    //             data: [
    //                 {
    //                     /** @type {String} data title - largest info */
    //                     main: Math.round(Math.random() * 999),

    //                     /** @type {String} data title - title */
    //                     label: `Engagement Time`,

    //                     /** @type {String} data title - sub text info */
    //                     subText: `participants spent enough time to form real impressions and start connecting their own thinking to the event`,

    //                     /** @type {String} data title - extra explanation */
    //                     help: `Total event time in hours, calculated from your submitted event context.`,
    //                 },
    //                 {
    //                     /** @type {String} data title - largest info */
    //                     main: (Math.random() * 4).toFixed(2),

    //                     /** @type {String} data title - title */
    //                     label: `Learning Rating`,

    //                     /** @type {String} data title - sub text info */
    //                     subText: `you reported limited learning, which may mean the event reinforced ideas you already understood`,

    //                     /** @type {String} data title - extra explanation */
    //                     help: `0-4 rating of how much you felt you learned, taken from your context response.`,
    //                 },
    //                 {
    //                     /** @type {String} data title - largest info */
    //                     main: (Math.random() * 4).toFixed(2),

    //                     /** @type {String} data title - title */
    //                     label: `Engagement Time`,

    //                     /** @type {String} data title - sub text info */
    //                     subText: `you felt meaningfully challenged, which often creates the right conditions for reflection and growth`,

    //                     /** @type {String} data title - extra explanation */
    //                     help: `0-4 rating of how challenging the event felt, taken from your context response.`,
    //                 },
    //             ],

    //             /** @type {String} bottom line */
    //             bottomLine: `Dynamic <b>example</b> bottom line about blah blah blah blah blah blah blah blah blah blah blah blah blah blah blah blah blah blah blah blah`,
    //         },

    //         /** brain stats section */
    //         scores: {
    //             c0: {
    //                 id: 0,
    //                 icon: `<svg  xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-topology-star"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M8 18a2 2 0 1 0 -4 0a2 2 0 0 0 4 0z" /><path d="M20 6a2 2 0 1 0 -4 0a2 2 0 0 0 4 0z" /><path d="M8 6a2 2 0 1 0 -4 0a2 2 0 0 0 4 0z" /><path d="M20 18a2 2 0 1 0 -4 0a2 2 0 0 0 4 0z" /><path d="M14 12a2 2 0 1 0 -4 0a2 2 0 0 0 4 0z" /><path d="M7.5 7.5l3 3" /><path d="M7.5 16.5l3 -3" /><path d="M13.5 13.5l3 3" /><path d="M16.5 7.5l-3 3" /></svg>`,
    //                 title: "Critical Reasoning",
    //                 num: {
    //                     low: 524,
    //                     mid: 557,
    //                     high: 590,
    //                 },
    //                 theme: {
    //                     color: {
    //                     text: "black",
    //                     highlight: "black",
    //                     contrast: "white",
    //                     },
    //                 },
    //                 description: {
    //                     meaning: {
    //                     content:
    //                         'Your reflection shows a solid, mid-to-strong ability to think through what you learned and connect it to how pitching works in practice. You did more than repeat the workshop topic: you explained the tradeoff between <span class="feedback-highlight">detail and clarity</span>, connected it to your own finance background, and used that idea again when describing <em>impact, evidence,</em> and the kind of audience you pitch to. That suggests you are able to organize information into a useful judgment, not just list facts.',
    //                     },
    //                     compliments: {
    //                     content:
    //                         'This was strongest in Answers 1–3, where you consistently argued that a pitch should be <strong>"simple and focused,"</strong> that <strong>"being detailed is not always the same as being persuasive,"</strong> and that the best pitches let you <span class="feedback-highlight">"quickly understand the problem, the solution, and why it mattered."</span> Answer 5 adds useful nuance about the <em>"balance"</em> between clear and vague.',
    //                     },
    //                     criticisms: {
    //                     content:
    //                         "The main thing that keeps the score from being higher is that the reflection stays fairly general and mostly one-directional, so it is hard to see whether you tested the idea against strong counterexamples or specific workshop moments. <strong>Next time, name one concrete pitch decision you would make differently and explain why it is better than an alternative.</strong>",
    //                     },
    //                     definition: {
    //                     content:
    //                         "<strong>Critical Reasoning</strong> means <strong>the overall quality of a learner’s thinking</strong>. It reflects how well someone understands information, examines reasoning, builds arguments, and makes thoughtful judgments. In work environments, critical reasoning shows up when people ask useful questions, notice gaps or assumptions, compare options, explain their thinking clearly, and make decisions that are grounded in evidence rather than impulse. This skill matters because it supports stronger productivity, deeper learning, and better life choices by helping people think clearly, solve problems, avoid weak conclusions, and adapt when situations are complex or uncertain.",
    //                     },
    //                 },
    //                 distribution: [],
    //                 subConstructs: [
    //                     {
    //                         id: 1,
    //                         icon: `<svg  xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-brand-react"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M6.306 8.711c-2.602 .723 -4.306 1.926 -4.306 3.289c0 2.21 4.477 4 10 4c.773 0 1.526 -.035 2.248 -.102" /><path d="M17.692 15.289c2.603 -.722 4.308 -1.926 4.308 -3.289c0 -2.21 -4.477 -4 -10 -4c-.773 0 -1.526 .035 -2.25 .102" /><path d="M6.305 15.287c-.676 2.615 -.485 4.693 .695 5.373c1.913 1.105 5.703 -1.877 8.464 -6.66c.387 -.67 .733 -1.339 1.036 -2" /><path d="M17.694 8.716c.677 -2.616 .487 -4.696 -.694 -5.376c-1.913 -1.105 -5.703 1.877 -8.464 6.66c-.387 .67 -.733 1.34 -1.037 2" /><path d="M12 5.424c-1.925 -1.892 -3.82 -2.766 -5 -2.084c-1.913 1.104 -1.226 5.877 1.536 10.66c.386 .67 .793 1.304 1.212 1.896" /><path d="M12 18.574c1.926 1.893 3.821 2.768 5 2.086c1.913 -1.104 1.226 -5.877 -1.536 -10.66c-.375 -.65 -.78 -1.283 -1.212 -1.897" /><path d="M11.5 12.866a1 1 0 1 0 1 -1.732a1 1 0 0 0 -1 1.732z" /></svg>`,
    //                         title: "Information Understanding",
    //                         num: {
    //                             low: 526,
    //                             mid: 559,
    //                             high: 592,
    //                         },
    //                         theme: {
    //                             color: {
    //                             text: "#1419fc",
    //                             highlight: "#1419fc",
    //                             contrast: "white",
    //                             },
    //                         },
    //                         description: {
    //                             meaning: {
    //                             content:
    //                                 'You show a good grasp of the workshop message and its implications, especially the idea that <span class="feedback-highlight">investors need the story first and the numbers second</span>. You seem to understand not just what was said, but why it mattered for startup pitching and for your own communication style. That is a sign you can take information from a workshop and translate it into practical meaning.',
    //                             },
    //                             compliments: {
    //                             content:
    //                                 'Answers 1 and 3 most clearly show understanding: you captured the workshop point that a pitch should make the <strong>"problem, solution, market opportunity and team"</strong> clear quickly, and that investors want <span class="feedback-highlight">"the main story first"</span> before details. Answer 2 shows you understood the implication for communication: <strong>"being detailed is not always the same as being persuasive."</strong>',
    //                             },
    //                             criticisms: {
    //                             content:
    //                                 "The score is not higher because your answers do not include many specific details from the workshop itself, so I have to infer your understanding from your summary rather than from precise examples. <strong>To improve quickly next time, pull out one exact concept, slide, or pitch moment and explain what it meant in your own words.</strong>",
    //                             },
    //                             definition: {
    //                             content:
    //                                 "<strong>Information Understanding</strong> means <strong>making sense of what information means</strong>. It reflects how well a learner grasps the meaning, relevance, context, and possible implications of information, experiences, data, or feedback. In work environments, this skill is displayed when someone accurately interprets instructions, identifies what details matter most, connects new information to the larger goal, and recognizes how information may affect a task, project, or decision. It is important for productivity, learning, and life because people cannot reason well, act effectively, or grow from experience unless they first understand what they are working with.",
    //                             },
    //                         },
    //                         distribution: [],
    //                         subConstructs: [],
    //                         },
    //                     {
    //                         id: 2,
    //                         icon: `<svg  xmlns="http://www.w3.org/2000/svg"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-message-2-search"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M8 9h8" /><path d="M8 13h5" /><path d="M12 21l-.5 -.5l-2.5 -2.5h-3a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v4.5" /><path d="M18 18m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" /><path d="M20.2 20.2l1.8 1.8" /></svg>`,
    //                         title: "Argument Analyzing",
    //                         num: {
    //                             low: 521,
    //                             mid: 554,
    //                             high: 587,
    //                         },
    //                         theme: {
    //                             color: {
    //                             text: "#dc2528",
    //                             highlight: "#dc2528",
    //                             contrast: "white",
    //                             },
    //                         },
    //                         description: {
    //                             meaning: {
    //                             content:
    //                                 'You do a decent job of examining why one approach to pitching works better than another. In particular, you compare <span class="feedback-highlight">detail versus clarity</span>, notice that pitches with too much technical or financial explanation can lose attention, and recognize that different audiences may react differently.',
    //                             },
    //                             compliments: {
    //                             content:
    //                                 'Answer 3 boosts this score most because you compare pitches with <span class="feedback-highlight">"the most information"</span> against ones where you could <strong>"quickly understand the problem"</strong> and note that being too detailed <em>"made it harder to stay engaged."</em> Answer 5 also shows analysis of tradeoffs by asking how the balance changes for an <strong>"early-stage investor"</strong> versus a <strong>"finance-focused investor."</strong>',
    //                             },
    //                             criticisms: {
    //                             content:
    //                                 "The main limitation is that your analysis is still fairly broad and based on general impressions, so it does not yet deeply test assumptions, counterarguments, or competing explanations. <strong>A quick way to improve is to name one objection to your own view and answer it directly.</strong>",
    //                             },
    //                             definition: {
    //                             content:
    //                                 "<strong>Argument Analysis</strong> means <strong>examining the strength of reasoning</strong>. It reflects how well a learner evaluates reasons, evidence, assumptions, tradeoffs, limitations, and weaknesses in ideas or claims. In work environments, argument analysis appears when someone questions whether evidence is reliable, spots hidden assumptions, weighs pros and cons, identifies risks, and challenges ideas respectfully before action is taken. This skill is important because it helps people avoid poor decisions, improve the quality of plans, learn from multiple perspectives, and become more thoughtful problem-solvers in both professional and everyday situations.",
    //                             },
    //                         },
    //                         distribution: [],
    //                         subConstructs: [],
    //                     },
    //                     {
    //                         id: 3,
    //                         icon: `<svg  xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-cash-edit"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M7 15h-3a1 1 0 0 1 -1 -1v-8a1 1 0 0 1 1 -1h12a1 1 0 0 1 1 1v3" /><path d="M11 19h-3a1 1 0 0 1 -1 -1v-8a1 1 0 0 1 1 -1h12a1 1 0 0 1 1 1v1.25" /><path d="M18.42 15.61a2.1 2.1 0 1 1 2.97 2.97l-3.39 3.42h-3v-3z" /></svg>`,
    //                         title: "Argument Creating",
    //                         num: {
    //                             low: 526,
    //                             mid: 559,
    //                             high: 592,
    //                         },
    //                         theme: {
    //                             color: {
    //                             text: "black",
    //                             highlight: "#ffbe08",
    //                             contrast: "black",
    //                             },
    //                         },
    //                         description: {
    //                             meaning: {
    //                             content:
    //                                 'Your reflection shows that you can build a clear position and explain it in a way that makes sense. You state a thesis early: a strong pitch should be <span class="feedback-highlight">"simple and focused"</span>, and you support it with examples from the workshop, your own background, and an analogy in Answer 4.',
    //                             },
    //                             compliments: {
    //                             content:
    //                                 'Answer 1 is the clearest example of argument creation: you make the claim that a pitch should be <strong>"simple and focused"</strong> and explain that investors need the <span class="feedback-highlight">"problem, solution, market opportunity and team"</span> quickly. Answer 2 reinforces the same position with <strong>"start with the key message."</strong> Answer 4 adds a fresh framing with the workshop as a <em>"meerkat,"</em> showing you can also create a metaphor to communicate meaning.',
    //                             },
    //                             criticisms: {
    //                             content:
    //                                 "The score is held back because the argument is mostly explanatory rather than strongly original; several answers repeat the same core idea without expanding it into a more developed line of reasoning. <strong>To improve next time, turn your main point into a sharper claim plus one concrete example that proves it.</strong>",
    //                             },
    //                             definition: {
    //                             content:
    //                                 "<strong>Argument Creation</strong> means <strong>building a clear and supported position</strong>. It reflects how well a learner develops their own explanation, interpretation, conclusion, or point of view using relevant information and logical reasoning. In work environments, this skill is displayed when someone presents a recommendation, explains why a decision makes sense, writes a persuasive proposal, defends a solution, or connects evidence to a clear conclusion. It matters for productivity, learning, and life because strong argument creation helps people communicate ideas effectively, influence decisions responsibly, clarify their own thinking, and contribute meaningfully to shared goals.",
    //                             },
    //                         },
    //                         distribution: [],
    //                         subConstructs: [],
    //                     },
    //                     {
    //                         id: 4,
    //                         icon: `<svg  xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-route-square"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M3 17h4v4h-4z" /><path d="M17 3h4v4h-4z" /><path d="M11 19h5.5a3.5 3.5 0 0 0 0 -7h-8a3.5 3.5 0 0 1 0 -7h4.5" /></svg>`,
    //                         title: "Decision Making",
    //                         num: {
    //                             low: 525,
    //                             mid: 558,
    //                             high: 591,
    //                         },
    //                         theme: {
    //                             color: {
    //                             text: "green",
    //                             highlight: "green",
    //                             contrast: "white",
    //                             },
    //                         },
    //                         description: {
    //                             meaning: {
    //                             content:
    //                                 'You show a practical ability to turn reflection into next steps. Rather than stopping at insight, you say you will <span class="feedback-highlight">"start with the key message"</span> and only add detail when it supports the pitch, which is a real decision about how you will act differently.',
    //                             },
    //                             compliments: {
    //                             content:
    //                                 'Answer 2 most strongly supports this score because you commit to <strong>"start with the key message"</strong> and add detail only where it <em>"supports the overall pitch."</em> Answer 5 also shows decision thinking by asking how the balance changes for different audiences like an <strong>"early-stage investor"</strong> or a <strong>"finance-focused investor."</strong>',
    //                             },
    //                             criticisms: {
    //                             content:
    //                                 "The score is not higher because you have not yet turned that insight into a fully worked plan, such as how you would test the balance in a real pitch or how you would decide what to cut. <strong>To improve quickly next time, write one specific rule for yourself, such as what must always come first in a pitch.</strong>",
    //                             },
    //                             definition: {
    //                             content:
    //                                 "<strong>Decision Making</strong> means <strong>moving from thinking to a justified next step</strong>. It reflects how well a learner uses understanding and analysis to reach a judgment, make a choice, identify an implication, or decide what should happen next. In work environments, decision making is shown when someone prioritizes tasks, chooses between options, recommends a course of action, considers consequences, and acts with appropriate confidence despite uncertainty. This skill is important because productivity depends on turning thought into action, learning depends on reflecting and adjusting choices, and life often requires making reasonable decisions with incomplete information.",
    //                             },
    //                         },
    //                         distribution: [],
    //                         subConstructs: [],
    //                     },
    //                 ],
    //             },
    //         },

    //         /** common themes section */
    //         themes: {
    //             /** @type {Array} common themes data (length: 3) */
    //             data: [
    //                 {
    //                     /** @type {String} theme/idea title */
    //                     title: `Applying Learning to Real Work`,

    //                     /** @type {String} theme/idea detail */
    //                     detail: `Many reflections describe how the training connects to everyday workplace situations. Employees often mention using the ideas in meetings, customer conversations, project planning, leadership moments, or team communication. This theme suggests participants are not only remembering the training content, but also thinking about how to transfer it into practical behavior.`,

    //                     /** @type {Number} theme/idea frequency (0 to 1) */
    //                     frequency: 0.82,

    //                     /** @type {String} theme/idea evidence snippets */
    //                     evidence: `"I can use this in my next team meeting"; "This applies directly to how I communicate with clients"; "I realized I need to slow down and listen before responding"; "The examples felt relevant to my daily work."`,
    //                 },
    //                 {
    //                     /** @type {String} theme/idea title */
    //                     title: `Clearer Communication and Active Listening`,

    //                     /** @type {String} theme/idea detail */
    //                     detail: `A common pattern is that employees reflect on the need to communicate more clearly, ask better questions, and listen more carefully before making assumptions. These reflections often focus on reducing misunderstandings, improving collaboration, and making conversations more productive across teams.`,

    //                     /** @type {Number} theme/idea frequency (0 to 1) */
    //                     frequency: 0.74,

    //                     /** @type {String} theme/idea evidence snippets */
    //                     evidence: `"I need to ask clarifying questions instead of assuming"; "Listening first can prevent conflict"; "Clear communication helps the whole team stay aligned"; "I want to be more intentional with how I give feedback."`,
    //                 },
    //                 {
    //                     /** @type {String} theme/idea title */
    //                     title: `Confidence Building and Growth Mindset`,

    //                     /** @type {String} theme/idea detail */
    //                     detail: `Many employees express increased confidence after the training, especially around handling challenging conversations, giving feedback, solving problems, or taking initiative. Reflections in this theme often show a growth mindset, where employees recognize that improvement comes through practice, feedback, and trying new approaches.`,

    //                     /** @type {Number} theme/idea frequency (0 to 1) */
    //                     frequency: 0.68,

    //                     /** @type {String} theme/idea evidence snippets */
    //                     evidence: `"I feel more confident handling difficult situations"; "This helped me see mistakes as learning opportunities"; "I want to practice this skill more"; "The training gave me a framework I can rely on."`,
    //                 },
    //             ],
    //         },

    //         /** event comments section */
    //         comments: {
    //             /** @type {Object} compliments */
    //             compliments: {
    //                 /** @type {Array} compliment tiles (length: 1-n) */
    //                 tiles: [
    //                     {
    //                         /** @type {String} tile title */
    //                         title: `Strong Practical Relevance`,

    //                         /** @type {String} tile detail */
    //                         detail: `Participants frequently felt the event connected well to their real work. Many comments suggest the examples, scenarios, and discussion prompts helped attendees see how the content could be applied immediately in their roles.`,
    //                     },
    //                     {
    //                         /** @type {String} tile title */
    //                         title: `Engaging Facilitation`,

    //                         /** @type {String} tile detail */
    //                         detail: `Several attendees responded positively to the facilitator’s energy, clarity, and ability to keep the session moving. The event appeared to feel approachable and interactive rather than overly lecture-based.`,
    //                     },
    //                     {
    //                         /** @type {String} tile title */
    //                         title: `Useful Peer Discussion`,

    //                         /** @type {String} tile detail */
    //                         detail: `Participants valued hearing from colleagues and comparing experiences. Group discussion seemed to help attendees understand the material from multiple perspectives and made the session feel more collaborative.`,
    //                     },
    //                 ],
    //             },

    //             /** @type {Object} criticisms */
    //             criticisms: {
    //                 /** @type {Array} criticism tiles (length: 1-n) */
    //                 tiles: [
    //                     {
    //                         /** @type {String} tile title */
    //                         title: `More Time for Practice`,

    //                         /** @type {String} tile detail */
    //                         detail: `A common improvement area is giving participants more time to apply the concepts during the event. Attendees may benefit from additional role plays, small-group exercises, or short practice rounds before moving into reflection or discussion.`,
    //                     },
    //                     {
    //                         /** @type {String} tile title */
    //                         title: `Clearer Takeaway Materials`,

    //                         /** @type {String} tile detail */
    //                         detail: `Some participants may leave with stronger retention if the event includes a concise takeaway sheet, checklist, or follow-up resource. This would make it easier for attendees to remember the key points and use them after the session.`,
    //                     },
    //                     {
    //                         /** @type {String} tile title */
    //                         title: `Better Pacing Between Sections`,

    //                         /** @type {String} tile detail */
    //                         detail: `The event could be improved by slightly adjusting the pacing. Some sections may feel quick when introducing new ideas, while discussion portions may benefit from firmer time boundaries to keep the full session balanced.`,
    //                     },
    //                 ],
    //             },
    //         },

    //         /** notable ideas section */
    //         review: {
    //             /** @type {Object} tiles management */
    //             manage: {
    //                 /** @type {Number} current review tile (1-n) */
    //                 selection: 1,
    //             },

    //             /** @type {Array} tiles data (length: 1-n) */
    //             tiles: [
    //                 {
    //                     /** @type {String} item title */
    //                     title: `Most Actionable Participant Insight`,

    //                     /** @type {String} item evidence (no html/quotes) */
    //                     evidence: `I realized I usually wait until a problem becomes urgent before asking for help. Next time, I want to raise smaller concerns earlier so the team has more options.`,

    //                     /** @type {String} item importance */
    //                     importance: `This is useful for the event leader because it shows the training helped a participant identify a specific behavior they can change immediately. It also suggests the session encouraged practical reflection rather than only general agreement.`,
    //                 },
    //                 {
    //                     /** @type {String} item title */
    //                     title: `Most Interesting Question`,

    //                     /** @type {String} item evidence (no html/quotes) */
    //                     evidence: `How do we balance being transparent with the team while still avoiding information overload or unnecessary worry?`,

    //                     /** @type {String} item importance */
    //                     importance: `This question points to a real workplace tension that may deserve more discussion in future sessions. It shows participants are thinking beyond the surface topic and trying to apply the training in complex situations.`,
    //                 },
    //                 {
    //                     /** @type {String} item title */
    //                     title: `Strongest Example of Growth Mindset`,

    //                     /** @type {String} item evidence (no html/quotes) */
    //                     evidence: `I used to see feedback as criticism, but the activity helped me understand that feedback gives me a clearer path for improving my work.`,

    //                     /** @type {String} item importance */
    //                     importance: `This gives the event leader evidence that the session may have shifted how at least some participants interpret feedback. That is valuable because mindset change is often harder to achieve than simple knowledge recall.`,
    //                 },
    //                 {
    //                     /** @type {String} item title */
    //                     title: `Best Signal of Team Culture`,

    //                     /** @type {String} item evidence (no html/quotes) */
    //                     evidence: `Several people mentioned that they want more chances to practice difficult conversations with peers before having them with clients or managers.`,

    //                     /** @type {String} item importance */
    //                     importance: `This suggests participants may value psychological safety and peer learning. The event leader could use this to design follow-up sessions that include role play, coaching circles, or structured practice time.`,
    //                 },
    //                 {
    //                     /** @type {String} item title */
    //                     title: `Most Useful Critique for Future Events`,

    //                     /** @type {String} item evidence (no html/quotes) */
    //                     evidence: `The examples were helpful, but I would have liked one full scenario that we followed from beginning to end instead of several shorter examples.`,

    //                     /** @type {String} item importance */
    //                     importance: `This is a concrete design suggestion. It tells the event leader that participants may benefit from deeper case-based learning, not just more examples or more content.`,
    //                 },
    //             ],
    //         },

    //         /** next steps section */
    //         next: {
    //             /** @type {Object} tiles management */
    //             manage: {
    //                 /** @type {Number} current tile (1-n) */
    //                 selection: 1,
    //             },

    //             /** @type {Array} next steps tiles data */
    //             tiles: [
    //                 {
    //                     /** @type {String} tile tag */
    //                     tag: `Improve`,

    //                     /** @type {String} tile title */
    //                     title: `Add More Practice Time`,

    //                     /** @type {String} tile content text */
    //                     content: `In the next version of the event, reserve a dedicated block for participants to practice the main skill instead of only discussing it. After the event, reflect on whether participants had enough time to try the behavior, make mistakes, receive feedback, and repeat the exercise with more confidence.`,
    //                 },
    //                 {
    //                     /** @type {String} tile tag */
    //                     tag: `Follow Up`,

    //                     /** @type {String} tile title */
    //                     title: `Send a Simple Takeaway Resource`,

    //                     /** @type {String} tile content text */
    //                     content: `Create a one-page summary, checklist, or decision guide that participants can use after the session. Reflect on whether the event made it clear what attendees should do differently tomorrow, not just what they should remember from the training.`,
    //                 },
    //                 {
    //                     /** @type {String} tile tag */
    //                     tag: `Investigate`,

    //                     /** @type {String} tile title */
    //                     title: `Explore the Hardest Question Raised`,

    //                     /** @type {String} tile content text */
    //                     content: `Choose one participant question that revealed uncertainty, tension, or disagreement, and use it as the basis for a follow-up discussion or manager conversation. Reflect on whether the event created enough space for honest questions, or whether some issues stayed too surface-level.`,
    //                 },
    //                 {
    //                     /** @type {String} tile tag */
    //                     tag: `Measure`,

    //                     /** @type {String} tile title */
    //                     title: `Check for Behavior Change Later`,

    //                     /** @type {String} tile content text */
    //                     content: `Follow up with participants after a few weeks to ask what they actually tried, what changed, and what still feels difficult. Reflect on whether the event produced real workplace transfer, or whether participants mainly left with positive impressions but limited action.`,
    //                 },
    //                 {
    //                     /** @type {String} tile tag */
    //                     tag: `Design`,

    //                     /** @type {String} tile title */
    //                     title: `Use One Longer Scenario`,

    //                     /** @type {String} tile content text */
    //                     content: `Consider replacing several short examples with one deeper case study that participants follow from beginning to end. Reflect on whether the current event design helps people connect ideas into a realistic workflow, or whether the learning feels too fragmented.`,
    //                 },
    //                 {
    //                     /** @type {String} tile tag */
    //                     tag: `Reflect`,

    //                     /** @type {String} tile title */
    //                     title: `Review Who Participated Most`,

    //                     /** @type {String} tile content text */
    //                     content: `Look back at which participants, teams, or roles contributed most during the session, and which voices were quieter. Reflect on whether the event format created equal access to participation, or whether future sessions need more structured pair-share, anonymous input, or small-group discussion.`,
    //                 },
    //             ],
    //         },
    //     },
    
    //     // popup 
    //     popups: { 
    //         // TEMPORARY
    //         error: {
    //             pic: NaN, // 0 = warning (type: num)
    //             mainText: ``, // warning header (type: string)
    //             subText: ``, // warning details (type: string)
    //             action: {
    //                 show: true, // show action button (type: boolean)
    //                 text: ``, // action button text (type: string)
    //             },
    //             exit: {
    //                 show: true, // show exit button (type: boolean)
    //             },
    //         },
    //     },
    // }))();

    // /**
    //  * Recursively sets .distribution for each skill and sub-skill.
    //  * Each skill gets a distribution array of the same length.
    //  *
    //  * @param {Object} scoresObject - Usually data.scores
    //  * @param {Number} length - Number of distribution items per skill
    //  * @param {Object} options
    //  * @param {Number} options.minScore - Min random score
    //  * @param {Number} options.maxScore - Max random score
    //  * @returns {Object} Mutated scoresObject
    //  */
    // function setRandomDistributions(
    // scoresObject,
    // length = 12,
    // { minScore = 450, maxScore = 650 } = {}
    // ) {
    // const firstNames = [
    //     "Alex",
    //     "Jordan",
    //     "Taylor",
    //     "Morgan",
    //     "Casey",
    //     "Riley",
    //     "Sam",
    //     "Avery",
    //     "Jamie",
    //     "Quinn",
    //     "Cameron",
    //     "Drew",
    //     "Harper",
    //     "Reese",
    //     "Skyler",
    //     "Emerson",
    // ];

    // const lastNames = [
    //     "Lee",
    //     "Patel",
    //     "Garcia",
    //     "Kim",
    //     "Brown",
    //     "Nguyen",
    //     "Smith",
    //     "Chen",
    //     "Davis",
    //     "Wilson",
    //     "Martinez",
    //     "Clark",
    //     "Lewis",
    //     "Walker",
    //     "Young",
    //     "Allen",
    // ];

    // const randomInt = (min, max) =>
    //     Math.floor(Math.random() * (max - min + 1)) + min;

    // const randomName = () => {
    //     const first = firstNames[randomInt(0, firstNames.length - 1)];
    //     const last = lastNames[randomInt(0, lastNames.length - 1)];
    //     return `${first} ${last}`;
    // };

    // const makeDistribution = () =>
    //     Array.from({ length }, () => ({
    //     name: randomName(),
    //     number: randomInt(minScore, maxScore),
    //     }));

    // const applyToSkill = (skill) => {
    //     skill.distribution = makeDistribution();

    //     if (Array.isArray(skill.subConstructs)) {
    //     skill.subConstructs.forEach(applyToSkill);
    //     }

    //     return skill;
    // };

    // Object.values(scoresObject).forEach(applyToSkill);

    // return scoresObject;
    // }
    // setRandomDistributions(allPageData.canvas.scores, Math.round(Math.random() * 30 + 2));

    return true;
}

/* ------------- HANDLES NAV BARS ------------- */

/**
 * manages common elements (nav elements, loader).
 * assigns help text, event listeners, load visuals.
 * exceptions: shards, streaks, action buttons
 * @returns {object} common page elements
 */
async function manageCommonElements() {
    // define common elements
    let commonElements = {
        nav: {
            topNav: {
                home: {
                    div: document.querySelector(`[commonTopHomeDiv]`),
                },
                social: {
                    div: document.querySelector(`[commonTopSocialDiv]`),
                },
                shards: {
                    div: document.querySelector(`[commonTopShardsDiv]`),
                    num: document.querySelector(`[commonTopShardsNum]`),
                },
                streaks: {
                    div: document.querySelector(`[commonTopStreaksDiv]`),
                    num: document.querySelector(`[commonTopStreaksNum]`),
                },
                notifs: {
                    div: document.querySelector(`[commonTopNotifsDiv]`),
                    numText: document.querySelector(`[commonTopNotifsNumText]`),
                },
                help: {
                    div: document.querySelector(`[commonTopHelpDiv]`),
                },
                profile: {
                    div: document.querySelector(`[commonTopProfileDiv]`),
                    mainText: document.querySelector(`[commonTopProfileMainText]`),
                },
            },
            leftNav: {
                move: {
                    expandNav: {
                        div: document.querySelector(`[commonLeftMoveExpandDiv]`),
                    },
                    collapseNav: {
                        div: document.querySelector(`[commonLeftMoveCollapseDiv]`),
                    },
                },
                header: {
                    text: document.querySelector(`[commonLeftHeadTxt]`),
                },
                sections: {
                    home: {
                        div: document.querySelector(`[commonLeftSectionsHomeDiv]`),
                        text: document.querySelector(`[commonLeftSectionsHomeTxt]`),
                    },
                    activeTime: {
                        div: document.querySelector(`[commonLeftSectionsActiveTimeDiv]`),
                        text: document.querySelector(`[commonLeftSectionsActiveTimeTxt]`),
                    },
                    pastScans: {
                        div: document.querySelector(`[commonLeftSectionsPastScansDiv]`),
                        text: document.querySelector(`[commonLeftSectionsPastScansTxt]`),
                    },
                    score: {
                        div: document.querySelector(`[commonLeftSectionsScoreDiv]`),
                        text: document.querySelector(`[commonLeftSectionsScoreTxt]`),
                    },
                    monthlyActivity: {
                        div: document.querySelector(`[commonLeftSectionsMonthlyActivityDiv]`),
                        text: document.querySelector(`[commonLeftSectionsMonthlyActivityTxt]`),
                    },
                },
                actions: {
                    scan: {
                        div: document.querySelector(`[commonLeftActionsScanDiv]`),
                        text: document.querySelector(`[commonLeftActionsScanTxt]`),
                    },
                    join: {
                        div: document.querySelector(`[commonLeftActionsJoinDiv]`),
                        text: document.querySelector(`[commonLeftActionsJoinTxt]`),
                    },
                    feedback: {
                        div: document.querySelector(`[commonLeftActionsFeedbackDiv]`),
                        text: document.querySelector(`[commonLeftActionsFeedbackTxt]`),
                    },
                },
            },
        },
        entryLoad: document.querySelectorAll(`[entryLoad]`),
        certify: document.querySelector(`[guildCertificate]`),
    };

    // convert node list to array
    commonElements.entryLoad = [...commonElements.entryLoad];

    // ensure entry load elements are showing
    commonElements.entryLoad.forEach(loadingEle => {
        loadingEle.style.display = `flex`;
    });

    // highlight section currently on
    commonElements.nav.topNav.home.div.classList.add(`selected`); // top nav
    commonElements.nav.leftNav.actions.scan.div.classList.add(`selected`); // left nav

    // set notif text
    setNotifText(commonElements);

    // set profile text
    setProfileText(commonElements);


    // define help text for common elements - MIRROR commonElements tree
    let helpText = {
        nav: {
            topNav: {
                home: {
                    div: [`Home`, `below`],
                },
                social: {
                    div: [`Social`, `below`],
                },
                shards: {
                    div: [`Brain Shards`, `below`],
                    num: [],
                },
                streaks: {
                    div: [`Weekly Streaks`, `below`],
                    num: [],
                },
                notifs: {
                    div: [`Notifications`, `below`],
                    numText: [],
                },
                help: {
                    div: [`Help`, `below`],
                },
                profile: {
                    div: [`Profile`, `below`],
                    mainText: [],
                },
            },
            leftNav: {
                move: {
                    expandNav: {
                        div: [`Expand Sidebar`, `right`],
                    },
                    collapseNav: {
                        div: [`Shrink Sidebar`, `right`],
                    },
                },
                header: {
                    text: [],
                },
                sections: {
                    home: {
                        div: [`My Brain`, `right`],
                        text: [],
                    },
                    activeTime: {
                        div: [`Active Time`, `right`],
                        text: [],
                    },
                    pastScans: {
                        div: [`Past Workouts`, `right`],
                        text: [],
                    },
                    score: {
                        div: [`Score Detail`, `right`],
                        text: [],
                    },
                    monthlyActivity: {
                        div: [`Monthly Activity`, `right`],
                        text: [],
                    },
                },
                actions: {
                    scan: {
                        div: [`Train My Brain`, `right`],
                        text: [],
                    },
                    join: {
                        div: (
                            userData.langRole == 0 ? [`Join Class`, `right`] :
                                userData.langRole == 1 ? [`Join ${capFirstLetter(roleTranslation.class.singular)}`, `right`] :
                                    [`Join Class`, `right`]
                        ),
                        text: [],
                    },
                    feedback: {
                        div: [`Give Feedback`, `right`],
                        text: [],
                    },
                },
            },
        },
        entryLoad: [],
        certify: [`Certification`, `auto`],
    };

    // attach help text to common elements
    processElementTreeWithHelp(commonElements, helpText);

    // attach links to common elements
    handleCommonElementClicks(commonElements);

    // dynamically set actions join button text based on user lang role
    if (userData.langRole == 0) {
        commonElements.nav.leftNav.actions.join.text.textContent = `Join Class`;
    } else if (userData.langRole == 1) {
        commonElements.nav.leftNav.actions.join.text.textContent = `Join ${capFirstLetter(roleTranslation.class.singular)}`;
    }

    return commonElements;
}

/**
 * sets notification text/div
 * @param {object} commonElements common elements obj
 * @returns {boolean} true
 */
function setNotifText(commonElements) {
    let notifTxt = commonElements.nav.topNav.notifs.numText;

    // show text
    if (userData.notifNum > 0) {
        notifTxt.textContent = String(userData.notifNum);
        notifTxt.style.display = `block`;
    }
    // hide red indicator
    else {
        notifTxt.textContent = `0`;
        notifTxt.style.display = `none`;
    }

    return true;
}

/**
 * sets profile initials
 * @param {object} commonElements common elements object
 * @returns {boolean} true
 */
function setProfileText(commonElements) {
    let profileTxt = commonElements.nav.topNav.profile.mainText;

    // show text
    if (userData?.profileInitials?.length > 0) {
        profileTxt.textContent = String(userData.profileInitials);
        profileTxt.style.display = `block`;
    }

    // error default - hide initials
    else {
        profileTxt.textContent = `<3`;
        profileTxt.style.display = `none`;
    }

    return true;
}

/**
 * assigns event listeners to common page elements
 * @param {object} commonElements nested commonElements object
 */
function handleCommonElementClicks(commonElements) {
    // top nav
    commonElements.nav.topNav.home.div.addEventListener(`click`, function () {
        window.location.href = redirectPaths.commonElements.topNav.home.div;
    });
    commonElements.nav.topNav.social.div.addEventListener(`click`, function () {
        window.location.href = redirectPaths.commonElements.topNav.social.div;
    });
    commonElements.nav.topNav.shards.div.addEventListener(`click`, function () {
        window.location.href = redirectPaths.commonElements.topNav.shards.div;
    });
    commonElements.nav.topNav.streaks.div.addEventListener(`click`, function () {
        window.location.href = redirectPaths.commonElements.topNav.streaks.div;
    });
    commonElements.nav.topNav.notifs.div.addEventListener(`click`, function () {
        window.location.href = redirectPaths.commonElements.topNav.notifs.div;
    });
    commonElements.nav.topNav.help.div.addEventListener(`click`, function () {
        window.location.href = redirectPaths.commonElements.topNav.help.div;
    });
    commonElements.nav.topNav.profile.div.addEventListener(`click`, function () {
        window.location.href = redirectPaths.commonElements.topNav.profile.div;
    });

    // left nav
    commonElements.nav.leftNav.move.expandNav.div.addEventListener(`click`, function () {
        // TEMPORARY - blank data (re-render charts/graphs)
    });
    commonElements.nav.leftNav.move.collapseNav.div.addEventListener(`click`, function () {
        // TEMPORARY - blank data (re-render charts/graphs)
    });
    commonElements.nav.leftNav.sections.home.div.addEventListener(`click`, function () {
        window.location.href = redirectPaths.commonElements.leftNav.sections.home.div;
    });
    commonElements.nav.leftNav.sections.activeTime.div.addEventListener(`click`, function () {
        window.location.href = redirectPaths.commonElements.leftNav.sections.activeTime.div;
    });
    commonElements.nav.leftNav.sections.pastScans.div.addEventListener(`click`, function () {
        window.location.href = redirectPaths.commonElements.leftNav.sections.pastScans.div;
    });
    commonElements.nav.leftNav.sections.score.div.addEventListener(`click`, function () {
        window.location.href = redirectPaths.commonElements.leftNav.sections.score.div;
    });
    commonElements.nav.leftNav.sections.monthlyActivity.div.addEventListener(`click`, function () {
        window.location.href = redirectPaths.commonElements.leftNav.sections.monthlyActivity.div;
    });
    // take scan handled in render canvas elements
    // join class handled in render canvas elements
    commonElements.nav.leftNav.actions.feedback.div.addEventListener(`click`, function () {
        window.location.href = redirectPaths.commonElements.leftNav.actions.feedback.div;
    });
}

/* -------------------------- PAGE FUNCTIONS -------------------------- */

/* ------------- CANVAS ------------- */

/**
 * renders page elements from page data.
 * hides loader.
 * @param {object} commonElements all common objs
 * @param {object} pageElements all page objs
 */
async function renderCanvasEles(commonElements, pageElements) {
    // ensure all top level canvas and popup elements (except entry load elements) are hidden
    for (let child of pageElements.canvas.div.children) {
        if (!child.hasAttribute('entryLoad')) {
            child.style.display = 'flex';
            child.style.visibility = 'hidden'; // hide 
        }
    }
    for (let child of pageElements.popups.div.children) {
        if (!child.hasAttribute('entryLoad')) {
            child.style.display = 'none'; // hide 
        }
    }

    // functions to call
    let canvasFuncts = [
        // manage screens (1 or 2) viz
        {
            fn: manageScreen,
            args: [commonElements, pageElements]
        },

        /* ------- COMMON ELEMENTS ------- */

        // manage join class button (common element and canvas popup)
        {
            fn: manageJoinClass,
            args: [commonElements, pageElements]
        },

        // manage take workout button (common element and canvas popup)
        {
            fn: manageTakeScan,
            args: [commonElements, pageElements]
        },

        // manage setting shards button text
        {
            fn: setShardsText,
            args: [commonElements]
        },

        // manage setting streaks button text
        {
            fn: setStreaksText,
            args: [commonElements]
        },
    ];
    // unique to screens 1 or 2
    if (allPageData.canvas.share.show) {
        canvasFuncts.push(
            // manage share section
            {
                fn: manageShare,
                args: [commonElements, pageElements]
            },
        );
    } else {
        canvasFuncts.push(
            // manage update popup
            {
                fn: manageBannerNew,
                args: [commonElements, pageElements]
            },

            // manage actions
            {
                fn: manageActions,
                args: [commonElements, pageElements]
            },

            // manage takeaways
            {
                fn: manageTakeaways,
                args: [commonElements, pageElements]
            },

            // manage brain stats (scores, explanation)
            {
                fn: manageBrainStats,
                args: [commonElements, pageElements]
            },

            // manage feedback
            {
                fn: manageThemes,
                args: [commonElements, pageElements]
            },

            // manage comments (compliments, criticisms)
            {
                fn: manageComments,
                args: [commonElements, pageElements]
            },

            // manage review
            {
                fn: manageReview,
                args: [commonElements, pageElements]
            },

            // manage next steps
            {
                fn: manageNext,
                args: [commonElements, pageElements]
            },
        );
    }

    await callParallelFunctions(canvasFuncts);

    // hide loader and show all top level canvas elements
    for (let child of pageElements.canvas.div.children) {
        if (!child.hasAttribute('entryLoad')) {
            child.style.display = 'flex';
            child.style.visibility = 'visible'; // show 
        } else {
            child.style.display = 'none'; // hide
        }
    }
    for (let child of pageElements.popups.div.children) {
        if (!child.hasAttribute('entryLoad')) {
            child.style.display = 'none'; // hide 
        }
    }
}

/**
 * handles screen viz (share or main)
 * @param {object} commonElements all common objs
 * @param {object} pageElements all page objs
 * @returns {boolean} true
 */
async function manageScreen(commonElements, pageElements) {
    // set share
    let showShare = allPageData.canvas.share.show;

    // set core canvas sections
    let sections = [
        pageElements.canvas.share.div,
        pageElements.canvas.main.div,
    ];

    // screen 1 - put attribute on everything but share
    // screen 2 - put attribute on share only
    if (showShare) {
        sections = sections[1]; // only include index 1
    } else {
        sections = sections[0]; // only include index 0
    }
    console.log('sections: ', sections);

    // put custom attribute
    sections.setAttribute(`entryLoad`, ``);

    return true;
}

/**
 * handles share section
 * @param {object} commonElements all common objs
 * @param {object} pageElements all page objs
 * @returns {boolean} true
 */
async function manageShare(commonElements, pageElements) {
    let shareQrRenderKey = ``;
    let shareQrStyledRenderKey = ``;
    let shareQrStylingLibraryPromise;
    let shareQrIconTimers = new WeakMap();
    let shareQrOriginalIcons = new WeakMap();
    const shareQrExportSize = 1000;
    const shareQrExportQuietZone = 4;
    const shareQrStyledMarginRatio = 0.01;
    const shareQrStyledBorderRadius = 10;
    const shareQrStyledLibraryUrl = `https://unpkg.com/qr-code-styling@1.5.0/lib/qr-code-styling.js`;
    const shareQrLogoUrl = `https://learnhaustech.github.io/assets/LearnHaus_webclip_256x256.png`;
    const checkIconSvg = `<svg xmlns="http://www.w3.org/2000/svg"  viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-check"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M5 12l5 5l10 -10" /></svg>`;

    /**
     * creates the QR server URL
     * @param {number} size QR image size in pixels
     * @param {number} quietZone quiet-zone size in QR modules
     * @returns {URL} QR image URL
     */
    function createShareQrUrl(size, quietZone = 0) {
        const qrApiUrl = new URL(`https://api.qrserver.com/v1/create-qr-code/`);
        qrApiUrl.searchParams.set(`size`, `${size}x${size}`);
        qrApiUrl.searchParams.set(`margin`, `0`);
        qrApiUrl.searchParams.set(`qzone`, `${quietZone}`);
        qrApiUrl.searchParams.set(`color`, `000000`);
        qrApiUrl.searchParams.set(`bgcolor`, `ffffff`);
        qrApiUrl.searchParams.set(`data`, allPageData.canvas.basic.form.link);

        return qrApiUrl;
    }

    /**
     * gets the current inner QR container size
     * @returns {number} QR size in pixels
     */
    function getShareQrSize() {
        const qrDiv = pageElements.canvas.share.form.qr.div;
        const qrStyle = window.getComputedStyle(qrDiv);
        const qrWidth = qrDiv.clientWidth - parseFloat(qrStyle.paddingLeft) - parseFloat(qrStyle.paddingRight);
        const qrHeight = qrDiv.clientHeight - parseFloat(qrStyle.paddingTop) - parseFloat(qrStyle.paddingBottom);

        return Math.max(120, Math.floor(Math.min(qrWidth, qrHeight)) || 240);
    }

    /**
     * gets the full QR container size, including parent padding
     * @returns {number} QR size in pixels
     */
    function getStyledShareQrSize() {
        const qrDiv = pageElements.canvas.share.form.qr.div;

        return Math.max(120, Math.floor(Math.min(qrDiv.clientWidth, qrDiv.clientHeight)) || 240);
    }

    /**
     * gets minimal inner white padding for styled QR output
     * @param {number} size QR size in pixels
     * @returns {number} styled QR margin in pixels
     */
    function getStyledShareQrMargin(size) {
        return Math.max(2, Math.round(size * shareQrStyledMarginRatio));
    }

    /**
     * lets styled QR fill the parent padding box; fallback QR keeps normal padding
     * @param {HTMLElement} styledQr styled QR wrapper
     * @returns {boolean} true
     */
    function setStyledShareQrFill(styledQr) {
        const qrStyle = window.getComputedStyle(pageElements.canvas.share.form.qr.div);
        const paddingTop = parseFloat(qrStyle.paddingTop) || 0;
        const paddingRight = parseFloat(qrStyle.paddingRight) || 0;
        const paddingBottom = parseFloat(qrStyle.paddingBottom) || 0;
        const paddingLeft = parseFloat(qrStyle.paddingLeft) || 0;

        styledQr.style.width = `calc(100% + ${paddingLeft + paddingRight}px)`;
        styledQr.style.height = `calc(100% + ${paddingTop + paddingBottom}px)`;
        styledQr.style.marginTop = `-${paddingTop}px`;
        styledQr.style.marginRight = `-${paddingRight}px`;
        styledQr.style.marginBottom = `-${paddingBottom}px`;
        styledQr.style.marginLeft = `-${paddingLeft}px`;
        styledQr.style.borderRadius = `${shareQrStyledBorderRadius}px`;
        styledQr.style.overflow = `hidden`;

        return true;
    }

    /**
     * removes the currently rendered QR image/canvas
     * @returns {boolean} true
     */
    function clearShareQrVisuals() {
        pageElements.canvas.share.form.qr.div.querySelector(`[shareQrImg]`)?.remove();
        pageElements.canvas.share.form.qr.div.querySelector(`[shareQrStyled]`)?.remove();

        return true;
    }

    /**
     * loads the optional QR styling library
     * @returns {Promise<Function>} QRCodeStyling constructor
     */
    function loadShareQrStylingLibrary() {
        if (window.QRCodeStyling) return Promise.resolve(window.QRCodeStyling);
        if (shareQrStylingLibraryPromise) return shareQrStylingLibraryPromise;

        shareQrStylingLibraryPromise = new Promise((resolve, reject) => {
            const script = document.createElement(`script`);
            script.src = shareQrStyledLibraryUrl;
            script.async = true;
            script.onload = () => {
                if (window.QRCodeStyling) {
                    resolve(window.QRCodeStyling);
                } else {
                    reject(new Error(`QR styling library is unavailable.`));
                }
            };
            script.onerror = () => reject(new Error(`Unable to load QR styling library.`));
            document.head.appendChild(script);
        });

        return shareQrStylingLibraryPromise;
    }

    /**
     * creates options for the styled QR generator
     * @param {number} size QR size in pixels
     * @returns {object} styled QR options
     */
    function createStyledShareQrOptions(size) {
        const options = {
            width: size,
            height: size,
            type: `canvas`,
            data: allPageData.canvas.basic.form.link,
            margin: getStyledShareQrMargin(size),
            qrOptions: {
                errorCorrectionLevel: `H`,
            },
            dotsOptions: {
                color: `#000000`,
                type: `rounded`,
            },
            cornersSquareOptions: {
                color: `#000000`,
                type: `extra-rounded`,
            },
            cornersDotOptions: {
                color: `#1419fc`,
                type: `dot`,
            },
            backgroundOptions: {
                color: `#ffffff`,
            },
        };

        if (shareQrLogoUrl) {
            options.image = shareQrLogoUrl;
            options.imageOptions = {
                crossOrigin: `anonymous`,
                hideBackgroundDots: true,
                imageSize: 0.18,
                margin: Math.max(4, Math.round(size * 0.02)),
            };
        }

        return options;
    }

    /**
     * creates a styled QR instance
     * @param {number} size QR size in pixels
     * @returns {Promise<object>} styled QR instance
     */
    async function createStyledShareQr(size) {
        if (!allPageData.canvas.basic.form.link) throw new Error(`QR link is unavailable.`);

        const QRCodeStyling = await loadShareQrStylingLibrary();
        return new QRCodeStyling(createStyledShareQrOptions(size));
    }

    /**
     * renders styled QR code at the current size of the QR container
     * @returns {Promise<boolean>} true
     */
    async function renderStyledShareQr() {
        const qrDiv = pageElements.canvas.share.form.qr.div;
        const qrSize = getStyledShareQrSize();
        const renderKey = `${allPageData.canvas.basic.form.link}|${qrSize}|${shareQrLogoUrl}`;
        const existingStyledQr = qrDiv.querySelector(`[shareQrStyled]`);
        if (shareQrStyledRenderKey === renderKey && existingStyledQr) return true;

        const qrCode = await createStyledShareQr(qrSize);
        const styledQr = document.createElement(`div`);
        styledQr.setAttribute(`shareQrStyled`, ``);
        styledQr.style.display = `flex`;
        styledQr.style.alignItems = `center`;
        styledQr.style.justifyContent = `center`;
        setStyledShareQrFill(styledQr);

        qrCode.append(styledQr);

        const renderedQr = styledQr.firstElementChild;
        if (renderedQr) {
            renderedQr.style.width = `100%`;
            renderedQr.style.height = `100%`;
            renderedQr.style.objectFit = `contain`;
            renderedQr.style.borderRadius = `${shareQrStyledBorderRadius}px`;
        }

        if (renderKey !== `${allPageData.canvas.basic.form.link}|${getStyledShareQrSize()}|${shareQrLogoUrl}`) return true;

        clearShareQrVisuals();
        qrDiv.insertBefore(styledQr, qrDiv.firstChild);
        shareQrStyledRenderKey = renderKey;

        return true;
    }

    /**
     * renders QR code at the current size of the QR container
     * @returns {boolean} true
     */
    function renderShareQr() {
        // clear if no link
        if (!allPageData.canvas.basic.form.link) {
            shareQrRenderKey = ``;
            shareQrStyledRenderKey = ``;
            clearShareQrVisuals();
            return true;
        }

        renderFallbackShareQr();
        renderStyledShareQr().catch(error => {
            console.error(`Unable to render styled QR code. Falling back to basic QR code.`, error);
            renderFallbackShareQr();
        });

        return true;
    }

    /**
     * renders basic QR code at the current size of the QR container
     * @returns {boolean} true
     */
    function renderFallbackShareQr() {
        const qrDiv = pageElements.canvas.share.form.qr.div;
        const qrSize = getShareQrSize();
        const qrApiUrl = createShareQrUrl(qrSize);

        let qrImg = qrDiv.querySelector(`[shareQrImg]`);
        if (shareQrRenderKey === qrApiUrl.toString() && qrImg) return true;
        shareQrRenderKey = qrApiUrl.toString();

        if (!qrImg) {
            qrImg = document.createElement(`img`);
            qrImg.setAttribute(`shareQrImg`, ``);
            qrDiv.insertBefore(qrImg, qrDiv.firstChild);
        }
        qrDiv.querySelector(`[shareQrStyled]`)?.remove();
        shareQrStyledRenderKey = ``;
        qrImg.src = shareQrRenderKey;
        qrImg.alt = `QR code for ${allPageData.canvas.basic.title} form`;
        qrImg.loading = `lazy`;
        qrImg.decoding = `async`;
        qrImg.style.display = `block`;
        qrImg.style.width = `100%`;
        qrImg.style.height = `100%`;
        qrImg.style.objectFit = `contain`;
        qrImg.style.imageRendering = `pixelated`;

        qrDiv.style.display = `flex`;
        qrDiv.style.alignItems = `center`;
        qrDiv.style.justifyContent = `center`;

        return true;
    }

    /**
     * gets the basic QR as an image blob
     * @returns {Promise<Blob>} QR image blob
     */
    async function getFallbackShareQrBlob({ exportQuality = false } = {}) {
        if (!allPageData.canvas.basic.form.link) throw new Error(`QR link is unavailable.`);

        const qrUrl = exportQuality
            ? createShareQrUrl(shareQrExportSize, shareQrExportQuietZone).toString()
            : shareQrRenderKey || createShareQrUrl(240).toString();

        const response = await fetch(qrUrl);
        if (!response.ok) throw new Error(`Unable to fetch QR image.`);

        const blob = await response.blob();
        const qrBlob = blob.type ? blob : new Blob([blob], { type: `image/png` });
        if (!exportQuality) return qrBlob;

        return normalizeShareQrBlob(qrBlob, shareQrExportSize);
    }

    /**
     * gets the styled QR as an image blob
     * @returns {Promise<Blob>} QR image blob
     */
    async function getStyledShareQrBlob() {
        const qrCode = await createStyledShareQr(shareQrExportSize);
        const blob = await qrCode.getRawData(`png`);
        if (!blob) throw new Error(`Unable to create styled QR PNG.`);

        return roundShareQrBlob(blob.type ? blob : new Blob([blob], { type: `image/png` }), shareQrStyledBorderRadius);
    }

    /**
     * gets the themed QR blob, falling back to the basic QR if needed
     * @returns {Promise<Blob>} QR image blob
     */
    async function getShareQrBlob() {
        try {
            return await getStyledShareQrBlob();
        } catch (error) {
            console.error(`Unable to create styled QR code. Falling back to basic QR code.`, error);
            return getFallbackShareQrBlob({ exportQuality: true });
        }
    }

    /**
     * redraws QR to a strict black/white PNG so copy targets do not inherit soft pixels
     * @param {Blob} blob source QR blob
     * @param {number} size output size in pixels
     * @returns {Promise<Blob>} normalized PNG blob
     */
    async function normalizeShareQrBlob(blob, size) {
        const image = `createImageBitmap` in window
            ? await createImageBitmap(blob)
            : await loadShareQrImage(blob);

        const canvas = document.createElement(`canvas`);
        canvas.width = size;
        canvas.height = size;

        const ctx = canvas.getContext(`2d`, { willReadFrequently: true });
        ctx.imageSmoothingEnabled = false;
        ctx.fillStyle = `#ffffff`;
        ctx.fillRect(0, 0, size, size);
        ctx.drawImage(image, 0, 0, size, size);

        const imageData = ctx.getImageData(0, 0, size, size);
        const pixels = imageData.data;
        for (let i = 0; i < pixels.length; i += 4) {
            const alpha = pixels[i + 3] / 255;
            const luminance = (pixels[i] * 0.299 + pixels[i + 1] * 0.587 + pixels[i + 2] * 0.114) * alpha + 255 * (1 - alpha);
            const value = luminance < 128 ? 0 : 255;
            pixels[i] = value;
            pixels[i + 1] = value;
            pixels[i + 2] = value;
            pixels[i + 3] = 255;
        }
        ctx.putImageData(imageData, 0, 0);

        if (typeof image.close === `function`) image.close();

        return new Promise((resolve, reject) => {
            canvas.toBlob(outputBlob => {
                if (outputBlob) {
                    resolve(outputBlob);
                } else {
                    reject(new Error(`Unable to create QR PNG.`));
                }
            }, `image/png`);
        });
    }

    /**
     * loads a QR blob as an image when createImageBitmap is unavailable
     * @param {Blob} blob source QR blob
     * @returns {Promise<HTMLImageElement>} loaded image
     */
    function loadShareQrImage(blob) {
        return new Promise((resolve, reject) => {
            const objectUrl = URL.createObjectURL(blob);
            const image = new Image();
            image.onload = () => {
                URL.revokeObjectURL(objectUrl);
                resolve(image);
            };
            image.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                reject(new Error(`Unable to load QR image.`));
            };
            image.src = objectUrl;
        });
    }

    /**
     * clips a QR PNG to rounded corners
     * @param {Blob} blob source QR blob
     * @param {number} radius corner radius in pixels
     * @returns {Promise<Blob>} rounded PNG blob
     */
    async function roundShareQrBlob(blob, radius) {
        const image = `createImageBitmap` in window
            ? await createImageBitmap(blob)
            : await loadShareQrImage(blob);

        const width = image.width || shareQrExportSize;
        const height = image.height || shareQrExportSize;
        const canvas = document.createElement(`canvas`);
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext(`2d`);
        drawRoundedShareQrPath(ctx, width, height, radius);
        ctx.clip();
        ctx.drawImage(image, 0, 0, width, height);

        if (typeof image.close === `function`) image.close();

        return new Promise((resolve, reject) => {
            canvas.toBlob(outputBlob => {
                if (outputBlob) {
                    resolve(outputBlob);
                } else {
                    reject(new Error(`Unable to create rounded QR PNG.`));
                }
            }, `image/png`);
        });
    }

    /**
     * draws a rounded rectangle path for QR clipping
     * @param {CanvasRenderingContext2D} ctx canvas context
     * @param {number} width canvas width
     * @param {number} height canvas height
     * @param {number} radius corner radius
     * @returns {boolean} true
     */
    function drawRoundedShareQrPath(ctx, width, height, radius) {
        const safeRadius = Math.max(0, Math.min(radius, width / 2, height / 2));

        ctx.beginPath();
        ctx.moveTo(safeRadius, 0);
        ctx.lineTo(width - safeRadius, 0);
        ctx.quadraticCurveTo(width, 0, width, safeRadius);
        ctx.lineTo(width, height - safeRadius);
        ctx.quadraticCurveTo(width, height, width - safeRadius, height);
        ctx.lineTo(safeRadius, height);
        ctx.quadraticCurveTo(0, height, 0, height - safeRadius);
        ctx.lineTo(0, safeRadius);
        ctx.quadraticCurveTo(0, 0, safeRadius, 0);
        ctx.closePath();

        return true;
    }

    /**
     * temporarily swaps a button icon to a check
     * @param {object} buttonObj pageElements button object
     * @returns {boolean} true
     */
    function flashCheckIcon(buttonObj) {
        if (!shareQrOriginalIcons.has(buttonObj.icon)) {
            shareQrOriginalIcons.set(buttonObj.icon, buttonObj.icon.innerHTML);
        }

        const originalIcon = shareQrOriginalIcons.get(buttonObj.icon);
        const existingTimer = shareQrIconTimers.get(buttonObj.icon);
        if (existingTimer) clearTimeout(existingTimer);

        buttonObj.icon.innerHTML = checkIconSvg;

        const timer = setTimeout(() => {
            buttonObj.icon.innerHTML = originalIcon;
            shareQrIconTimers.delete(buttonObj.icon);
        }, 3000);
        shareQrIconTimers.set(buttonObj.icon, timer);

        return true;
    }

    /**
     * creates a safe file name stem
     * @param {string} value raw filename value
     * @returns {string} safe filename value
     */
    function safeFileName(value) {
        return (value || `event-form`)
            .toString()
            .trim()
            .replace(/[<>:"/\\|?*]+/g, `-`)
            .replace(/\s+/g, `-`)
            .replace(/-+/g, `-`)
            .replace(/^-|-$/g, ``)
            .toLowerCase() || `event-form`;
    }

    // set top
    pageElements.canvas.share.top.subTitle.textContent = `${allPageData.canvas.basic.title} | ${timestampToFormalDate(allPageData.canvas.basic.date, userData.timeZone)}`;

    // set form info: link
    pageElements.canvas.share.form.link.innerHTML = allPageData.canvas.basic.form.link
        ? `<a href="${allPageData.canvas.basic.form.link}" target="_blank" rel="noopener noreferrer">${allPageData.canvas.basic.form.link}</a>`
        : `No Form`;

    // set form info: qr
    if (allPageData.canvas.basic.form.link) {
        renderShareQr();
    } else {
        pageElements.canvas.share.form.qr.div.style.display = `none`;
    }

    // rerender QR when the square changes dimensions
    if (`ResizeObserver` in window) {
        let qrResizeFrame;
        const qrResizeObserver = new ResizeObserver(() => {
            cancelAnimationFrame(qrResizeFrame);
            qrResizeFrame = requestAnimationFrame(renderShareQr);
        });
        qrResizeObserver.observe(pageElements.canvas.share.form.qr.div);
    }

    // rerender QR after page width changes
    [
        commonElements.nav.leftNav.move.expandNav.div,
        commonElements.nav.leftNav.move.collapseNav.div,
    ].forEach(ele => {
        ele.addEventListener(`click`, async function() {
            await delay(500);
            if (allPageData.canvas.basic.form.link) {
                renderShareQr();
            } else {
                pageElements.canvas.share.form.qr.div.style.display = `none`;
            }
        });
    });

    // copy QR image
    pageElements.canvas.share.form.qr.copy.div.onclick = async function() {
        try {
            if (!navigator.clipboard?.write || !window.ClipboardItem) throw new Error(`Image clipboard is unavailable.`);

            const blob = await getShareQrBlob();
            await navigator.clipboard.write([
                new ClipboardItem({
                    [blob.type || `image/png`]: blob,
                }),
            ]);

            flashCheckIcon(pageElements.canvas.share.form.qr.copy);
        } catch (error) {
            console.error(error);
            manageCornerPopup(true, 3, undefined, `Unable to copy QR code.`, `gold`);
        }
    };

    // download QR image
    pageElements.canvas.share.form.qr.download.div.onclick = async function() {
        let objectUrl = ``;

        try {
            const blob = await getShareQrBlob();
            objectUrl = URL.createObjectURL(blob);

            const link = document.createElement(`a`);
            link.href = objectUrl;
            link.download = `${safeFileName(allPageData.canvas.basic.title)}-qr-code.png`;
            document.body.appendChild(link);
            link.click();
            link.remove();

            flashCheckIcon(pageElements.canvas.share.form.qr.download);
        } catch (error) {
            console.error(error);
            manageCornerPopup(true, 3, undefined, `Unable to download QR code.`, `gold`);
        } finally {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        }
    };

    // set form info: pw
    if (allPageData.canvas.basic.form.pw) {
        pageElements.canvas.share.form.pw.style.display = `block`;
        pageElements.canvas.share.form.pw.innerHTML = `pw: ${allPageData.canvas.basic.form.pw}`;
    } else {
        pageElements.canvas.share.form.pw.style.display = `none`;
    }
    
    // info
    pageElements.canvas.share.info.textContent = `Results will become available after participants start submitting. Completed: ${Math.round(allPageData.canvas.basic.progress.completed / allPageData.canvas.basic.progress.expected * 100)}% (${allPageData.canvas.basic.progress.completed} / ${allPageData.canvas.basic.progress.expected}).`;

    return true;
}

/**
 * handles banner popup: new result
 * @param {object} commonElements all common objs
 * @param {object} pageElements all page objs
 * @returns {boolean} true
 */
async function manageBannerNew(commonElements, pageElements) {
    // show / not
    if (allPageData.canvas.update.show) {
        // set text
        pageElements.canvas.main.bannerNew.title.innerHTML = `New Results`;
        pageElements.canvas.main.bannerNew.note.innerHTML = `rerun neural engine for the latest insights (est. 3 min)`;

        // on click
        pageElements.canvas.main.bannerNew.div.addEventListener("click", () => {
            const url = new URL(window.location.href);

            // URL query parameter
            url.searchParams.set("new", "1");

            // Reload the same page with all previous query params preserved
            window.location.href = url.toString();
        });

        // show
        pageElements.canvas.main.bannerNew.div.style.display = `flex`;
    } else {
        pageElements.canvas.main.bannerNew.div.style.display = `none`;
    }

    return true;
}

/**
 * handles action buttons (context and share)
 * @param {object} commonElements all common objs
 * @param {object} pageElements all page objs
 * @returns {boolean} true
 */
async function manageActions(commonElements, pageElements) {
    // --------- ELEMENTS ---------

    const actionsParent = pageElements?.canvas?.main?.actions?.div;

    const contextButton = pageElements?.canvas?.main?.actions?.context?.div;
    const contextIcon = pageElements?.canvas?.main?.actions?.context?.icon;
    const contextTitle = pageElements?.canvas?.main?.actions?.context?.title;

    const shareButton = pageElements?.canvas?.main?.actions?.share?.div;
    const shareIcon = pageElements?.canvas?.main?.actions?.share?.icon;
    const shareTitle = pageElements?.canvas?.main?.actions?.share?.title;

    if (!actionsParent || (!contextButton && !shareButton)) return false;

    // --------- STATE ---------

    const shareButtonParent = shareButton?.parentElement || contextButton?.parentElement || actionsParent;

    let contextCopyInProgress = false;
    let shareInProgress = false;
    let shareModeOpen = false;
    let shareChoiceButtons = [];

    let promptShareState = createPromptShareState();

    // --------- ICONS ---------

    const ICONS = EPSILON_ACTION_ICONS;
    const shareChoiceSelectors = {
        shareIconAttr: `maiActShaIcn`,
        contextIconAttr: `maiActConIcn`,
        shareTitleAttr: `maiActShaTit`,
        contextTitleAttr: `maiActConTit`,
        shareDivAttr: `maiActShaDiv`,
        contextDivAttr: `maiActConDiv`,
    };

    // --------- BUTTON HELPERS ---------

    function setContextButtonState(text, iconSvg, disabled = false) {
        return setButtonState(contextButton, contextIcon, contextTitle, text, iconSvg, disabled);
    }

    function setShareButtonState(text, iconSvg, disabled = false) {
        return setButtonState(shareButton, shareIcon, shareTitle, text, iconSvg, disabled);
    }

    function flashContextButtonState(tempText, tempIcon, durationMs = 5000) {
        setContextButtonState(tempText, tempIcon, true);

        window.setTimeout(() => {
            setContextButtonState(`Use with AI`, ICONS.contextDefault, false);
            contextCopyInProgress = false;
        }, durationMs);

        return true;
    }

    function flashShareButtonState(tempText, tempIcon, durationMs = 5000) {
        setShareButtonState(tempText, tempIcon, true);

        window.setTimeout(() => {
            setShareButtonState(`Share Results`, ICONS.shareDefault, false);
            shareInProgress = false;
        }, durationMs);

        return true;
    }

    // --------- TEXT / DATA HELPERS ---------

    function getEventDateText() {
        const timestamp = allPageData?.canvas?.basic?.date;

        if (!Number.isFinite(timestamp)) return ``;

        try {
            return timestampToFormalDate(timestamp, userData.timeZone);
        } catch (error) {
            console.warn(`Unable to format event date`, error);
            return new Date(timestamp).toLocaleDateString();
        }
    }

    function getCompletionPercent(progress) {
        const completed = progress?.completed;
        const expected = progress?.expected;

        if (!Number.isFinite(completed) || !Number.isFinite(expected) || expected <= 0) return null;

        return Math.round((completed / expected) * 100);
    }

    function getMainScoreConstruct() {
        const scoresObj = allPageData?.canvas?.scores || {};
        return Object.values(scoresObj).find(score => score && typeof score == `object`) || null;
    }

    function getMainScoreNum() {
        const mainScore = getMainScoreConstruct();
        const score = mainScore?.num?.mid;

        return Number.isFinite(score) ? Math.round(score) : null;
    }

    function getMainScoreTitle() {
        return toPlainText(getMainScoreConstruct()?.title) || `Reflection Score`;
    }

    function getTopTakeawayMetricText(limit = 3) {
        return getTopTakeawayMetricTextForCanvas(allPageData?.canvas, limit);
    }

    function lowerFirst(text) {
        return lowerFirstRaw(text, true);
    }

    function groupifyAnalyticsText(text) {
        text = toPlainText(text);

        if (!text) return ``;

        return text
            .replace(/\bYou seem to have taken away\b/g, `Participants took away`)
            .replace(/\byou seem to have taken away\b/g, `participants took away`)
            .replace(/\bYou showed\b/g, `Participants showed`)
            .replace(/\byou showed\b/g, `participants showed`)
            .replace(/\bYou demonstrated\b/g, `Participants demonstrated`)
            .replace(/\byou demonstrated\b/g, `participants demonstrated`)
            .replace(/\bYour answers show that you\b/g, `Participant reflections showed that the group`)
            .replace(/\byour answers show that you\b/g, `participant reflections showed that the group`)
            .replace(/\bYour reflection shows that you\b/g, `The reflections showed that participants`)
            .replace(/\byour reflection shows that you\b/g, `the reflections showed that participants`)
            .replace(/\bYour answers show\b/g, `Participant reflections showed`)
            .replace(/\byour answers show\b/g, `participant reflections showed`)
            .replace(/\byour\b/g, `their`)
            .replace(/\bYour\b/g, `Their`)
            .replace(/\byou\b/g, `participants`)
            .replace(/\bYou\b/g, `Participants`)
            .replace(/\s{2,}/g, ` `)
            .trim();
    }

    function cleanImpactCandidate(text) {
        return groupifyAnalyticsText(text)
            .replace(/^The strongest signal was that\s+/i, ``)
            .replace(/^The strongest signal was clear:\s*/i, ``)
            .replace(/^The clearest signal was that\s+/i, ``)
            .replace(/^The biggest signal was that\s+/i, ``)
            .replace(/^A key takeaway was that\s+/i, ``)
            .replace(/^The key takeaway was that\s+/i, ``)
            .replace(/^Participants took away that\s+/i, ``)
            .replace(/^The group took away that\s+/i, ``)
            .trim();
    }

    function getConciseImpactFragment(snap, maxChars = 190) {
        const candidates = [
            snap.themes?.[0]?.title,
            getFirstSentence(snap.bottomLine),
            getFirstSentence(snap.mainScoreCompliment),
            getFirstSentence(snap.mainScoreMeaning),
        ];

        for (const candidate of candidates) {
            const cleaned = cleanImpactCandidate(candidate);
            if (!cleaned) continue;

            const firstClause = cleaned.split(/,\s+(?:but|while|although|and)\b|;\s*/i)[0].trim();

            if (firstClause.length > 35 && firstClause.length <= maxChars) return firstClause;
            if (cleaned.length <= maxChars) return cleaned;
        }

        for (const candidate of candidates) {
            const cleaned = cleanImpactCandidate(candidate);
            if (!cleaned) continue;

            return cleaned
                .split(/\s+/)
                .slice(0, 24)
                .join(` `)
                .replace(/[,:;.\-\s]+$/g, ``)
                .trim();
        }

        return `participants left with clearer evidence of what to practice next`;
    }

    function getBriefEventImpactLine(snap) {
        const impact = getConciseImpactFragment(snap);
        return finishSentence(`The strongest impact was clear: ${lowerFirst(impact)}`);
    }

    function getCleanFeedbackTiles(values, limit = 3) {
        if (!Array.isArray(values)) return [];

        return values
            .map(tile => ({
                title: toPlainText(tile?.title),
                detail: groupifyAnalyticsText(tile?.detail),
            }))
            .filter(hasContent)
            .slice(0, limit);
    }

    function getEventImpactOneLiner(snap, maxChars = 170) {
        const source = snap.bottomLine || snap.themes?.[0]?.detail || snap.mainScoreMeaning || ``;
        let lesson = groupifyAnalyticsText(getFirstSentence(source));

        if (!lesson) {
            lesson = snap.eventTitle
                ? `The event gave participants a clearer picture of what to practice next.`
                : `The results gave me a clearer picture of what participants should practice next.`;
        } else if (!/^(The|Participants|Participant|This|Our|My)\b/.test(lesson)) {
            lesson = `The strongest signal was that ${lowerFirst(lesson)}`;
        }

        if (!/[.!?]$/.test(lesson)) lesson += `.`;

        return truncateText(lesson, maxChars);
    }

    function getShareBulletText(items, limit = 2, maxChars = 165) {
        return items
            .filter(hasContent)
            .slice(0, limit)
            .map(item => {
                const title = toPlainText(item?.title);
                const detail = groupifyAnalyticsText(item?.detail);
                const text = [title, detail].filter(Boolean).join(`: `);
                return `- ${truncateText(text, maxChars)}`;
            })
            .join(`\n`);
    }

    function getNextStepLine(snap, maxChars = 190) {
        const step = snap.nextSteps?.[0];
        const title = groupifyAnalyticsText(step?.title);
        const content = groupifyAnalyticsText(step?.content);
        const text = [title, content].filter(Boolean).join(`: `);

        return truncateText(text, maxChars);
    }

    function getEventAnalyticsSnapshot() {
        const canvas = allPageData?.canvas || {};
        const progress = canvas?.basic?.progress || {};
        const mainScore = getMainScoreNum();
        const mainScoreConstruct = getMainScoreConstruct();

        return {
            adminFirstName: toPlainText(canvas?.basic?.name),
            eventTitle: toPlainText(canvas?.basic?.title),
            date: getEventDateText(),
            progressCompleted: Number.isFinite(progress?.completed) ? progress.completed : null,
            progressExpected: Number.isFinite(progress?.expected) ? progress.expected : null,
            completionPercent: getCompletionPercent(progress),
            mainScoreTitle: getMainScoreTitle(),
            mainScore,
            scoreBar: getScoreEmojiBar(mainScore),
            metricsText: getTopTakeawayMetricText(3),
            bottomLine: groupifyAnalyticsText(canvas?.takeaways?.bottomLine),
            mainScoreMeaning: groupifyAnalyticsText(mainScoreConstruct?.description?.meaning?.content),
            mainScoreCompliment: groupifyAnalyticsText(mainScoreConstruct?.description?.compliments?.content),
            mainScoreCriticism: groupifyAnalyticsText(mainScoreConstruct?.description?.criticisms?.content),
            themes: Array.isArray(canvas?.themes?.data)
                ? canvas.themes.data.slice(0, 4).map(theme => ({
                    title: toPlainText(theme?.title),
                    detail: groupifyAnalyticsText(theme?.detail),
                    frequency: Number.isFinite(theme?.frequency) ? theme.frequency : null,
                    approximatePercent: Number.isFinite(theme?.frequency) ? Math.round(theme.frequency * 100) : null,
                    evidence: toPlainText(theme?.evidence),
                })).filter(hasContent)
                : [],
            compliments: getCleanFeedbackTiles(canvas?.comments?.compliments?.tiles, 3),
            criticisms: getCleanFeedbackTiles(canvas?.comments?.criticisms?.tiles, 3),
            reviewSnippets: Array.isArray(canvas?.review?.tiles)
                ? canvas.review.tiles.slice(0, 5).map((tile, index) => ({
                    number: index + 1,
                    title: toPlainText(tile?.title),
                    evidence: toPlainText(tile?.evidence),
                    importance: groupifyAnalyticsText(tile?.importance),
                })).filter(hasContent)
                : [],
            nextSteps: Array.isArray(canvas?.next?.tiles)
                ? canvas.next.tiles.slice(0, 4).map(step => ({
                    tag: toPlainText(step?.tag),
                    title: groupifyAnalyticsText(step?.title),
                    content: groupifyAnalyticsText(step?.content),
                })).filter(hasContent)
                : [],
        };
    }

    function cleanDistribution(distribution) {
        if (!Array.isArray(distribution)) return [];

        return distribution
            .map((item, index) => ({
                participantNumber: index + 1,
                name: toPlainText(item?.name),
                score: Number.isFinite(item?.number) ? item.number : null,
            }))
            .filter(hasContent);
    }

    function cleanScoreConstruct(construct, depth = 1) {
        if (!construct || typeof construct != `object`) return null;

        const children = Array.isArray(construct.subConstructs)
            ? construct.subConstructs
                .map(child => cleanScoreConstruct(child, depth + 1))
                .filter(Boolean)
            : [];

        const cleanConstruct = {
            sectionMeaning: (
                depth === 1
                    ? `This is the main averaged skill/construct score for the event group.`
                    : depth === 2
                        ? `This is an averaged sub-score beneath the main group skill score.`
                        : `This is a deeper averaged sub-score beneath a broader group skill score.`
            ),
            hierarchy: {
                depth,
                depthMeaning: (
                    depth === 1
                        ? `Main event-level score.`
                        : depth === 2
                            ? `Second-level diagnostic score.`
                            : `Third-level diagnostic score.`
                ),
                canHaveChildren: Array.isArray(construct.subConstructs),
            },
            skill: {
                id: Number.isFinite(construct.id) ? construct.id : null,
                title: toPlainText(construct.title),
                definition: {
                    meaning: `A rigid definition of what this skill/construct measures.`,
                    content: toPlainText(construct?.description?.definition?.content),
                },
            },
            aggregateScore: {
                meaning: (
                    `Scores are aggregated across event participants. ` +
                    `Use averageMostLikely as the main displayed event-level estimate. ` +
                    `averageLikelyLow and averageLikelyHigh show the displayed uncertainty/range.`
                ),
                scale: `1-1000, where higher generally means stronger evidence of this skill across participant reflections.`,
                averageLikelyLow: Number.isFinite(construct?.num?.low) ? construct.num.low : null,
                averageMostLikely: Number.isFinite(construct?.num?.mid) ? construct.num.mid : null,
                averageLikelyHigh: Number.isFinite(construct?.num?.high) ? construct.num.high : null,
            },
            groupFeedback: {
                meaning: {
                    sectionMeaning: `Explanation of what the aggregate score suggests about the event group's thinking.`,
                    content: toPlainText(construct?.description?.meaning?.content),
                },
                topCompliment: {
                    sectionMeaning: `What the group did well regarding this skill, grounded in participant reflections.`,
                    content: toPlainText(construct?.description?.compliments?.content),
                },
                topCriticism: {
                    sectionMeaning: `What the group struggled with or could improve regarding this skill.`,
                    content: toPlainText(construct?.description?.criticisms?.content),
                },
            },
            participantScoreDistribution: {
                sectionMeaning: `Exact participant scores for this construct, used to inspect spread, high performers, and struggling participants.`,
                scores: cleanDistribution(construct?.distribution),
            },
            subSkills: children,
        };

        return hasContent(cleanConstruct) ? cleanConstruct : null;
    }

    function buildScoreTree() {
        const scoresObj = allPageData?.canvas?.scores || {};

        return Object.values(scoresObj)
            .map(scoreConstruct => cleanScoreConstruct(scoreConstruct, 1))
            .filter(Boolean);
    }

    function buildOrganizerContextObject() {
        const canvas = allPageData?.canvas || {};
        const progress = canvas?.basic?.progress || {};

        const takeaways = Array.isArray(canvas?.takeaways?.data)
            ? canvas.takeaways.data.map((tile, index) => ({
                metricNumber: index + 1,
                sectionMeaning: (
                    `This is a dynamic aggregate participation/takeaway metric from the event's Reflection 2.0 submissions. ` +
                    `The exact metric can change depending on the event form and reflection questions.`
                ),
                metric: {
                    label: toPlainText(tile?.label),
                    value: toPlainText(tile?.main),
                    organizerFacingInterpretation: toPlainText(tile?.subText),
                    measurementExplanation: toPlainText(tile?.help),
                },
            })).filter(hasContent)
            : [];

        return {
            metadata: {
                contentType: `Reflection 2.0 event analytics AI context packet`,
                productName: `Reflection 2.0`,
                internalName: `workout`,
                sourcePage: `An event analytics page for an organizer/admin reviewing aggregate participant reflection results.`,
                eventContext: `The event may be a workshop, class, training, meeting, conference, pitch event, or similar learning/professional experience.`,
                intendedUser: `The organizer/admin who ran or manages the event.`,
                intendedUse: (
                    `This packet is meant to be pasted into an AI assistant so the organizer can understand participant takeaways, ` +
                    `aggregate scores, score distributions, common themes, notable snippets, event strengths, event improvement opportunities, and recommended next steps.`
                ),
                importantInstruction: (
                    `Ground responses in participant snippets, aggregate score explanations, distributions, themes, comments, and next steps. ` +
                    `Do not treat averaged numeric scores alone as the full story, and do not invent event details that are not present here.`
                ),
                privacyNote: `This packet may contain participant names, scores, and reflection snippets. Treat it as private organizer-provided context.`,
                includedDataScope: `Only allPageData.canvas analytics results are included. Navbar, share state, refresh/update popup state, and unrelated page state are excluded.`,
                generatedAt: new Date().toISOString(),
            },
            fieldGuide: {
                basic: (
                    `Basic identifies the admin, event, event date, Reflection 2.0 form, and completion progress. ` +
                    `Reflection 2.0 is internally called a workout. Progress is completed reflections out of expected reflections.`
                ),
                takeaways: `Takeaways are aggregate event participation or experience metrics from survey/reflection questions. The bottom line summarizes what participants took away from the event.`,
                scores: (
                    `Scores are averaged aggregate skill/construct evaluations across participant reflections. ` +
                    `Each construct can have up to three depth levels. Description contains score meaning, top group compliment, top group criticism, and skill definition. ` +
                    `Distribution contains participant names and exact most-likely scores for that skill.`
                ),
                themes: `Themes are the most common patterns in participant thinking, with approximate frequency from 0 to 1 and reflection evidence snippets.`,
                comments: `Comments summarize event-level strengths and event improvement opportunities. Compliments are group thinking or event strengths; criticisms are improvement opportunities.`,
                review: `Review contains notable selected participant answer snippets, why each was notable, and why it matters to the organizer.`,
                nextSteps: `Next steps are recommended organizer actions after the event, ordered from easiest to hardest.`,
            },
            result: {
                basic: {
                    sectionMeaning: `Identifies the organizer/admin, event, Reflection 2.0 form, and response progress.`,
                    adminFirstName: toPlainText(canvas?.basic?.name),
                    eventTitle: toPlainText(canvas?.basic?.title),
                    eventStartDate: getEventDateText(),
                    progress: {
                        completedReflections: Number.isFinite(progress?.completed) ? progress.completed : null,
                        expectedReflections: Number.isFinite(progress?.expected) ? progress.expected : null,
                        completionPercent: getCompletionPercent(progress),
                    },
                    form: {
                        sectionMeaning: `Information about the Reflection 2.0/workout form used by event participants.`,
                        link: toPlainText(canvas?.basic?.form?.link),
                        password: toPlainText(canvas?.basic?.form?.pw),
                    },
                },
                takeaways: {
                    sectionMeaning: `Aggregate participation and event experience metrics from participants' Reflection 2.0 submissions.`,
                    metrics: takeaways,
                    bottomLine: {
                        sectionMeaning: `Top-level summary of what participants took away from the event.`,
                        content: toPlainText(canvas?.takeaways?.bottomLine),
                    },
                },
                scores: {
                    sectionMeaning: `Recursive tree of averaged event-level skill/construct scores and participant score distributions.`,
                    scoreScale: `1-1000, with averageMostLikely as the primary displayed estimate and averageLikelyLow/averageLikelyHigh as uncertainty bounds.`,
                    skills: buildScoreTree(),
                },
                themes: {
                    sectionMeaning: `Most common themes in participants' thinking across reflections.`,
                    items: Array.isArray(canvas?.themes?.data)
                        ? canvas.themes.data.map((theme, index) => ({
                            themeNumber: index + 1,
                            title: toPlainText(theme?.title),
                            detail: toPlainText(theme?.detail),
                            frequency: Number.isFinite(theme?.frequency) ? theme.frequency : null,
                            approximatePercent: Number.isFinite(theme?.frequency) ? Math.round(theme.frequency * 100) : null,
                            evidence: toPlainText(theme?.evidence),
                        })).filter(hasContent)
                        : [],
                },
                comments: {
                    sectionMeaning: `Organizer-facing feedback split into event/group strengths and improvement opportunities.`,
                    constructiveCompliments: {
                        meaning: `Group thinking strengths or event strengths the organizer should preserve.`,
                        bullets: Array.isArray(canvas?.comments?.compliments?.tiles)
                            ? canvas.comments.compliments.tiles.map((tile, index) => ({
                                itemNumber: index + 1,
                                title: toPlainText(tile?.title),
                                detail: toPlainText(tile?.detail),
                            })).filter(hasContent)
                            : [],
                    },
                    constructiveCriticisms: {
                        meaning: `Event improvement opportunities or group struggles the organizer should address.`,
                        bullets: Array.isArray(canvas?.comments?.criticisms?.tiles)
                            ? canvas.comments.criticisms.tiles.map((tile, index) => ({
                                itemNumber: index + 1,
                                title: toPlainText(tile?.title),
                                detail: toPlainText(tile?.detail),
                            })).filter(hasContent)
                            : [],
                    },
                },
                review: {
                    sectionMeaning: `Notable selected participant answer snippets and why they matter for the organizer.`,
                    notableSnippets: Array.isArray(canvas?.review?.tiles)
                        ? canvas.review.tiles.map((tile, index) => ({
                            snippetNumber: index + 1,
                            title: toPlainText(tile?.title),
                            evidence: toPlainText(tile?.evidence),
                            importance: toPlainText(tile?.importance),
                        })).filter(hasContent)
                        : [],
                },
                nextSteps: {
                    sectionMeaning: `Recommended actions for the event organizer after reviewing the analytics, ordered from easiest to hardest.`,
                    steps: Array.isArray(canvas?.next?.tiles)
                        ? canvas.next.tiles.map((tile, index) => ({
                            order: index + 1,
                            tag: toPlainText(tile?.tag),
                            title: toPlainText(tile?.title),
                            instructions: toPlainText(tile?.content),
                        })).filter(hasContent)
                        : [],
                },
            },
        };
    }

    function buildAiContextPrompt() {
        const contextObj = buildOrganizerContextObject();
        const eventTitle = contextObj?.result?.basic?.eventTitle || `the event`;
        const adminName = contextObj?.result?.basic?.adminFirstName || `the organizer`;

        return [
            `I am giving you a structured Reflection 2.0 event analytics context packet.`,
            ``,
            `These analytics are for ${adminName}'s event: "${eventTitle}".`,
            ``,
            `Use the JSON below as grounding context. It includes both:`,
            `1. field explanations, so you understand what each analytics section means, and`,
            `2. the actual aggregate Reflection 2.0 event result data from the page.`,
            ``,
            `Important: this is not one participant's result. It summarizes many participant reflections for an organizer/admin.`,
            ``,
            `Use participant snippets, common themes, aggregate score distributions, group feedback, event comments, and recommended next steps together. Do not rely only on averaged scores, and do not invent event details that are not present here.`,
            ``,
            `You can use this context to help summarize the event, plan follow-up, identify participant learning patterns, improve future event design, draft organizer reports, or create thoughtful next actions.`,
            ``,
            `REFLECTION_EVENT_ANALYTICS_CONTEXT_JSON:`,
            JSON.stringify(contextObj, null, 2),
        ].join(`\n`);
    }

    // --------- CODE-GENERATED SHARE TEXT ---------

    function buildBriefShareText() {
        const snap = getEventAnalyticsSnapshot();
        const scoreLine = buildScoreShareLine(snap);
        const progressLine = (
            Number.isFinite(snap.progressCompleted) && Number.isFinite(snap.progressExpected)
                ? `Reflections: ${snap.progressCompleted}/${snap.progressExpected}${Number.isFinite(snap.completionPercent) ? ` (${snap.completionPercent}%)` : ``}`
                : ``
        );

        return [
            `I just finished running ${snap.eventTitle || `an event`} and reviewing its Reflection 2.0 results.`,
            ``,
            getBriefEventImpactLine(snap),
            ``,
            scoreLine,
            [progressLine, snap.metricsText].filter(Boolean).join(` | `),
        ].filter(Boolean).join(`\n`);
    }

    function buildMediumShareText() {
        const snap = getEventAnalyticsSnapshot();
        const scoreLine = buildScoreShareLine(snap);
        const strengths = getShareBulletText(snap.compliments, 2, 190);
        const growth = getShareBulletText(snap.criticisms, 1, 190);
        const nextStep = getNextStepLine(snap, 220);
        const themeLines = snap.themes.slice(0, 2).map(theme => {
            const pct = Number.isFinite(theme.approximatePercent) ? ` (${theme.approximatePercent}%)` : ``;
            return `- ${truncateText(`${theme.title}${pct}: ${theme.detail}`, 190)}`;
        }).join(`\n`);

        return [
            `I just finished running ${snap.eventTitle || `an event`} and reviewing its Reflection 2.0 results.`,
            snap.date ? `Date: ${snap.date}` : ``,
            Number.isFinite(snap.progressCompleted) && Number.isFinite(snap.progressExpected)
                ? `Reflections: ${snap.progressCompleted}/${snap.progressExpected}${Number.isFinite(snap.completionPercent) ? ` (${snap.completionPercent}%)` : ``}`
                : ``,
            ``,
            scoreLine,
            snap.metricsText ? `Event stats: ${snap.metricsText}` : ``,
            ``,
            `Main impact:`,
            getEventImpactOneLiner(snap, 280),
            themeLines ? `\nTop participant themes:\n${themeLines}` : ``,
            strengths ? `\nWhat I want to preserve:\n${strengths}` : ``,
            growth ? `\nWhat I want to improve next:\n${growth}` : ``,
            nextStep ? `\nWhat I'm doing next:\n- ${nextStep}` : ``,
        ].filter(Boolean).join(`\n`);
    }

    function buildCompactPromptBrief(audience = `general`) {
        const snap = getEventAnalyticsSnapshot();

        return [
            `Reflection 2.0 event analytics share-writing data brief.`,
            ``,
            `Write as the event organizer in first person. The topic is what the event produced across participants, not one individual's result. Do not copy source feedback verbatim; transform it into an authentic organizer voice.`,
            ``,
            `Target output: ${audience}.`,
            ``,
            `Event: ${snap.eventTitle || `N/A`}`,
            `Date: ${snap.date || `N/A`}`,
            `Progress: ${Number.isFinite(snap.progressCompleted) && Number.isFinite(snap.progressExpected) ? `${snap.progressCompleted}/${snap.progressExpected}` : `N/A`}`,
            `Main aggregate score: ${snap.mainScoreTitle}${Number.isFinite(snap.mainScore) ? ` ${snap.mainScore}/1000` : ``}`,
            `Metrics: ${snap.metricsText || `N/A`}`,
            `Organizer impact candidate: ${getBriefEventImpactLine(snap)}`,
            ``,
            `Aggregate score meaning, for meaning only: ${truncateText(snap.mainScoreMeaning, 520) || `N/A`}`,
            `Top group compliment, for meaning only: ${truncateText(snap.mainScoreCompliment, 420) || `N/A`}`,
            `Top group criticism, for meaning only: ${truncateText(snap.mainScoreCriticism, 420) || `N/A`}`,
            ``,
            `Common participant themes:`,
            snap.themes.map(theme => {
                const pct = Number.isFinite(theme.approximatePercent) ? ` (${theme.approximatePercent}%)` : ``;
                return `- ${theme.title}${pct}: ${truncateText(theme.detail, 230)}`;
            }).join(`\n`) || `- N/A`,
            ``,
            `Event/group strengths to transform:`,
            snap.compliments.map(item => `- ${truncateText(`${item.title}: ${item.detail}`, 220)}`).join(`\n`) || `- N/A`,
            ``,
            `Event improvement opportunities to transform:`,
            snap.criticisms.map(item => `- ${truncateText(`${item.title}: ${item.detail}`, 220)}`).join(`\n`) || `- N/A`,
            ``,
            `Concrete next steps:`,
            snap.nextSteps.map(step => `- ${truncateText(step.title, 90)}: ${truncateText(step.content, 190)}`).join(`\n`) || `- N/A`,
            ``,
            `Selected participant evidence. Prefer this over generic summary when deciding the message:`,
            snap.reviewSnippets.map(item => `- ${item.title}: "${truncateText(item.evidence, 220)}" (${truncateText(item.importance, 160)})`).join(`\n`) || `- N/A`,
        ].join(`\n`);
    }

    function buildSocialFallbackShareText() {
        const snap = getEventAnalyticsSnapshot();
        const lessonLine = finishSentence(getConciseImpactFragment(snap)).replace(/[.!?]$/g, ``);
        const themes = snap.themes.slice(0, 4).map(theme => theme.title).filter(Boolean).join(`\n`);
        const nextStep = getNextStepLine(snap, 180);
        const scoreText = Number.isFinite(snap.mainScore)
            ? `${snap.mainScoreTitle}: ${snap.mainScore}/1000`
            : ``;

        return [
            `I recently ran ${snap.eventTitle || `an event`}, and the reflection data surfaced one lesson I keep coming back to:`,
            ``,
            lessonLine,
            ``,
            `The strongest result was not just that participants understood the session. It was that their reflections pointed toward what should become easier to practice after the event.`,
            ``,
            themes ? `The participant signals I want to design around next:\n${themes}` : ``,
            ``,
            [scoreText, snap.metricsText].filter(Boolean).join(` | `),
            ``,
            `My organizer takeaway is simple: event impact gets much easier to improve when reflection data points to the next operating rhythm, not just the next topic.`,
            nextStep ? `\nWhat I am doing next: ${nextStep}` : ``,
        ].filter(Boolean).join(`\n`);
    }

    function buildFormalFallbackShareText() {
        const snap = getEventAnalyticsSnapshot();
        const scoreLine = buildScoreShareLine(snap, false);

        const strengths = snap.compliments.length
            ? `\nStrengths to preserve:\n${snap.compliments.map(item => `- ${truncateText(`${item.title}: ${item.detail}`, 240)}`).join(`\n`)}`
            : ``;

        const growth = snap.criticisms.length
            ? `\nImprovement opportunities:\n${snap.criticisms.map(item => `- ${truncateText(`${item.title}: ${item.detail}`, 240)}`).join(`\n`)}`
            : ``;

        const themes = snap.themes.length
            ? `\nTop participant themes:\n${snap.themes.slice(0, 3).map(theme => {
                const pct = Number.isFinite(theme.approximatePercent) ? ` (${theme.approximatePercent}%)` : ``;
                return `- ${theme.title}${pct}: ${truncateText(theme.detail, 230)}`;
            }).join(`\n`)}`
            : ``;

        const next = snap.nextSteps.length
            ? `\nWhat I plan to do next:\n${snap.nextSteps.slice(0, 3).map(step => `- ${truncateText(step.title, 110)}: ${truncateText(step.content, 240)}`).join(`\n`)}`
            : ``;

        return [
            `Subject: Event Impact Report${snap.eventTitle ? ` - ${snap.eventTitle}` : ``}`,
            ``,
            `Hi,`,
            ``,
            `I wanted to share what I learned from running ${snap.eventTitle || `the event`} and reviewing the Reflection 2.0 results. The data gives me a useful read on what the event changed for participants, where the session created traction, and what I should improve next.`,
            ``,
            `Participation snapshot:`,
            snap.date ? `- Date: ${snap.date}` : ``,
            Number.isFinite(snap.progressCompleted) && Number.isFinite(snap.progressExpected)
                ? `- Reflections completed: ${snap.progressCompleted}/${snap.progressExpected}${Number.isFinite(snap.completionPercent) ? ` (${snap.completionPercent}%)` : ``}`
                : ``,
            scoreLine ? `- ${scoreLine}` : ``,
            snap.metricsText ? `- Key metrics: ${snap.metricsText}` : ``,
            ``,
            `Main learning from the results:`,
            getEventImpactOneLiner(snap, 320),
            ``,
            `What this means for the event:`,
            `The results give me a clearer view of what participants understood, what they are likely to carry into practice, and where the next design iteration should add structure. I am reading the scores and comments as evidence about participant impact, not as a standalone grade.`,
            themes,
            strengths,
            growth,
            next,
            ``,
            `My practical takeaway is that the event produced usable evidence, not just impressions. My next priority is turning the strongest themes into follow-up structure participants can use after the session.`,
        ].filter(Boolean).join(`\n`);
    }

    // --------- PROMPT API HELPERS ---------

    function setPromptShareStatus(status) {
        return setPromptShareStatusForState(promptShareState, status);
    }

    function getPromptShareLoadingText() {
        return getPromptShareLoadingTextForState(promptShareState);
    }

    function updateShareButtonForPromptPrep() {
        return setShareButtonState(getPromptShareLoadingText(), ICONS.loading, true);
    }

    async function generateSocialShareText() {
        const brief = buildCompactPromptBrief(`LinkedIn/professional social post from the event organizer`);

        return await runPromptPipeline({
            label: `social`,
            setStatus: setPromptShareStatus,
            system: `You write grounded first-person LinkedIn posts from event analytics data. You synthesize participant reflection results into one memorable organizer lesson. You never copy feedback verbatim, never invent event details, and never use cringe motivational language.`,
            stages: [
                () => [
                    `Stage 1 of 3: compress the Reflection 2.0 event analytics into a compact writing brief for a strong LinkedIn post.`,
                    ``,
                    `Return only a brief with these labels:`,
                    `Core organizer lesson:`,
                    `Provocative opening angle:`,
                    `Participant signals to use:`,
                    `Useful stat, only if it supports the lesson:`,
                    `Tension or design opportunity:`,
                    `Next design move:`,
                    ``,
                    `Rules for the brief:`,
                    `- Write in first person as the event organizer.`,
                    `- Keep it under 170 words.`,
                    `- Find one synthesized lesson from running the event, not a section-by-section recap.`,
                    `- Use participant evidence before generic score language, and omit score if it distracts.`,
                    `- Do not copy long source feedback sentences.`,
                    `- Prefer one sharp idea over many scattered points.`,
                    `- Do not invent details.`,
                    ``,
                    brief,
                ].join(`\n`),

                (stage1) => [
                    `Stage 2 of 3: write a provocative but professional LinkedIn-style post from this brief.`,
                    ``,
                    `Post requirements:`,
                    `- First person organizer voice.`,
                    `- Start as someone who recently ran the event, then name one lesson I keep coming back to.`,
                    `- Include one short standalone principle line.`,
                    `- Use short paragraphs.`,
                    `- Make the post feel like a personal learning from organizing the event, not an analytics summary.`,
                    `- Explain what participant reflection data revealed about impact and follow-up design.`,
                    `- Mention score or stats only if they add credibility; never use emoji score bars.`,
                    `- Use one compact list only if it makes the participant signals clearer.`,
                    `- End with a concrete next design move or a principle I will use next time.`,
                    `- Sound thoughtful, specific, and a little provocative, not self-congratulatory.`,
                    ``,
                    `Avoid:`,
                    `- Opening with "Reflecting on" or "I reviewed the results".`,
                    `- Addressing the reader as "you".`,
                    `- Copying source feedback.`,
                    `- Fake details, hype, life-coach tone, excessive hashtags.`,
                    `- Markdown formatting.`,
                    ``,
                    `Brief:`,
                    stage1,
                ].join(`\n`),

                (stage2) => [
                    `Stage 3 of 3: format this post for direct pasting into LinkedIn.`,
                    ``,
                    `Rules:`,
                    `- Plain text only.`,
                    `- No markdown asterisks.`,
                    `- Keep paragraph spacing clean.`,
                    `- Sparse unicode emphasis is okay for at most 1-2 key phrases, but plain text is preferred.`,
                    `- Fix any missing spaces after punctuation.`,
                    `- Preserve first-person organizer voice.`,
                    `- Do not invent new content.`,
                    ``,
                    `Post:`,
                    stage2,
                ].join(`\n`),
            ],
        });
    }

    async function generateFormalShareText() {
        const brief = buildCompactPromptBrief(`formal email/report from the event organizer to a boss, mentor, sponsor, or stakeholder`);

        return await runPromptPipeline({
            label: `formal`,
            setStatus: setPromptShareStatus,
            system: `You write clear first-person professional event impact report emails from Reflection 2.0 analytics data. You synthesize participant reflection results into a grounded organizer learning report. You never copy feedback verbatim, never invent event details, and never include placeholder names.`,
            stages: [
                () => [
                    `Stage 1 of 3: compress the Reflection 2.0 event analytics into a formal event impact brief.`,
                    ``,
                    `Return only a brief with these labels:`,
                    `Executive organizer learning:`,
                    `Event and participation facts:`,
                    `Useful quantitative context:`,
                    `Participant impact:`,
                    `Strengths to preserve:`,
                    `Improvement opportunities:`,
                    `Recommended next steps:`,
                    ``,
                    `Rules for the brief:`,
                    `- Write in first person as the event organizer.`,
                    `- Keep it under 250 words.`,
                    `- Focus on what organizing and running the event produced across participants.`,
                    `- Synthesize learnings and impact. Do not make this a pasted analytics inventory.`,
                    `- Use participant evidence first, then aggregate feedback.`,
                    `- Do not copy long source feedback sentences.`,
                    `- Do not include emojis, emoji score bars, or progress-bar visuals.`,
                    `- Do not invent details.`,
                    ``,
                    brief,
                ].join(`\n`),

                (stage1) => [
                    `Stage 2 of 3: write a detailed but readable formal event impact email/report from this brief.`,
                    ``,
                    `Audience: boss, mentor, sponsor, organizer, or professional stakeholder who wants to know what the event meant and what it produced.`,
                    ``,
                    `Requirements:`,
                    `- Include a subject line.`,
                    `- First person organizer voice.`,
                    `- Use "Hi," if a greeting is needed. Do not include placeholders like [Name], [Stakeholder Name], or [Your Name].`,
                    `- Summarize the event, date if present, response progress, numeric score, and useful stats.`,
                    `- Do not include emojis, emoji score bars, or progress-bar visuals.`,
                    `- Give a deep but concise account of what I learned from organizing the event, top participant takeaways, and practical impact.`,
                    `- Include strengths, improvement opportunities, and concrete next steps.`,
                    `- Make it sound like a thoughtful professional update about impact and next design moves, not a pasted analytics report.`,
                    `- Do not copy source feedback.`,
                    ``,
                    `Brief:`,
                    stage1,
                ].join(`\n`),

                (stage2) => [
                    `Stage 3 of 3: format this email/report for direct pasting into email, Substack, or a letter.`,
                    ``,
                    `Rules:`,
                    `- Plain text only.`,
                    `- No markdown.`,
                    `- Preserve the subject line.`,
                    `- Keep paragraph spacing clean.`,
                    `- Use simple section labels only if they help readability.`,
                    `- Preserve first-person organizer voice.`,
                    `- Remove any placeholders, sign-off placeholders, emojis, or emoji score bars if they appear.`,
                    `- Do not invent new content.`,
                    ``,
                    `Draft:`,
                    stage2,
                ].join(`\n`),
            ],
        });
    }

    async function warmPromptShareGeneration(reason = `startup`) {
        if (promptShareState.warmupStarted) return true;

        promptShareState.briefText = promptShareState.briefText || buildBriefShareText();
        promptShareState.generationFailed = false;
        setPromptShareStatus(`checking`);

        const availability = await getPromptApiAvailability();

        if (promptShareState.warmupStarted) return true;

        promptShareState.availability = availability;
        promptShareState.availabilityChecked = true;

        if (![ `available`, `downloadable`, `downloading` ].includes(availability)) {
            promptShareState.fallbackReason = `Prompt API unavailable: ${availability}`;
            setPromptShareStatus(`fallback`);
            console.warn(promptShareState.fallbackReason);
            return false;
        }

        if ([`downloadable`, `downloading`].includes(availability) && !hasUserActivationForPromptApi()) {
            promptShareState.fallbackReason = `Prompt API needs user activation before warmup.`;
            setPromptShareStatus(`idle`);
            armPromptWarmupOnFirstActivation();
            return false;
        }

        promptShareState.warmupStarted = true;
        promptShareState.fallbackReason = ``;
        setPromptShareStatus(`loading-model`);

        console.log(`Starting Prompt API event analytics share warmup: ${reason}`);

        promptShareState.socialPromise = generateSocialShareText()
            .then((text) => {
                if (!text) promptShareState.generationFailed = true;
                promptShareState.socialText = text || buildSocialFallbackShareText();
                return promptShareState.socialText;
            })
            .catch((error) => {
                console.warn(`Social generation failed; using fallback.`, error);
                promptShareState.generationFailed = true;
                promptShareState.socialText = buildSocialFallbackShareText();
                return promptShareState.socialText;
            });

        promptShareState.formalPromise = generateFormalShareText()
            .then((text) => {
                if (!text) promptShareState.generationFailed = true;
                promptShareState.formalText = text || buildFormalFallbackShareText();
                return promptShareState.formalText;
            })
            .catch((error) => {
                console.warn(`Formal generation failed; using fallback.`, error);
                promptShareState.generationFailed = true;
                promptShareState.formalText = buildFormalFallbackShareText();
                return promptShareState.formalText;
            });

        return true;
    }

    function armPromptWarmupOnFirstActivation() {
        if (promptShareState.firstActivationArmed) return true;

        promptShareState.firstActivationArmed = true;

        const kickoff = () => {
            window.removeEventListener(`pointerdown`, kickoff, true);
            window.removeEventListener(`keydown`, kickoff, true);
            window.removeEventListener(`touchstart`, kickoff, true);

            window.setTimeout(() => {
                startShareWarmupNonBlocking(`first user activation`);
            }, 0);
        };

        window.addEventListener(`pointerdown`, kickoff, { once: true, capture: true });
        window.addEventListener(`keydown`, kickoff, { once: true, capture: true });
        window.addEventListener(`touchstart`, kickoff, { once: true, capture: true });

        return true;
    }

    function getShareWarmupPromise(reason = `non-blocking`) {
        if (promptShareState.warmupPromise) return promptShareState.warmupPromise;

        promptShareState.warmupPromise = warmPromptShareGeneration(reason).finally(() => {
            promptShareState.warmupPromise = null;
        });

        promptShareState.warmupPromise.catch((error) => {
            promptShareState.fallbackReason = error?.message || `Prompt API warmup failed.`;
            console.warn(promptShareState.fallbackReason, error);
        });

        return promptShareState.warmupPromise;
    }

    function startShareWarmupNonBlocking(reason = `non-blocking`) {
        getShareWarmupPromise(reason);
        return true;
    }

    function waitForPromptTextGeneration(timeoutMs = 120000) {
        const workPromise = Promise.all([
            promptShareState.socialText
                ? Promise.resolve(promptShareState.socialText)
                : promptShareState.socialPromise,
            promptShareState.formalText
                ? Promise.resolve(promptShareState.formalText)
                : promptShareState.formalPromise,
        ]);

        return new Promise((resolve) => {
            let settled = false;

            const timeoutId = window.setTimeout(() => {
                if (settled) return;

                settled = true;
                promptShareState.fallbackReason = `Prompt API generation timed out before share choices opened.`;
                promptShareState.generationFailed = true;
                setPromptShareStatus(`fallback`);
                console.warn(promptShareState.fallbackReason);
                resolve(false);
            }, timeoutMs);

            workPromise
                .then(() => {
                    if (settled) return;

                    settled = true;
                    window.clearTimeout(timeoutId);
                    setPromptShareStatus(promptShareState.generationFailed ? `fallback` : `ready`);
                    resolve(Boolean(
                        !promptShareState.generationFailed &&
                        promptShareState.socialText &&
                        promptShareState.formalText
                    ));
                })
                .catch((error) => {
                    if (settled) return;

                    settled = true;
                    window.clearTimeout(timeoutId);
                    promptShareState.generationFailed = true;
                    promptShareState.fallbackReason = error?.message || `Prompt API generation failed.`;
                    setPromptShareStatus(`fallback`);
                    console.warn(promptShareState.fallbackReason, error);
                    resolve(false);
                });
        });
    }

    async function preparePromptShareChoices() {
        promptShareState.briefText = promptShareState.briefText || buildBriefShareText();

        if (
            promptShareState.socialText &&
            promptShareState.formalText &&
            !promptShareState.generationFailed
        ) {
            setPromptShareStatus(`ready`);
            return true;
        }

        updateShareButtonForPromptPrep();

        const updateIntervalId = window.setInterval(updateShareButtonForPromptPrep, 250);

        try {
            const warmupStarted = await getShareWarmupPromise(`share click`);

            if (!warmupStarted || !promptShareState.warmupStarted) return false;

            updateShareButtonForPromptPrep();

            return await waitForPromptTextGeneration();
        } finally {
            window.clearInterval(updateIntervalId);
        }
    }

    // --------- SHARE MODE UI ---------

    function getShareChoice(type) {
        return shareChoiceButtons.find(choice => choice?.type === type) || null;
    }

    function openShareMode() {
        if (shareModeOpen) return false;
        if (!shareButtonParent) return false;

        shareModeOpen = true;

        shareChoiceButtons.forEach(choice => choice?.button?.remove());
        shareChoiceButtons = [];

        const choices = [
            createShareChoiceButton({
                shareButton,
                selectors: shareChoiceSelectors,
                onClick: handleShareChoiceClick,
                type: `brief`,
                text: `Text`,
                icon: ICONS.brief,
                label: `Share a brief Reflection 2.0 event summary`,
            }),
            createShareChoiceButton({
                shareButton,
                selectors: shareChoiceSelectors,
                onClick: handleShareChoiceClick,
                type: `social`,
                text: `Social`,
                icon: ICONS.social,
                label: `Share a LinkedIn-style Reflection 2.0 event post`,
            }),
            createShareChoiceButton({
                shareButton,
                selectors: shareChoiceSelectors,
                onClick: handleShareChoiceClick,
                type: `formal`,
                text: `Formal`,
                icon: ICONS.detailed,
                label: `Share a formal Reflection 2.0 event summary`,
            }),
            createShareChoiceButton({
                shareButton,
                selectors: shareChoiceSelectors,
                onClick: handleShareChoiceClick,
                type: `exit`,
                text: `Exit`,
                icon: ICONS.exit,
                label: `Exit share options`,
            }),
        ].filter(Boolean);

        shareChoiceButtons = choices;

        if (contextButton) contextButton.style.display = `none`;
        if (shareButton) shareButton.style.display = `none`;

        const frag = document.createDocumentFragment();

        choices.forEach(choice => {
            frag.appendChild(choice.button);
        });

        shareButtonParent.appendChild(frag);

        return true;
    }

    function exitShareMode() {
        shareModeOpen = false;
        shareInProgress = false;

        shareChoiceButtons.forEach(choice => choice?.button?.remove());
        shareChoiceButtons = [];

        if (contextButton) contextButton.style.display = `flex`;
        if (shareButton) shareButton.style.display = `flex`;

        setContextButtonState(`Use with AI`, ICONS.contextDefault, false);
        setShareButtonState(`Share Results`, ICONS.shareDefault, false);

        return true;
    }

    async function getShareTextByType(type) {
        if (type === `brief`) return promptShareState.briefText || buildBriefShareText();

        if (!promptShareState.warmupStarted) startShareWarmupNonBlocking(`choice click backup`);

        if (type === `social`) {
            return await resolveTextWithFallback(
                promptShareState.socialText
                    ? Promise.resolve(promptShareState.socialText)
                    : promptShareState.socialPromise,
                28000,
                buildSocialFallbackShareText,
                `Social share`
            );
        }

        if (type === `formal`) {
            return await resolveTextWithFallback(
                promptShareState.formalText
                    ? Promise.resolve(promptShareState.formalText)
                    : promptShareState.formalPromise,
                32000,
                buildFormalFallbackShareText,
                `Formal share`
            );
        }

        return buildMediumShareText();
    }

    function isShareTextReady(type) {
        if (type === `brief`) return true;
        if (type === `social`) return Boolean(promptShareState.socialText || !promptShareState.socialPromise);
        if (type === `formal`) return Boolean(promptShareState.formalText || !promptShareState.formalPromise);
        return true;
    }

    async function handleContextClick() {
        if (contextCopyInProgress) return false;
        contextCopyInProgress = true;

        try {
            setContextButtonState(`Preparing...`, ICONS.loading, true);
            await new Promise(resolve => requestAnimationFrame(resolve));

            const contextText = buildAiContextPrompt();
            const copied = await copyTextToClipboard(contextText);

            if (!copied) throw new Error(`Clipboard copy returned false.`);

            flashContextButtonState(`Copied`, ICONS.check);
        } catch (error) {
            console.error(`Unable to copy Reflection 2.0 event analytics AI context`, error);
            flashContextButtonState(`Copy Failed`, ICONS.warning);
        }

        return true;
    }

    async function handleShareChoiceClick(type) {
        if (type === `exit`) return exitShareMode();

        if (shareInProgress) return false;
        shareInProgress = true;

        const choice = getShareChoice(type);

        try {
            const needsTextWait = !isShareTextReady(type);

            if (needsTextWait) {
                setShareChoiceState(choice, `Loading...`, ICONS.loading, true);
            }

            const text = await getShareTextByType(type);
            const title = `${getEventAnalyticsSnapshot().eventTitle || `Reflection 2.0`} Event Results`;
            const result = await shareOrCopyText(text, title);

            setShareChoiceState(
                choice,
                result === `shared` ? `Shared` : `Copied`,
                ICONS.check,
                true
            );

            window.setTimeout(() => {
                resetShareChoiceState(choice);
                shareInProgress = false;
            }, 5000);
        } catch (error) {
            console.error(`Unable to share ${type} event analytics result.`, error);

            setShareChoiceState(choice, `Share Failed`, ICONS.warning, true);

            window.setTimeout(() => {
                resetShareChoiceState(choice);
                shareInProgress = false;
            }, 5000);
        }

        return true;
    }

    async function handleFallbackShareClick() {
        if (shareInProgress) return false;
        shareInProgress = true;

        try {
            setShareButtonState(`Preparing...`, ICONS.loading, true);

            const text = buildMediumShareText();
            const title = `${getEventAnalyticsSnapshot().eventTitle || `Reflection 2.0`} Event Results`;
            const result = await shareOrCopyText(text, title);

            flashShareButtonState(result === `shared` ? `Shared` : `Copied`, ICONS.check);
        } catch (error) {
            console.error(`Unable to share fallback Reflection 2.0 event analytics result.`, error);
            flashShareButtonState(`Share Failed`, ICONS.warning);
        }

        return true;
    }

    async function handleShareClick() {
        if (shareModeOpen || shareInProgress) return false;

        if (!canUsePromptShareChoices(promptShareState)) {
            handleFallbackShareClick();
            return true;
        }

        shareInProgress = true;

        try {
            const readyForChoices = await preparePromptShareChoices();

            shareInProgress = false;

            if (readyForChoices) {
                openShareMode();
                return true;
            }

            return await handleFallbackShareClick();
        } catch (error) {
            console.warn(`Prompt event analytics share preparation failed; using fallback share.`, error);
            promptShareState.generationFailed = true;
            promptShareState.fallbackReason = error?.message || `Prompt event analytics share preparation failed.`;
            setPromptShareStatus(`fallback`);
            shareInProgress = false;
            return await handleFallbackShareClick();
        }
    }

    // --------- INITIAL VISUALS ---------

    setContextButtonState(`Use with AI`, ICONS.contextDefault, false);
    setShareButtonState(`Share Results`, ICONS.shareDefault, false);

    setupButton(
        contextButton,
        `Copy Reflection 2.0 event analytics to use with AI`,
        handleContextClick
    );

    setupButton(
        shareButton,
        `Share Reflection 2.0 event analytics`,
        handleShareClick
    );

    promptShareState.briefText = buildBriefShareText();
    startShareWarmupNonBlocking(`manageActions startup`);
    armPromptWarmupOnFirstActivation();

    return true;
}

/**
 * handles key takeaways
 * @param {object} commonElements all common objs
 * @param {object} pageElements all page objs
 * @returns {boolean} true
 */
async function manageTakeaways(commonElements, pageElements) {
    // ----------- top texts -----------
    // set sub title
    pageElements.canvas.main.takeaways.top.subTitle.textContent = `Check out your event stats, ${allPageData.canvas.basic.name}.`;

    // top info
    pageElements.canvas.main.takeaways.top.info.progress.textContent = `${Math.round(allPageData.canvas.basic.progress.completed / allPageData.canvas.basic.progress.expected * 100)}% Completed`;
    pageElements.canvas.main.takeaways.top.info.form.innerHTML = allPageData.canvas.basic.form.link
        ? `<a href="${allPageData.canvas.basic.form.link}" target="_blank" rel="noopener noreferrer">form</a> ` + (allPageData.canvas.basic.form.pw ? `(pw: ${allPageData.canvas.basic.form.pw})` : ``)
        : `No Form`;
    pageElements.canvas.main.takeaways.top.info.basic.textContent = `${allPageData.canvas.basic.title} | ${timestampToFormalDate(allPageData.canvas.basic.date, userData.timeZone)}`;

    // help
    attachHelpText(
        pageElements.canvas.main.takeaways.top.info.progress, 
        `${allPageData.canvas.basic.progress.completed} / ${allPageData.canvas.basic.progress.expected}`,
        `auto`,
    );

    // ----------- tiles -----------
    // clear any existing tiles
    pageElements.canvas.main.takeaways.tiles.div.innerHTML = ``; // clear parent

    // frag
    let frag = document.createDocumentFragment();

    // render tiles
    allPageData.canvas.takeaways.data.forEach((tileData, index) => {
        // clone tile
        let tileEle = pageElements.canvas.main.takeaways.tiles.tile.div.cloneNode(true);

        // set big (main)
        tileEle.querySelector(`[highMain]`).innerHTML = tileData.main;
        
        // set smaller (label)
        tileEle.querySelector(`[highLabel]`).innerHTML = tileData.label;
        
        // set smallest (sub-text)
        tileEle.querySelector(`[highSub]`).innerHTML = tileData.subText;

        // set help text
        attachHelpText(tileEle, tileData.help, `auto`);

        // append to fragment, not directly to DOM
        frag.appendChild(tileEle);            
    });

    // move
    pageElements.canvas.main.takeaways.tiles.div.appendChild(frag);

    // ----------- bottom line -----------
    pageElements.canvas.main.takeaways.bottomLine.content.innerHTML = `Bottom Line: ` + allPageData.canvas.takeaways.bottomLine;

    return true;
}

/**
 * handles brain stats (scores, explanation)
 * @param {object} commonElements all common objs
 * @param {object} pageElements all page objs
 * @returns {boolean} true
 */
async function manageBrainStats(commonElements, pageElements) {
    /**
     * manage and render stats area
     * @returns {boolean} true
     */
    async function manageStats() {
        /* ------ visuals ------ */

        let scoresResizeFrame;
        let scoresResizeTimeout;
        let lastScoresMapSize = {
            windowWidth: 0,
            windowHeight: 0,
            canvasWidth: 0,
            canvasHeight: 0,
        };

        /**
         * gets current score map dimensions
         * @returns {object} current score map size
         */
        function getScoresMapSize() {
            return {
                windowWidth: Math.round(pageElements.canvas.main.scores.window?.clientWidth || 0),
                windowHeight: Math.round(pageElements.canvas.main.scores.window?.clientHeight || 0),
                canvasWidth: Math.round(pageElements.canvas.main.scores.canvas.div?.clientWidth || 0),
                canvasHeight: Math.round(pageElements.canvas.main.scores.canvas.div?.clientHeight || 0),
            };
        }

        /**
         * stores current score map dimensions after a render
         * @returns {boolean} true
         */
        function updateScoresMapSize() {
            lastScoresMapSize = getScoresMapSize();

            return true;
        }

        /**
         * checks whether score map dimensions changed
         * @returns {boolean} true if dimensions changed
         */
        function scoresMapSizeChanged() {
            const currentSize = getScoresMapSize();

            return Object.keys(currentSize).some(key => currentSize[key] !== lastScoresMapSize[key]);
        }

        /**
         * schedules a score map re-render after layout settles
         * @param {number} waitMs debounce time in ms
         * @param {boolean} force whether to render even if dimensions look unchanged
         * @returns {boolean} true
         */
        function scheduleRenderScores(waitMs = 0, force = false) {
            if (scoresResizeTimeout) clearTimeout(scoresResizeTimeout);
            if (scoresResizeFrame) cancelAnimationFrame(scoresResizeFrame);

            const run = () => {
                scoresResizeFrame = requestAnimationFrame(() => {
                    if (!force && !scoresMapSizeChanged()) return;
                    renderScores();
                });
            };

            if (waitMs > 0) {
                scoresResizeTimeout = setTimeout(run, waitMs);
            } else {
                run();
            }

            return true;
        }

        /**
         * renders scores
         */
        function renderScores() {
            // hide info div initially
            pageElements.canvas.main.scores.extra.div.style.display = `none`;
            pageElements.canvas.main.scores.extra.info.div.style.display = `none`;
            pageElements.canvas.main.scores.extra.distribution.div.style.display = `none`;
            pageElements.canvas.main.scores.extra.groups.div.style.display = `none`;            

            // replace canvas on re-render
            let old = pageElements.canvas.main.scores.window;
            let copy = old.cloneNode(true);
            old.parentNode.replaceChild(copy, old);
            pageElements.canvas.main.scores.window = copy;
            pageElements.canvas.main.scores.canvas.div = copy.querySelector(`[scrCnv]`); // MATCH IN PAGEELES

            // key elements TEMPORARY
            let windowDiv = pageElements.canvas.main.scores.window;
            let canvas = pageElements.canvas.main.scores.canvas.div;
            let layer1Div = pageElements.canvas.main.scores.canvas.level1.div.cloneNode(true);
            let layer2Div = pageElements.canvas.main.scores.canvas.level2.div.cloneNode(true);
            let layer3Div = pageElements.canvas.main.scores.canvas.level3.div.cloneNode(true);

            // re-render: reset transforms
            canvas.style.transform = 'none';
            canvas.style.transformOrigin = '0 0';

            // line config
            let lineConfig = { color: '#000', width: 2 };

            /* ------- ZOOM/CANVAS INTERACTIONS ------- */

            // record original canvas size
            let origCW = canvas.clientWidth;
            let origCH = canvas.clientHeight;

            // state
            let scale = 1;
            let maxScale = 3;

            // ensure zooms originate at top-left of canvas
            canvas.style.transformOrigin = '0 0';

            // zoom helper functions
            const clamp = (v, min, max) => Math.min(Math.max(v, min), max);
            function getDynMin() {
                return Math.max(
                    windowDiv.clientWidth  / (origCW),
                    windowDiv.clientHeight / (origCH)
                );
            }
            function clampScroll() {
                const sw = origCW * scale;
                const sh = origCH * scale;
                const maxX = Math.max(0, sw - windowDiv.clientWidth);
                const maxY = Math.max(0, sh - windowDiv.clientHeight);
                windowDiv.scrollLeft = clamp(windowDiv.scrollLeft, 0, maxX);
                windowDiv.scrollTop  = clamp(windowDiv.scrollTop,  0, maxY);
            }
            /**
             * apply zoom to score canvas
             * @param {*} newScale 
             * @param {*} cx 
             * @param {*} cy 
             */
            function applyZoom(newScale, clientX, clientY) {
                const dynMin = getDynMin();
                newScale = clamp(newScale, dynMin, maxScale);
                const prevScale = scale;

                // to check element viz
                let dx = (clientX - sx)/scale, 
                    dy = (clientY - sy)/scale;
                const newLeft = ox - dx;
                const newTop  = oy - dy;
            
                // pointer relative to windowDiv
                const rect = windowDiv.getBoundingClientRect();
                const mx = clientX - rect.left;
                const my = clientY - rect.top;
            
                // compute unscaled content point under pointer
                const contentX = (windowDiv.scrollLeft + mx) / prevScale;
                const contentY = (windowDiv.scrollTop  + my) / prevScale;
            
                // apply new scale
                scale = newScale;
                canvas.style.transform = `scale(${scale})`;
            
                // compute new scroll to keep contentX contentY under pointer
                windowDiv.scrollLeft = contentX * scale - mx;
                windowDiv.scrollTop  = contentY * scale - my;
            
                // clamp 
                clampScroll();
            }

            // clear score canvas
            let isDragging = false, 
                hasDragged,
                sx, sy, ox, oy;
            canvas.innerHTML = ``;

            // add elements for bounding elements
            canvas.appendChild(layer1Div);
            canvas.appendChild(layer2Div);
            canvas.appendChild(layer3Div);

            // handle grabbing/dragging - helper
            function handleEvents() {
                windowDiv.style.cursor = 'grab';
                windowDiv.addEventListener('mousedown', e => {
                    isDragging = true;
                    hasDragged = false;
                    windowDiv.style.cursor = 'grabbing';
                    sx = e.clientX;
                    sy = e.clientY;
                    ox = windowDiv.scrollLeft;
                    oy = windowDiv.scrollTop;
                    e.preventDefault();
                });
                ['mouseup','mouseleave'].forEach(ev =>
                    windowDiv.addEventListener(ev, async function() {
                        windowDiv.style.cursor = 'grab';

                        isDragging = false;
                    })
                );
                windowDiv.addEventListener('mousemove', e => {
                    // exclude non-grabs
                    if (!isDragging) return;

                    // when drag is big enough, count as drag
                    hasDragged ||= Math.hypot(e.clientX - sx, e.clientY - sy) > 5;

                    let dx = (e.clientX - sx)/scale, 
                        dy = (e.clientY - sy)/scale;
        
                    const newLeft = ox - dx;
                    const newTop  = oy - dy;
                
                    // apply temporarily
                    const prevLeft = windowDiv.scrollLeft;
                    const prevTop  = windowDiv.scrollTop;
                    windowDiv.scrollLeft = newLeft;
                    windowDiv.scrollTop  = newTop;
                
                    // check each layer element remains at least partly visible
                    const winW = windowDiv.clientWidth;
                    const winH = windowDiv.clientHeight;
                    let anyPartVisible = false;
                    let childrenIndex = 0;
                    for (const el of canvas.children) {
                        if (childrenIndex == 0) {
                            childrenIndex++;
                            continue; // skip lines canvas (dinemsions aren't zoomed correctlyuy)
                        }
                        const elW = el.offsetWidth * scale;
                        const elH = el.offsetHeight * scale;
                        const elX = el.offsetLeft * scale - newLeft;
                        const elY = el.offsetTop  * scale - newTop;
                        const intersects = 
                            elX + elW > 0 && elX < winW &&
                            elY + elH > 0 && elY < winH;
                        if (intersects) {
                            anyPartVisible = true;
                            break;
                        }
                        childrenIndex++;
                    }

                    // if any element fully out of view, revert
                    if (!anyPartVisible) {
                        windowDiv.scrollLeft = prevLeft;
                        windowDiv.scrollTop  = prevTop;
                    }
                    drawLines();
                });
        
                // wheel zoom (vertical wheel → zoom; horizontal wheel → scroll)
                windowDiv.addEventListener('scroll', drawLines);
                windowDiv.addEventListener('wheel', e => {
                    if (e.deltaY === 0) return;
                    e.preventDefault();
        
                    // speed up pinch-zoom on touchpads (pinch fires wheel with ctrlKey)
                    const multiplier = e.ctrlKey ? 0.02 : 0.001;
                    const factor = 1 - e.deltaY * multiplier;

                    applyZoom(scale * factor, e.clientX, e.clientY);
        
                    // lines
                    drawLines();
                }, { passive: false });
        
                // pinch to zoom on mobile
                let pinchDist = null,
                    startScale = 1,
                    center = { x:0, y:0 };
        
                windowDiv.addEventListener('touchstart', e => {
                    if (e.touches.length === 2) {
                        const [a,b] = e.touches;
                        pinchDist = Math.hypot(b.clientX-a.clientX, b.clientY-a.clientY);
                        startScale = scale;
                        center.x = (a.clientX+b.clientX)/2;
                        center.y = (a.clientY+b.clientY)/2;
                        e.preventDefault();
                    }
                }, { passive: false });
        
                windowDiv.addEventListener('touchmove', e => {
                    if (e.touches.length === 2 && pinchDist) {
                        const [a,b] = e.touches;
                        const dist = Math.hypot(b.clientX-a.clientX, b.clientY-a.clientY);

                        // base pinch factor
                        const rawFactor = dist / pinchDist;

                        // assume “computer” pinch when viewport ≥1100px → speed it up
                        const pinchMultiplier = window.innerWidth >= 1000 ? 1.25 : 1;
                        const factor = rawFactor * pinchMultiplier;

                        applyZoom(startScale * factor, center.x, center.y);
                        drawLines();
                        e.preventDefault();
                    }
                }, { passive: false });
        
                windowDiv.addEventListener('touchend', e => {
                    if (e.touches.length < 2) pinchDist = null;
                });

                return true;
            }
            handleEvents();

            /* ------- BACKGROUND LINES ------- */

            // helper
            function drawLines() {
                ctx.clearRect(0,0,origCW,origCH);
                // center of layer1
                const l1cx = layer1Div.offsetLeft + layer1Div.offsetWidth/2;
                const l1cy = layer1Div.offsetTop  + layer1Div.offsetHeight/2;
                ctx.strokeStyle = lineConfig.color;
                ctx.lineWidth   = lineConfig.width;
                l2Clones.forEach((l2,i) => {
                    const x2c = l2.offsetLeft + l2.offsetWidth/2;
                    const y2c = l2.offsetTop  + l2.offsetHeight/2;
                    ctx.beginPath(); ctx.moveTo(l1cx,l1cy); ctx.lineTo(x2c,y2c); ctx.stroke();
                    l3Clones[i].forEach(l3 => {
                        const x3c = l3.offsetLeft + l3.offsetWidth/2;
                        const y3c = l3.offsetTop  + l3.offsetHeight/2;
                        ctx.beginPath(); ctx.moveTo(x2c,y2c); ctx.lineTo(x3c,y3c); ctx.stroke();
                    });
                });
            }

            // html canvas underlayer
            const lineCanvas = document.createElement('canvas');
            lineCanvas.id = 'lineCanvas';
            lineCanvas.width = origCW;
            lineCanvas.height = origCH;
            lineCanvas.style.position = 'absolute';
            lineCanvas.style.top = '0';
            lineCanvas.style.left = '0';
            lineCanvas.style.pointerEvents = 'none';
            lineCanvas.style.zIndex = '0';
            canvas.insertBefore(lineCanvas, canvas.firstChild);
            const ctx = lineCanvas.getContext('2d');

            // Store clones for line drawing
            let l2Clones = [];
            let l3Clones = [];

            /* ------- RENDER SCORE TILES (LAYERS 1-3) ------- */

            // LAYER 1
            // set position - place main score at canvas center
            let cx = origCW/2,
                cy = origCH/2;
            let r1 = layer1Div.getBoundingClientRect();
            layer1Div.style.left = `${cx - r1.width/2}px`;
            layer1Div.style.top = `${cy - r1.height/2}px`;

            // assign elements
            let circleDiv = layer1Div.querySelector(`[circleDiv]`);
            let radarDiv = layer1Div.querySelector(`[radarDiv]`);
            let scoreDiv = layer1Div.querySelector(`[scoreDiv]`); // around score text
            let score = layer1Div.querySelector(`[score]`);
            let levelDiv = layer1Div.querySelector(`[levelDiv]`);
            let levelIcn = layer1Div.querySelector(`[levelIcon]`);
            let levelTxt = layer1Div.querySelector(`[levelText]`);
            let icon = layer1Div.querySelector(`[iconDiv]`);
            let title = layer1Div.querySelector(`[title]`);
            let item = allPageData.canvas.scores.c0;

            // set text & icon
            score.textContent = item.num.mid; // raw score
            levelTxt.textContent = scoreLvl( // level
                item.num,
                `long`,
                false,
                true,
            );
            title.textContent = item.title; // title
            icon.innerHTML = item.icon; // icon

            // set colors
            [icon, title].forEach((ele) => { ele.style.color = item.theme.color.text; });
            scoreDiv.style.backgroundColor = item.theme.color.highlight;
            score.style.color = item.theme.color.contrast;
            circleDiv.style.backgroundColor = convertToRGBA(item.theme.color.highlight, 0.1);
            levelDiv.style.display = `none`; // DONT SHOW

            // help text
            attachHelpText(layer1Div, item.title, `auto`);

            // click functions
            /**
             * render extra info: construct/circle items
             * @param {object} scoreObj page data object of one skill
             */
            function setCircle(scoreObj) {
                /* ---- RENDER INNER CIRCLE ITEMS ---- */

                // construct name
                pageElements.canvas.main.scores.extra.info.construct.titleText.textContent = scoreObj.title;

                // set text & icon
                pageElements.canvas.main.scores.extra.info.construct.circle.scoreText.textContent = scoreObj.num.mid; // raw score
                pageElements.canvas.main.scores.extra.info.construct.circle.icon.innerHTML = scoreObj.icon; // icon

                // set colors
                [
                    pageElements.canvas.main.scores.extra.info.construct.circle.icon, 
                ].forEach((ele) => { ele.style.color = scoreObj.theme.color.text; });
                pageElements.canvas.main.scores.extra.info.construct.circle.scoreDiv.style.backgroundColor = scoreObj.theme.color.highlight;
                pageElements.canvas.main.scores.extra.info.construct.circle.scoreText.style.color = scoreObj.theme.color.contrast;
                pageElements.canvas.main.scores.extra.info.construct.circle.div.style.backgroundColor = convertToRGBA(scoreObj.theme.color.highlight, 0.1);

                /* ---- RENDER RADAR ---- */

                // render radar
                pageElements.canvas.main.scores.extra.info.construct.circle.radar.id = `infCstCrlRdr`;
                generateRadarChart(
                    (scoreObj.num.mid / 1000) * 100,
                    pageElements.canvas.main.scores.extra.info.construct.circle.radar.id,
                    scoreObj.theme.color.highlight,
                    convertToRGBA(scoreObj.theme.color.highlight, 0.2),
                );

                /* ---- RENDER SCALE GRADIENT ---- */

                pageElements.canvas.main.scores.extra.info.construct.range.band.style.backgroundImage =
                    `radial-gradient(circle farthest-corner at 50% 50%, ` +
                    `${scoreObj.theme.color.highlight} 0%, ` +
                    `transparent 100%)`;

                pageElements.canvas.main.scores.extra.info.construct.range.lowText.textContent = scoreObj.num.low;
                pageElements.canvas.main.scores.extra.info.construct.range.highText.textContent = scoreObj.num.high;
            }
            /**
             * extra info: render construct feedback
             * @param {object} scoreObj page data object of one skill
             */
            function setFeedback(scoreObj) {
                // meaning
                pageElements.canvas.main.scores.extra.info.feedback.meaning.content.innerHTML = scoreObj.description.meaning.content;

                // compliments
                pageElements.canvas.main.scores.extra.info.feedback.compliments.content.innerHTML = scoreObj.description.compliments.content;

                // criticisms
                pageElements.canvas.main.scores.extra.info.feedback.criticisms.content.innerHTML = scoreObj.description.criticisms.content;

                // definition
                pageElements.canvas.main.scores.extra.info.feedback.definition.content.innerHTML = scoreObj.description.definition.content;
            }
            /**
             * render selected skill score distribution as a D3 dot plot
             * @param {object} scoreObj page data object of one skill
             * @param {Array<{name: string, number: number}>} scoreObj.distribution score distribution items
             * @returns {boolean} true
             */
            function setDistribution(scoreObj) {
                const graphCanvas = pageElements.canvas.main.scores.extra.distribution.div;
                if (!graphCanvas) return false;
                graphCanvas.style.display = `flex`;

                // Re-render from a clean canvas on every skill selection.
                graphCanvas.innerHTML = ``;
                d3.select(`body`).selectAll(`[scoreDistributionTooltip]`).remove();

                const rect = graphCanvas.getBoundingClientRect();
                const containerWidth = Math.max(160, rect.width || graphCanvas.clientWidth || 320);
                const containerHeight = Math.max(110, rect.height || graphCanvas.clientHeight || 140);
                const margin = {
                    top: 10,
                    right: 16,
                    bottom: 28,
                    left: 16,
                };
                const plotLeft = margin.left;
                const plotRight = containerWidth - margin.right;
                const axisY = containerHeight - margin.bottom;
                const plotTop = margin.top;
                const plotBottom = axisY - 10;
                const plotHeight = Math.max(32, plotBottom - plotTop);
                const centerY = plotTop + plotHeight / 2;
                const dotColor = convertToRGBA(scoreObj.theme.color.highlight, 1);

                // Use only valid score entries and keep scores inside the fixed axis domain.
                const distribution = Array.isArray(scoreObj?.distribution) ? scoreObj.distribution : [];
                const data = distribution
                    .map((item, index) => ({
                        name: item?.name || `Person ${index + 1}`,
                        number: Number(item?.number),
                    }))
                    .filter(item => Number.isFinite(item.number))
                    .map(item => ({
                        ...item,
                        number: Math.min(Math.max(item.number, 0), 1000),
                    }));

                const xScale = d3.scaleLinear()
                    .domain([0, 1000])
                    .range([plotLeft, plotRight]);

                /**
                 * choose x-axis ticks based on available width while always showing 0, 500, and 1000
                 * @returns {number[]} tick values
                 */
                function getTickValues() {
                    const innerWidth = plotRight - plotLeft;
                    const step = innerWidth >= 900 ? 100 : innerWidth >= 520 ? 250 : 500;
                    return d3.range(0, 1000 + step, step).filter(value => value <= 1000);
                }

                const svg = d3.select(graphCanvas)
                    .append(`svg`)
                    .attr(`width`, containerWidth)
                    .attr(`height`, containerHeight)
                    .attr(`viewBox`, `0 0 ${containerWidth} ${containerHeight}`)
                    .style(`display`, `block`)
                    .style(`overflow`, `hidden`);

                // Bottom x-axis only; no title or y-axis are rendered for this compact plot.
                const xAxis = d3.axisBottom(xScale)
                    .tickValues(getTickValues())
                    .tickSizeOuter(0)
                    .tickFormat(d => d);

                const axis = svg.append(`g`)
                    .attr(`class`, `score-distribution-x-axis`)
                    .attr(`transform`, `translate(0,${axisY})`)
                    .call(xAxis);

                // X-AXIS BASELINE: edit here to bring the horizontal axis line back.
                // To show it again, remove/comment the display none line below.
                axis.selectAll(`path`)
                    .attr(`class`, `score-distribution-axis-line`)
                    .style(`display`, `none`);
                    // Optional visible baseline styling:
                    // .attr(`stroke`, `#000`)
                    // .attr(`stroke-width`, 1.5);

                // X-AXIS TICK LINES: edit here to bring the vertical tick marks back.
                // To show them again, remove/comment the display none line below.
                axis.selectAll(`line`)
                    .attr(`class`, `score-distribution-axis-tick-line`)
                    .style(`display`, `none`);
                    // Optional visible tick styling:
                    // .attr(`stroke`, `#000`)
                    // .attr(`stroke-width`, 1);

                // X-AXIS NUMBER LABELS: use this class for font/color control in CSS.
                axis.selectAll(`text`)
                    .attr(`class`, `custom-graph-label`)
                    .attr(`fill`, `#000`)
                    .style(`font-size`, null);

                if (data.length === 0) return true;

                const scoreCounts = new Map();
                data.forEach(item => {
                    scoreCounts.set(item.number, (scoreCounts.get(item.number) || 0) + 1);
                });
                const maxSameScore = Math.max(...scoreCounts.values(), 1);
                const areaRadius = Math.sqrt(((plotRight - plotLeft) * plotHeight) / (data.length * Math.PI)) * 0.38;
                const stackRadius = plotHeight / (maxSameScore * 2.4);
                const radius = Math.min(12.5, Math.max(4, Math.min(areaRadius, stackRadius)));
                const maxXDrift = Math.max(6, radius * 2.5);

                const nodes = data.map(item => ({
                    ...item,
                    x: xScale(item.number),
                    y: centerY,
                    xTarget: xScale(item.number),
                }));

                /**
                 * keep simulated dots inside the graph area and close to their score x-position
                 * @returns {void}
                 */
                function boundNodes() {
                    nodes.forEach(node => {
                        const minX = Math.max(plotLeft + radius, node.xTarget - maxXDrift);
                        const maxX = Math.min(plotRight - radius, node.xTarget + maxXDrift);
                        node.x = Math.min(Math.max(node.x, minX), maxX);
                        node.y = Math.min(Math.max(node.y, plotTop + radius), plotBottom - radius);
                    });
                }

                // Run the force simulation synchronously so the dots are settled when the chart appears.
                const simulation = d3.forceSimulation(nodes)
                    .force(`x`, d3.forceX(d => d.xTarget).strength(0.95))
                    .force(`y`, d3.forceY(centerY).strength(0.08))
                    .force(`collide`, d3.forceCollide(radius + 1.5).iterations(4))
                    .stop();

                for (let i = 0; i < 260; i++) {
                    simulation.tick();
                    boundNodes();
                }

                const tooltip = d3.select(`body`)
                    .append(`div`)
                    .attr(`scoreDistributionTooltip`, ``)
                    .attr(`class`, `custom-graph-tooltip`)
                    .style(`position`, `fixed`)
                    .style(`display`, `none`)
                    .style(`pointer-events`, `none`)
                    .style(`z-index`, `9999`)
                    .style(`background`, `rgba(0, 0, 0, 0.78)`)
                    .style(`color`, `#fff`)
                    .style(`border-radius`, `6px`)
                    .style(`padding`, `6px 8px`);

                svg.append(`g`)
                    .selectAll(`circle`)
                    .data(nodes)
                    .enter()
                    .append(`circle`)
                    .attr(`cx`, d => d.x)
                    .attr(`cy`, d => d.y)
                    .attr(`r`, radius)
                    .attr(`fill`, dotColor)
                    .attr(`stroke`, `#000`)
                    .attr(`stroke-width`, 1)
                    .attr(`opacity`, 0.9)
                    .on(`mouseenter`, function(event, d) {
                        d3.select(this)
                            .attr(`opacity`, 1)
                            .attr(`stroke-width`, 2);
                        tooltip
                            .style(`display`, `block`)
                            .text(`${d.name}: ${Math.round(d.number)}`);
                    })
                    .on(`mousemove`, function(event) {
                        tooltip
                            .style(`left`, `${event.clientX + 12}px`)
                            .style(`top`, `${event.clientY - 12}px`);
                    })
                    .on(`mouseleave`, function() {
                        d3.select(this)
                            .attr(`opacity`, 0.9)
                            .attr(`stroke-width`, 1);
                        tooltip.style(`display`, `none`);
                    });

                return true;
            }
            /**
             * render selected skill distribution summary tiles
             * @param {object} scoreObj page data object of one skill
             * @param {Array<{name: string, number: number}>} scoreObj.distribution score distribution items
             * @returns {boolean} true
             */
            function setDistTiles(scoreObj) {
                const groupEles = pageElements.canvas.main.scores.extra.groups;
                groupEles.div.style.display = `grid`;

                // Keep score parsing aligned with the distribution plot: valid 0-1000 values only.
                const distribution = Array.isArray(scoreObj?.distribution) ? scoreObj.distribution : [];
                const scores = distribution
                    .map(item => Number(item?.number))
                    .filter(number => Number.isFinite(number))
                    .map(number => Math.min(Math.max(number, 0), 1000))
                    .sort((a, b) => a - b);

                /**
                 * return an interpolated percentile from a sorted score array
                 * @param {number[]} sortedScores low-to-high score array
                 * @param {number} p percentile as 0-1
                 * @returns {number} percentile score
                 */
                function getPercentile(sortedScores, p) {
                    if (sortedScores.length === 1) return sortedScores[0];

                    const index = (sortedScores.length - 1) * p;
                    const lower = Math.floor(index);
                    const upper = Math.ceil(index);
                    const weight = index - lower;

                    return sortedScores[lower] * (1 - weight) + sortedScores[upper] * weight;
                }

                /**
                 * return arithmetic mean for an array of scores
                 * @param {number[]} scoreList score values
                 * @returns {number} average score
                 */
                function getAverage(scoreList) {
                    return scoreList.reduce((sum, number) => sum + number, 0) / scoreList.length;
                }

                /**
                 * format scores as whole-number display text
                 * @param {number} score score value
                 * @returns {string} rounded score text
                 */
                function formatScore(score) {
                    return Math.round(score).toLocaleString();
                }

                if (scores.length === 0) {
                    [
                        groupEles.bottom.quartile.data,
                        groupEles.bottom.avg.data,
                        groupEles.top.quartile.data,
                        groupEles.top.avg.data,
                    ].forEach(ele => { ele.textContent = `--`; });

                    return true;
                }

                const decileCount = Math.max(1, Math.ceil(scores.length * 0.1));
                const bottomQuartile = getPercentile(scores, 0.25);
                const topQuartile = getPercentile(scores, 0.75);
                const bottomDecileAvg = getAverage(scores.slice(0, decileCount));
                const topDecileAvg = getAverage(scores.slice(-decileCount));

                // set bottom
                groupEles.bottom.quartile.data.textContent = formatScore(bottomQuartile);
                groupEles.bottom.avg.data.textContent = formatScore(bottomDecileAvg);

                // set top
                groupEles.top.quartile.data.textContent = formatScore(topQuartile);
                groupEles.top.avg.data.textContent = formatScore(topDecileAvg);

                // set theme color
                groupEles.bottom.div.style.backgroundColor = convertToRGBA(scoreObj.theme.color.highlight, 0.1);
                groupEles.top.div.style.backgroundColor = convertToRGBA(scoreObj.theme.color.highlight, 0.1);

                return true;
            }

            // on click
            layer1Div.addEventListener(`click`, function() {
                if (!hasDragged) {
                    // show info div
                    pageElements.canvas.main.scores.extra.info.div.style.display = `flex`;
                    pageElements.canvas.main.scores.extra.info.div.style.visibility = `hidden`;

                    // set extra div color
                    pageElements.canvas.main.scores.extra.div.style.borderColor = item.theme.color.highlight;
                    pageElements.canvas.main.scores.extra.div.style.display = `flex`;

                    // set construct circle
                    setCircle(item);

                    // set feedback
                    setFeedback(item);

                    // set distribution
                    setDistribution(item);

                    // set distribution tiles
                    setDistTiles(item);

                    // scroll to info
                    pageElements.canvas.main.scores.extra.info.div.style.visibility = `visible`;
                    scrollToElement(pageElements.canvas.main.scores.extra.info.div, pageElements.canvas.parent, { offsetTop: 25, });
                }
            });

            // render radar
            radarDiv.id = `CRl1R`;
            generateRadarChart(
                (item.num.mid / 1000) * 100,
                radarDiv.id,
                item.theme.color.highlight,
                convertToRGBA(item.theme.color.highlight, 0.2),
            );

            // for lines
            layer1Div.style.zIndex = '1';

            // LAYER 2
            // measure prototypes once
            let r2p = layer2Div.getBoundingClientRect(),
                r3p = layer3Div.getBoundingClientRect();
            let layer1Radius = Math.hypot(r1.width, r1.height)/2,
                layer2Radius = Math.hypot(r2p.width, r2p.height)/2,
                layer3Radius = Math.hypot(r3p.width, r3p.height)/2;

            // clear canvas except layer 1 
            canvas.removeChild(layer2Div);
            canvas.removeChild(layer3Div);

            // min spacing
            let rad2 = layer1Radius + layer2Radius + 0;
            allPageData.canvas.scores.c0.subConstructs.forEach((item, i) => {
                // position angle
                let a2 = (2*Math.PI / allPageData.canvas.scores.c0.subConstructs.length) * i;

                // clone layer 2 score
                let c2 = layer2Div.cloneNode(true);

                // set position
                c2.style.display = 'flex';
                c2.style.position = 'absolute';
                let x2 = cx + Math.cos(a2)*rad2 - r2p.width/2;
                let y2 = cy + Math.sin(a2)*rad2 - r2p.height/2;
                c2.style.left = `${x2}px`;
                c2.style.top  = `${y2}px`;

                // assign elements
                let circleDiv = c2.querySelector(`[circleDiv]`);
                let radarDiv = c2.querySelector(`[radarDiv]`);
                let scoreDiv = c2.querySelector(`[scoreDiv]`); // around score text
                let score = c2.querySelector(`[score]`);
                let levelDiv = c2.querySelector(`[levelDiv]`);
                let levelIcn = c2.querySelector(`[levelIcon]`);
                let levelTxt = c2.querySelector(`[levelText]`);
                let icon = c2.querySelector(`[iconDiv]`);

                // set text & icon
                score.textContent = item.num.mid; // raw score
                levelTxt.textContent = scoreLvl( // level
                    item.num,
                    `long`,
                    false,
                    true,
                );
                icon.innerHTML = item.icon; // icon

                // set colors
                [icon, ].forEach((ele) => { ele.style.color = item.theme.color.text; });
                scoreDiv.style.backgroundColor = item.theme.color.highlight;
                score.style.color = item.theme.color.contrast;
                circleDiv.style.backgroundColor = convertToRGBA(item.theme.color.highlight, 0.1);
                levelDiv.style.display = `none`; // DONT SHOW

                // help text
                attachHelpText(c2, item.title, `auto`);

                // append (for radar/trend to read)
                canvas.appendChild(c2);

                // on click
                c2.addEventListener(`click`, function() {
                    if (!hasDragged) {
                        // show info div
                        pageElements.canvas.main.scores.extra.info.div.style.display = `flex`;
                        pageElements.canvas.main.scores.extra.info.div.style.visibility = `hidden`;

                        // set extra div color
                        pageElements.canvas.main.scores.extra.div.style.borderColor = item.theme.color.highlight;
                        pageElements.canvas.main.scores.extra.div.style.display = `flex`;

                        // set construct circle
                        setCircle(item);

                        // set feedback
                        setFeedback(item);

                        // set distribution
                        setDistribution(item);

                        // set distribution tiles
                        setDistTiles(item);

                        // scroll to info
                        pageElements.canvas.main.scores.extra.info.div.style.visibility = `visible`;
                        scrollToElement(pageElements.canvas.main.scores.extra.info.div, pageElements.canvas.parent, { offsetTop: 25, });
                    }
                });

                // render radar
                radarDiv.id = `CRl2R${i}`;
                generateRadarChart(
                    (item.num.mid / 1000) * 100,
                    radarDiv.id,
                    item.theme.color.highlight,
                    convertToRGBA(item.theme.color.highlight, 0.2),
                );

                // update for lines
                l2Clones.push(c2);
                l3Clones[i] = [];
                c2.style.zIndex = '1';

                // LAYER 3
                // orbit each layer‑2, but only on the “outside” semicircle
                let subs = item.subConstructs;
                let rad3 = layer2Radius + layer3Radius + 0; // spacing
                let span = Math.PI * 3 / 4;
                let start = a2 - span/2;
                subs.forEach((item2, j) => {
                    // position angle
                    let a3 = subs.length === 1
                        ? a2
                        : start + (j/(subs.length-1)) * span;

                    // clone layer 3 score
                    let c3 = layer3Div.cloneNode(true);

                    // set position
                    c3.style.display = 'flex';
                    c3.style.position = 'absolute';
                    let x0 = cx + Math.cos(a2)*rad2,
                        y0 = cy + Math.sin(a2)*rad2;
                    let x3 = x0 + Math.cos(a3)*rad3 - r3p.width/2;
                    let y3 = y0 + Math.sin(a3)*rad3 - r3p.height/2;
                    c3.style.left = `${x3}px`;
                    c3.style.top = `${y3}px`;

                    // assign elements
                    let circleDiv = c3.querySelector(`[circleDiv]`);
                    let radarDiv = c3.querySelector(`[radarDiv]`);
                    let scoreDiv = c3.querySelector(`[scoreDiv]`); // around score text
                    let score = c3.querySelector(`[score]`);
                    let levelDiv = c3.querySelector(`[levelDiv]`);
                    let levelIcn = c3.querySelector(`[levelIcon]`);
                    let levelTxt = c3.querySelector(`[levelText]`);
                    let icon = c3.querySelector(`[iconDiv]`);

                    // set text & icon
                    score.textContent = item2.num.mid; // raw score
                    levelTxt.textContent = scoreLvl( // level
                        item2.num,
                        `long`,
                        false,
                        true,
                    );
                    icon.innerHTML = item2.icon; // icon

                    // set colors
                    [icon, ].forEach((ele) => { ele.style.color = item2.theme.color.text; });
                    scoreDiv.style.backgroundColor = item2.theme.color.highlight;
                    score.style.color = item2.theme.color.contrast;
                    circleDiv.style.backgroundColor = convertToRGBA(item2.theme.color.highlight, 0.1);
                    levelDiv.style.display = `none`; // DONT SHOW

                    // help text
                    attachHelpText(c3, item2.title, `auto`);

                    // on click
                    c3.addEventListener(`click`, function() {
                        if (!hasDragged) {
                            // show info div
                            pageElements.canvas.main.scores.extra.info.div.style.display = `flex`;
                            pageElements.canvas.main.scores.extra.info.div.style.visibility = `hidden`;

                            // set extra div color
                            pageElements.canvas.main.scores.extra.div.style.borderColor = item2.theme.color.highlight;
                            pageElements.canvas.main.scores.extra.div.style.display = `flex`;

                            // set construct circle
                            setCircle(item2);

                            // set feedback
                            setFeedback(item2);

                            // set distribution
                            setDistribution(item2);

                            // set distribution tiles
                            setDistTiles(item2);

                            // scroll to info
                            pageElements.canvas.main.scores.extra.info.div.style.visibility = `visible`;
                            scrollToElement(pageElements.canvas.main.scores.extra.info.div, pageElements.canvas.parent, { offsetTop: 25, });
                        }
                    });

                    // append (for radar/trend to read)
                    canvas.appendChild(c3);

                    // render radar
                    radarDiv.id = `CRl3R${i}${j}`;
                    generateRadarChart(
                        (item2.num.mid / 1000) * 100,
                        radarDiv.id,
                        item2.theme.color.highlight,
                        convertToRGBA(item2.theme.color.highlight, 0.2),
                    );

                    // update for lines
                    l3Clones[i].push(c3);
                    c3.style.zIndex = '1';
                });
            });

            /* ------- INITIAL AUTO-ZOOM AND FIT ------- */

            // draw lines
            drawLines();

            // compute content bounding box in original coordinates
            let minX=Infinity, minY=Infinity, maxX=-Infinity, maxY=-Infinity;
            Array.from(canvas.children).forEach((el, index) => {
                if (index == 0) return; // skip lines canvas (dinemsions aren't zoomed correctlyuy)
                const x = el.offsetLeft, y = el.offsetTop;
                const w = el.offsetWidth, h = el.offsetHeight;
                minX = Math.min(minX, x);
                minY = Math.min(minY, y);
                maxX = Math.max(maxX, x + w);
                maxY = Math.max(maxY, y + h);
            });
            let contentW = maxX - minX;
            let contentH = maxY - minY;
            let fitScale = Math.min(
                windowDiv.clientWidth/contentW,
                windowDiv.clientHeight/contentH
            );
            scale = clamp(fitScale, getDynMin(), maxScale);
            canvas.style.transform = `scale(${scale})`;

            // center content box in viewport
            let scrollX = ((minX+maxX)/2)*scale - windowDiv.clientWidth/2;
            let scrollY = ((minY+maxY)/2)*scale - windowDiv.clientHeight/2;
            windowDiv.scrollLeft = scrollX;
            windowDiv.scrollTop  = scrollY;

            updateScoresMapSize();

            return true;
        }

        /* ------ section event listeners ------ */

        // score skip button
        pageElements.canvas.main.scores.top.skip.div.addEventListener(`click`, function() {
            scrollToElement(pageElements.canvas.main.themes.div, pageElements.canvas.parent);
        });

        /* ------ left sections event listeners ------ */

        // buttons that change page width
        let pageSizeChange = [
            commonElements.nav.leftNav.move.expandNav.div,
            commonElements.nav.leftNav.move.collapseNav.div,
        ];

        // re-render scores and trend
        pageSizeChange.forEach(ele => {
            ele.addEventListener(`click`, async function() {
                // delay
                await delay(500);

                scheduleRenderScores(0, true);
            });
        });

        // re-render when responsive layout changes the score map dimensions
        if (`ResizeObserver` in window) {
            const scoresResizeObserver = new ResizeObserver(() => {
                scheduleRenderScores(100);
            });
            scoresResizeObserver.observe(pageElements.canvas.main.scores.div);
        }

        /* ------ initial call ------ */

        renderScores();

        return true;
    }
    manageStats();

    return true;
}

/**
 * handles common themes
 * @param {object} commonElements all common objs
 * @param {object} pageElements all page objs
 * @returns {boolean} true
 */
async function manageThemes(commonElements, pageElements) {
    // clear any existing tiles
    pageElements.canvas.main.themes.body.div.innerHTML = ``; // clear parent

    // frag
    let frag = document.createDocumentFragment();

    // render tiles
    allPageData.canvas.themes.data.forEach((tileData, index) => {
        // clone tile
        let tileEle = pageElements.canvas.main.themes.body.tile.div.cloneNode(true);

        // subelements
        let tileEles = {
            div: tileEle,
            title: tileEle.querySelector(`[thmTit]`),
            detail: tileEle.querySelector(`[thmDet]`),
            frequency: {
                div: tileEle.querySelector(`[thmEviDiv]`),
                icon: tileEle.querySelector(`[thmEviIcn]`),
                text: tileEle.querySelector(`[thmEviFre]`),
            },
            evidence: tileEle.querySelector(`[thmSni]`),
        };

        // set title
        tileEles.title.textContent = tileData.title;

        // set detail
        tileEles.detail.innerHTML = tileData.detail;

        // set freq text
        tileEles.frequency.text.textContent = `In ${Math.round(tileData.frequency * 100)}% of responses`;

        // set snippets
        tileEles.evidence.innerHTML = tileData.evidence;

        // help text
        attachHelpText(tileEles.detail, `Theme Detail`, `auto`);
        attachHelpText(tileEles.evidence, `Snippets`, `auto`);

        // append to fragment, not directly to DOM
        frag.appendChild(tileEle);            
    });

    // move
    pageElements.canvas.main.themes.body.div.appendChild(frag);

    return true;
}

/**
 * handles scores comments (compliments, criticisms)
 * @param {object} commonElements all common objs
 * @param {object} pageElements all page objs
 * @returns {boolean} true
 */
async function manageComments(commonElements, pageElements) {
    // compliments
    function manageCompliments() {
        // clear any existing tiles
        pageElements.canvas.main.comments.body.compliments.div.innerHTML = ``; // clear parent

        // frag
        let frag = document.createDocumentFragment();

        // title
        let titleEle = pageElements.canvas.main.comments.body.compliments.title;
        frag.appendChild(titleEle);

        // render tiles
        allPageData.canvas.comments.compliments.tiles.forEach((tileData, index) => {
            // clone tile
            let tileEle = pageElements.canvas.main.comments.body.compliments.tile.div.cloneNode(true);

            // set text
            tileEle.querySelector(`[comTit]`).innerHTML = tileData.title;
            tileEle.querySelector(`[comDet]`).innerHTML = tileData.detail;

            // append to fragment, not directly to DOM
            frag.appendChild(tileEle);            
        });

        // move
        pageElements.canvas.main.comments.body.compliments.div.appendChild(frag);
    }
    manageCompliments();

    // criticisms
    function manageCriticisms() {
        // clear any existing tiles
        pageElements.canvas.main.comments.body.criticisms.div.innerHTML = ``; // clear parent

        // frag
        let frag = document.createDocumentFragment();

        // title
        let titleEle = pageElements.canvas.main.comments.body.criticisms.title;
        frag.appendChild(titleEle);

        // render tiles
        allPageData.canvas.comments.criticisms.tiles.forEach((tileData, index) => {
            // clone tile
            let tileEle = pageElements.canvas.main.comments.body.criticisms.tile.div.cloneNode(true);

            // set text
            tileEle.querySelector(`[comTit]`).innerHTML = tileData.title;
            tileEle.querySelector(`[comDet]`).innerHTML = tileData.detail;

            // append to fragment, not directly to DOM
            frag.appendChild(tileEle);            
        });

        // move
        pageElements.canvas.main.comments.body.criticisms.div.appendChild(frag);
    }
    manageCriticisms();

    return true;
}

/**
 * handles notable ideas review section
 * @param {object} commonElements all common objs
 * @param {object} pageElements all page objs
 * @returns {boolean} true
 */
async function manageReview(commonElements, pageElements) {
    // --------- FUNCTIONS ---------

    /**
     * renders review tile
     * @returns {Boolean} true
     */
    function renderReview() {
        /* ---------- SET DATA ---------- */

        // tile data
        let data = [...allPageData.canvas.review.tiles],
            selection = allPageData.canvas.review.manage.selection;

        /* ---------- SET VISUALS ---------- */

        // check index
        if (!checkReviewIndex(selection)) return;

        // viz manage buttons
        function manageButtons() {
            // manage bar index subtext
            pageElements.canvas.main.review.top.index.textContent = `${selection} of ${data.length}`;

            // get buttons
            let prev = pageElements.canvas.main.review.top.manage.left;
            let next = pageElements.canvas.main.review.top.manage.right;

            // show/hide buttons in certain circumstances
            if (selection == 1) { // min case
                prev.style.display = `none`;
                next.style.display = `flex`;
            } else if (selection == data.length) { // max case
                prev.style.display = `flex`;
                next.style.display = `none`;
            } else { // middle cases
                prev.style.display = `flex`;
                next.style.display = `flex`;
            }

            return true;
        }
        manageButtons();

        // viz data
        function manageData() {
            // tile index
            let tileIndex = selection - 1;

            // data
            pageElements.canvas.main.review.body.title.innerHTML = data[tileIndex].title;
            pageElements.canvas.main.review.body.snippet.textContent = data[tileIndex].evidence;
            pageElements.canvas.main.review.body.detail.innerHTML = data[tileIndex].importance;

            return true;
        }
        manageData();

        return true;
    }

    /**
     * checks whether a review selection number exists.
     * @param {Number} reviewSelection review tile number, 1 to n
     * @returns {Boolean} true if selection exists, false if not
     */
    function checkReviewIndex(reviewSelection) {
        // selection must be a whole number
        if (!Number.isInteger(reviewSelection)) return false;

        // min is 1
        if (reviewSelection < 1) return false;

        // max is length
        if (reviewSelection > allPageData.canvas.review.tiles.length) return false;

        return true;
    }

    // --------- ARROW BUTTONS ---------

    let transFlag = false;
    pageElements.canvas.main.review.top.manage.left.addEventListener(`click`, function() {
        // raise flag
        if (transFlag) return;
        transFlag = true;

        // new index
        let oneLess = allPageData.canvas.review.manage.selection - 1;

        // check 
        if (!checkReviewIndex(oneLess)) return;
        allPageData.canvas.review.manage.selection = oneLess;

        // render
        renderReview();

        // lower flag
        transFlag = false;
    });
    pageElements.canvas.main.review.top.manage.right.addEventListener(`click`, function() {
        // raise flag
        if (transFlag) return;
        transFlag = true;

        // new index
        let oneMore = allPageData.canvas.review.manage.selection + 1;

        // check 
        if (!checkReviewIndex(oneMore)) return;
        allPageData.canvas.review.manage.selection = oneMore;

        // render
        renderReview();

        // lower flag
        transFlag = false;
    });

    // --------- INITIAL RENDER ---------

    renderReview();

    return true;
}

/**
 * handles next steps
 * @param {object} commonElements all common objs
 * @param {object} pageElements all page objs
 * @returns {boolean} true
 */
async function manageNext(commonElements, pageElements) {
    // --------- FUNCTIONS ---------

    /**
     * renders next tile
     * @returns {Boolean} true
     */
    function renderNext() {
        /* ---------- SET DATA ---------- */

        // tile data
        let data = [...allPageData.canvas.next.tiles],
            selection = allPageData.canvas.next.manage.selection;

        /* ---------- SET VISUALS ---------- */

        // viz manage buttons
        function manageButtons() {
            // manage bar index subtext
            pageElements.canvas.main.next.top.index.textContent = `${selection} of ${data.length}`;

            // get buttons
            let prev = pageElements.canvas.main.next.top.manage.left;
            let next = pageElements.canvas.main.next.top.manage.right;

            // show/hide buttons in certain circumstances
            if (selection == 1) { // min case
                prev.style.display = `none`;
                next.style.display = `flex`;
            } else if (selection == data.length) { // max case
                prev.style.display = `flex`;
                next.style.display = `none`;
            } else { // middle cases
                prev.style.display = `flex`;
                next.style.display = `flex`;
            }

            return true;
        }
        manageButtons();

        // viz data
        function manageData() {
            // tile index
            let tileIndex = selection - 1;

            // data
            pageElements.canvas.main.next.body.tag.innerHTML = data[tileIndex].tag;
            pageElements.canvas.main.next.body.title.innerHTML = data[tileIndex].title;
            pageElements.canvas.main.next.body.content.innerHTML = data[tileIndex].content;

            return true;
        }
        manageData();

        return true;
    }

    /**
     * check a new index to see if in acceptable tile range
     * @param {Number} newIndex selection tile index
     * @returns {Boolean} true if pass, false if fail
     */
    function checkIndex(newIndex) {
        // min is 1
        if (newIndex < 1) return false;

        // max is length
        if (newIndex > allPageData.canvas.next.tiles.length) return false;

        return true;
    }

    // --------- ARROW BUTTONS ---------

    let transFlag = false;
    pageElements.canvas.main.next.top.manage.left.addEventListener(`click`, function() {
        // raise flag
        if (transFlag) return;
        transFlag = true;

        // new index
        let oneLess = allPageData.canvas.next.manage.selection - 1;

        // check 
        if (!checkIndex(oneLess)) return;
        allPageData.canvas.next.manage.selection = oneLess;

        // render
        renderNext();

        // lower flag
        transFlag = false;
    });
    pageElements.canvas.main.next.top.manage.right.addEventListener(`click`, function() {
        // raise flag
        if (transFlag) return;
        transFlag = true;

        // new index
        let oneMore = allPageData.canvas.next.manage.selection + 1;

        // check 
        if (!checkIndex(oneMore)) return;
        allPageData.canvas.next.manage.selection = oneMore;

        // render
        renderNext();

        // lower flag
        transFlag = false;
    });

    // --------- INITIAL RENDER ---------

    renderNext();

    return true;
}

/* ------------- ACTION/NAV BUTTONS ------------- */

/**
 * handles take workout element click (left nav bar)
 * @param {object} commonElements all common objs
 * @param {object} pageElements all page objs
 * @returns {boolean} true
 */
async function manageTakeScan(commonElements, pageElements) {
    // common action button click
    commonElements.nav.leftNav.actions.scan.div.addEventListener(`click`, function() { // go to waitlist
        window.location.href = redirectPaths.commonElements.leftNav.actions.scan.div;
    });

    return true;
}

/**
 * handles join class element click (left nav bar)
 * @param {object} commonElements all common objs
 * @param {object} pageElements all page objs
 * @returns {boolean} true
 */
async function manageJoinClass(commonElements, pageElements) {
    // common action button click
    commonElements.nav.leftNav.actions.join.div.addEventListener(`click`, function() { // go to waitlist
        window.location.href = redirectPaths.commonElements.leftNav.actions.scan.div;
    });

    return true;
}

/**
 * sets nav bar shards text.
 * update pageInfo navbar data.
 * @param {object} commonElements common elements obj
 * @returns {boolean} true
 */
async function setShardsText(commonElements) {
    let shardsTxt = commonElements.nav.topNav.shards.num;

    // show text
    shardsTxt.textContent = allPageData.navBar.shards.toLocaleString();
    shardsTxt.style.display = `flex`;

    // save nav bar data
    let navBar = { 
        shards: allPageData.navBar.shards, 
        streaks: allPageData.navBar.streaks,
    };
    updatePageInfo("pageData.navBar", navBar);

    return true;
}

/**
 * sets nav bar streaks text
 * @param {object} commonElements common elements obj
 * @returns {boolean} true
 */
async function setStreaksText(commonElements) {
    console.log(commonElements);
    console.log(allPageData);
    let streaksTxt = commonElements.nav.topNav.streaks.num;

    // show text
    streaksTxt.textContent = allPageData.navBar.streaks.toLocaleString();
    streaksTxt.style.display = `flex`;

    return true;
}

/* -------------------------- PAGE FUNCTIONS: MANAGE -------------------------- */

/**
 * demo user info without auth
 * @returns {boolean} true
 */
function manageAuth() {
    // set default user data
    userData.notifNum = 0;
    userData.onboarding = {
        overall: 4, // on error, set to complete stage
        page: 2, // on error, set to complete stage
    };
    userData.langRole = 1; // 0 = edu student language (eg. class), 1 = prof business language (eg. team) (type: num, default: 0)
    userData.activeRole = 0; // 0 = stu, 1 = inst, 2 = org admin (type: num)
    userData.profileInitials = `EG`; // user's name (type: str)
    userData.timeZone = new Date().getTimezoneOffset(); // user's timezone offset from UTC in min (type: num)

    return true;
}

// root calls
(async function pageRoot() {
    /* -------- BEFORE DOM READY -------- */

    // auth check
    if (!manageAuth()) return;

    // update global vars
    redirectPaths = setCommonRedirectPaths(redirectPaths); // auth, top/left nav bar
    roleTranslation = setRoleTranslation(roleTranslation);
    rankThemes = setRankThemes(rankThemes);

    // run checks to continue forward
    let pageChecks = runPageChecks();
    let userChecks = runUserChecks();
    if (!pageChecks || !userChecks) return;

    // kick off long-running work
    const pageDataPromise = calcPageData(); // fetch/compute page data API

    await domReady();

    /* -------- AFTER DOM READY -------- */

    // manage UX
    const commonElements = await manageCommonElements(); // set loaders and links
    await manageStage1(commonElements, pageDataPromise); // set page visuals

    // AFTER-LOAD: non-critical polish, analytics, heavy images, etc.
    afterLoad().then(() => {
        // update PageInfo redirect items
        updatePageInfoRedirects(redirectPaths);

        // e.g., start non-blocking stuff
        requestIdleCallback?.(() => {
            // precompute hints, warm caches, etc.
            // TEMPORARY: EMPTY
        });
    });
})();

/**
 * manages display visuals and interactions
 * @param {object} commonElements commonElements object
 * @param {Promise} pageDataPromise promise returns true after page data set
 * @returns {boolean} true
 */
async function manageStage1(commonElements, pageDataPromise) {
    // define page elements
    let canvas = document.querySelector(`[canvas]`);
    let pageElements = {
        // canvas elements
        canvas: {
            parent: document.querySelector(`[parentCanvas]`), // scrollable element
            div: canvas,
            share: {
                div: canvas.querySelector(`[shaDiv]`),
                top: {
                    div: canvas.querySelector(`[shaTopDiv]`),
                    icon: canvas.querySelector(`[shaTopIcn]`),
                    title: canvas.querySelector(`[shaTopTit]`),
                    subTitle: canvas.querySelector(`[shaTopSub]`),
                },
                form: {
                    div: canvas.querySelector(`[shaForDiv]`),
                    qr: {
                        div: canvas.querySelector(`[shaForQrDiv]`),
                        copy: {
                            div: canvas.querySelector(`[shaForQrCopDiv]`),
                            icon: canvas.querySelector(`[shaForQrCopIcn]`),
                        },
                        download: {
                            div: canvas.querySelector(`[shaForQrDowDiv]`),
                            icon: canvas.querySelector(`[shaForQrDowIcn]`),
                        },
                    },
                    link: canvas.querySelector(`[shaForLnk]`),
                    pw: canvas.querySelector(`[shaForPas]`),
                },
                info: canvas.querySelector(`[shaInf]`),
            },
            main: {
                div: canvas.querySelector(`[maiDiv]`),
                bannerNew: {
                    div: canvas.querySelector(`[maiBannDiv]`),
                    icon: canvas.querySelector(`[maiBannIcn]`),
                    title: canvas.querySelector(`[maiBannTit]`),
                    note: canvas.querySelector(`[maiBannNot]`),
                },
                actions: {
                    div: canvas.querySelector(`[maiActDiv]`),
                    context: {
                        div: canvas.querySelector(`[maiActConDiv]`),
                        icon: canvas.querySelector(`[maiActConIcn]`),
                        title: canvas.querySelector(`[maiActConTit]`),
                    },
                    share: {
                        div: canvas.querySelector(`[maiActShaDiv]`),
                        icon: canvas.querySelector(`[maiActShaIcn]`),
                        title: canvas.querySelector(`[maiActShaTit]`),
                    },
                },
                takeaways: {
                    div: canvas.querySelector(`[maiTakeDiv]`),
                    top: {
                        div: canvas.querySelector(`[maiTakeTopDiv]`),
                        icon: canvas.querySelector(`[maiTakeTopIcn]`),
                        title: canvas.querySelector(`[maiTakeTopTit]`),
                        subTitle: canvas.querySelector(`[maiTakeTopSub]`),
                        info: {
                            progress: canvas.querySelector(`[maiTakeTopInfPro]`),
                            form: canvas.querySelector(`[maiTakeTopInfFor]`),
                            basic: canvas.querySelector(`[maiTakeTopInfBas]`),
                        },
                    },
                    tiles: {
                        div: canvas.querySelector(`[maiTakeTilDiv]`),
                        tile: {
                            div: canvas.querySelector(`[maiTakeTilTilDiv]`),
                        },
                    },
                    bottomLine: {
                        div: canvas.querySelector(`[maiTakeBotDiv]`),
                        content: canvas.querySelector(`[maiTakeBotCon]`),
                    },
                },
                scores: {
                    div: canvas.querySelector(`[scrDiv]`),
                    top: {
                        div: canvas.querySelector(`[scrTopDiv]`),
                        title: canvas.querySelector(`[scrTopTit]`),
                        subTitle: canvas.querySelector(`[scrTopSub]`),
                        skip: {
                            div: canvas.querySelector(`[skipDiv]`),
                        },
                    },
                    window: canvas.querySelector(`[scrWind]`),
                    canvas: {
                        div: canvas.querySelector(`[scrCnv]`), // string used in score render function
                        level1: {
                            div: canvas.querySelector(`[scrLvl1]`),
                        },
                        level2: {
                            div: canvas.querySelector(`[scrLvl2]`),
                        },
                        level3: {
                            div: canvas.querySelector(`[scrLvl3]`),
                        },
                    },
                    extra: {
                        div: canvas.querySelector(`[scrExtDiv]`),
                        info: {
                            div: canvas.querySelector(`[scrInfDiv]`),
                            construct: {
                                div: canvas.querySelector(`[scrInfConDiv]`),
                                titleText: canvas.querySelector(`[scrInfConTit]`),
                                circle: {
                                    div: canvas.querySelector(`[scrInfConCirDiv]`),
                                    radar: canvas.querySelector(`[scrInfConCirRad]`),
                                    icon: canvas.querySelector(`[scrInfConCirIcn]`),
                                    scoreDiv: canvas.querySelector(`[scrInfConCirScrDiv]`),
                                    scoreText: canvas.querySelector(`[scrInfConCirScrTxt]`),
                                },
                                range: {
                                    div: canvas.querySelector(`[scrInfConRngDiv]`),
                                    band: canvas.querySelector(`[scrInfConRngBan]`),
                                    lowText: canvas.querySelector(`[scrInfConRngLow]`),
                                    highText: canvas.querySelector(`[scrInfConRngHig]`),
                                },
                            },
                            feedback: {
                                div: canvas.querySelector(`[scrInfFeeDiv]`),
                                meaning: {
                                    div: canvas.querySelector(`[scrInfFeeMeaDiv]`),
                                    top: {
                                        div: canvas.querySelector(`[scrInfFeeMeaTopDiv]`),
                                    },
                                    content: canvas.querySelector(`[scrInfFeeMeaCon]`),
                                },
                                compliments: {
                                    div: canvas.querySelector(`[scrInfFeeCompDiv]`),
                                    top: {
                                        div: canvas.querySelector(`[scrInfFeeCompTopDiv]`),
                                    },
                                    content: canvas.querySelector(`[scrInfFeeCompCon]`),
                                },
                                criticisms: {
                                    div: canvas.querySelector(`[scrInfFeeCritDiv]`),
                                    top: {
                                        div: canvas.querySelector(`[scrInfFeeCritTopDiv]`),
                                    },
                                    content: canvas.querySelector(`[scrInfFeeCritCon]`),
                                },
                                definition: {
                                    div: canvas.querySelector(`[scrInfFeeDefDiv]`),
                                    top: {
                                        div: canvas.querySelector(`[scrInfFeeDefTopDiv]`),
                                    },
                                    content: canvas.querySelector(`[scrInfFeeDefCon]`),
                                },
                            },
                        },
                        distribution: {
                            div: canvas.querySelector(`[scrDistDiv]`),
                        },
                        groups: {
                            div: canvas.querySelector(`[scrGroDiv]`),
                            bottom: {
                                div: canvas.querySelector(`[scrGroBotDiv]`),
                                quartile: {
                                    div: canvas.querySelector(`[scrGroBotQuaDiv]`),
                                    label: canvas.querySelector(`[scrGroBotQuaLab]`),
                                    data: canvas.querySelector(`[scrGroBotQuaDat]`),
                                },
                                avg: {
                                    div: canvas.querySelector(`[scrGroBotAvgDiv]`),
                                    label: canvas.querySelector(`[scrGroBotAvgLab]`),
                                    data: canvas.querySelector(`[scrGroBotAvgDat]`),
                                },
                            },
                            top: {
                                div: canvas.querySelector(`[scrGroTopDiv]`),
                                quartile: {
                                    div: canvas.querySelector(`[scrGroTopQuaDiv]`),
                                    label: canvas.querySelector(`[scrGroTopQuaLab]`),
                                    data: canvas.querySelector(`[scrGroTopQuaDat]`),
                                },
                                avg: {
                                    div: canvas.querySelector(`[scrGroTopAvgDiv]`),
                                    label: canvas.querySelector(`[scrGroTopAvgLab]`),
                                    data: canvas.querySelector(`[scrGroTopAvgDat]`),
                                },
                            },
                        },
                    },
                },
                themes: {
                    div: canvas.querySelector(`[maiThemDiv]`),
                    top: {
                        div: canvas.querySelector(`[maiThemTopDiv]`),
                        icon: canvas.querySelector(`[maiThemTopIcn]`),
                        title: canvas.querySelector(`[maiThemTopTit]`),
                        subTitle: canvas.querySelector(`[maiThemTopSub]`),
                    },
                    body: {
                        div: canvas.querySelector(`[maiThemBodDiv]`),
                        tile: {
                            div: canvas.querySelector(`[maiThemBodTilDiv]`),
                        },
                    },
                },
                comments: {
                    div: canvas.querySelector(`[maiComDiv]`),
                    top: {
                        div: canvas.querySelector(`[maiComTopDiv]`),
                        icon: canvas.querySelector(`[maiComTopIcn]`),
                        title: canvas.querySelector(`[maiComTopTit]`),
                        subTitle: canvas.querySelector(`[maiComTopSub]`),
                    },
                    body: {
                        div: canvas.querySelector(`[maiComBodDiv]`),
                        compliments: {
                            div: canvas.querySelector(`[maiComBodComDiv]`),
                            title: canvas.querySelector(`[maiComBodComTit]`),
                            tile: {
                                div: canvas.querySelector(`[maiComBodComTilDiv]`),
                            },
                        },
                        criticisms: {
                            div: canvas.querySelector(`[maiComBodCriDiv]`),
                            title: canvas.querySelector(`[maiComBodCriTit]`),
                            tile: {
                                div: canvas.querySelector(`[maiComBodCriTilDiv]`),
                            },
                        },
                    },
                },
                review: {
                    div: canvas.querySelector(`[maiRvwDiv]`),
                    top: {
                        title: canvas.querySelector(`[maiRvwHedTit]`),
                        subText: canvas.querySelector(`[maiRvwHedSub]`),
                        index: canvas.querySelector(`[maiRvwHedInd]`),
                        manage: {
                            div: canvas.querySelector(`[maiRvwHedMngDiv]`),
                            left: canvas.querySelector(`[maiRvwHedMngLef]`),
                            right: canvas.querySelector(`[maiRvwHedMngRht]`),
                        },
                    },
                    body: {
                        div: canvas.querySelector(`[maiRvwBodDiv]`),
                        title: canvas.querySelector(`[maiRvwBodTit]`),
                        snippet: canvas.querySelector(`[maiRvwBodSni]`),
                        subTitle: canvas.querySelector(`[maiRvwBodSub]`),
                        detail: canvas.querySelector(`[maiRvwBodDet]`),
                    },
                },
                next: {
                    div: canvas.querySelector(`[maiNxtDiv]`),
                    top: {
                        title: canvas.querySelector(`[maiNxtHedTit]`),
                        subText: canvas.querySelector(`[maiNxtHedSub]`),
                        index: canvas.querySelector(`[maiNxtHedInd]`),
                        manage: {
                            div: canvas.querySelector(`[maiNxtHedMngDiv]`),
                            left: canvas.querySelector(`[maiNxtHedMngLef]`),
                            right: canvas.querySelector(`[maiNxtHedMngRht]`),
                        },
                    },
                    body: {
                        div: canvas.querySelector(`[maiNxtBodDiv]`),
                        tag: canvas.querySelector(`[maiNxtBodTag]`),
                        title: canvas.querySelector(`[maiNxtBodTit]`),
                        content: canvas.querySelector(`[maiNxtBodCon]`),
                    },
                },
            },
        },

        // popup elements
        popups: {
            div: document.querySelector(`[popDiv]`),
            error: {
                div: document.querySelector(`[popErrDiv]`),
                bubble: {
                    div: document.querySelector(`[popErrBDiv]`),
                    exit: document.querySelector(`[popErrBExt]`),
                    icon: {
                        div: document.querySelector(`[popErrBIcnDiv]`),
                        pic: document.querySelector(`[popErrBIcnPic]`),
                    },
                    title: {
                        div: document.querySelector(`[popErrBTitDiv]`),
                        text: document.querySelector(`[popErrBTitTxt]`),
                    },
                    message: {
                        div: document.querySelector(`[popErrBMsgDiv]`),
                        text: document.querySelector(`[popErrBMsgTxt]`),
                    },
                    bottom: {
                        div: document.querySelector(`[popErrBBotDiv]`),
                        button: {
                            div: document.querySelector(`[popErrBBotButDiv]`),
                            text: document.querySelector(`[popErrBBotButTxt]`),
                        },
                    },
                },
            },
        },
    };

    // define help text elements - MIRROR pageElements STRUCTURE
    let pageHelpTextElements = {
        // canvas elements
        canvas: {
            parent: [],
            div: [],
            share: {
                div: [],
                top: {
                    div: [],
                    icon: [],
                    title: [],
                    subTitle: [],
                },
                form: {
                    div: [],
                    qr: {
                        div: [],
                        copy: {
                            div: [],
                            icon: [],
                        },
                        download: {
                            div: [],
                            icon: [],
                        },
                    },
                    link: [`Reflections 2.0 Form`, `auto`],
                    pw: [`Form Password`, `auto`],
                },
                info: [],
            },
            main: {
                div: [],
                bannerNew: {
                    div: [`Refresh`, `auto`],
                    icon: [],
                    title: [],
                    note: [],
                },
                actions: {
                    div: [],
                    context: {
                        div: [`Copy Context for Prompt`, `auto`],
                        icon: [],
                        title: [],
                    },
                    share: {
                        div: [`Send Results to Others`, `auto`],
                        icon: [],
                        title: [],
                    },
                },
                takeaways: {
                    div: [],
                    top: {
                        div: [],
                        icon: [],
                        title: [],
                        subTitle: [],
                        info: {
                            progress: [],
                            form: [`Reflection 2.0 Form`, `auto`],
                            basic: [`Basic Info`, `auto`],
                        },
                    },
                    tiles: {
                        div: [],
                        tile: {
                            div: [],
                        },
                    },
                    bottomLine: {
                        div: [],
                        content: [`Bottom Line`, `auto`],
                    },
                },
                scores: {
                    div: [],
                    top: {
                        div: [],
                        title: [],
                        subTitle: [],
                        skip: {
                            div: [`Skip Section`, `auto`],
                        },
                    },
                    window: [],
                    canvas: {
                        div: [],
                        level1: {
                            div: [],
                        },
                        level2: {
                            div: [],
                        },
                        level3: {
                            div: [],
                        },
                    },
                    extra: {
                        div: [],
                        info: {
                            div: [],
                            construct: {
                                div: [],
                                titleText: [`Skill Title`, `auto`],
                                circle: {
                                    div: [`Skill Score`, `auto`],
                                    radar: [],
                                    icon: [],
                                    scoreDiv: [],
                                    scoreText: [],
                                },
                                range: {
                                    div: [`Range`, `auto`],
                                    band: [],
                                    lowText: [`Min`, `auto`],
                                    highText: [`Max`, `auto`],
                                },
                            },
                            feedback: {
                                div: [],
                                meaning: {
                                    div: [`Score Meaning`, `auto`],
                                    top: {
                                        div: [],
                                    },
                                    content: [],
                                },
                                compliments: {
                                    div: [`Strengths`, `auto`],
                                    top: {
                                        div: [],
                                    },
                                    content: [],
                                },
                                criticisms: {
                                    div: [`Weaknesses`, `auto`],
                                    top: {
                                        div: [],
                                    },
                                    content: [],
                                },
                                definition: {
                                    div: [`Skill Definition`, `auto`],
                                    top: {
                                        div: [],
                                    },
                                    content: [],
                                },
                            },
                        },
                        distribution: {
                            div: [`Distribution`, `auto`],
                        },
                        groups: {
                            div: [],
                            bottom: {
                                div: [],
                                quartile: {
                                    div: [],
                                    label: [],
                                    data: [`Score`, `auto`],
                                },
                                avg: {
                                    div: [],
                                    label: [],
                                    data: [`Score`, `auto`],
                                },
                            },
                            top: {
                                div: [],
                                quartile: {
                                    div: [],
                                    label: [],
                                    data: [`Score`, `auto`],
                                },
                                avg: {
                                    div: [],
                                    label: [],
                                    data: [`Score`, `auto`],
                                },
                            },
                        },
                    },
                },
                themes: {
                    div: [],
                    top: {
                        div: [],
                        icon: [],
                        title: [],
                        subTitle: [],
                    },
                    body: {
                        div: [],
                        tile: {
                            div: [],
                        },
                    },
                },
                comments: {
                    div: [],
                    top: {
                        div: [],
                        icon: [],
                        title: [],
                        subTitle: [],
                    },
                    body: {
                        div: [],
                        compliments: {
                            div: [`Constructive Compliments`, `auto`],
                            title: [],
                            tile: {
                                div: [],
                            },
                        },
                        criticisms: {
                            div: [`Constructive Criticisms`, `auto`],
                            title: [],
                            tile: {
                                div: [],
                            },
                        },
                    },
                },
                review: {
                    div: [],
                    top: {
                        title: [],
                        subText: [],
                        index: [],
                        manage: {
                            div: [],
                            left: [`Prev`, `auto`],
                            right: [`Next`, `auto`],
                        },
                    },
                    body: {
                        div: [],
                        title: [],
                        snippet: [`Answer Snippet`, `auto`],
                        subTitle: [],
                        detail: [`Importance`, `auto`],
                    },
                },
                next: {
                    div: [],
                    top: {
                        title: [],
                        subText: [],
                        index: [],
                        manage: {
                            div: [],
                            left: [`Prev`, `auto`],
                            right: [`Next`, `auto`],
                        },
                    },
                    body: {
                        div: [`Action Rec`, `auto`],
                        tag: [],
                        title: [],
                        content: [],
                    },
                },
            },
        },

        // popup elements
        popups: {
            div: [],
            error: {
                div: [],
                bubble: {
                    div: [],
                    exit: [`Exit`, `auto`],
                    icon: {
                        div: [],
                        pic: [],
                    },
                    title: {
                        div: [],
                        text: [],
                    },
                    message: {
                        div: [],
                        text: [],
                    },
                    bottom: {
                        div: [],
                        button: {
                            div: [`Go`, `auto`],
                            text: [],
                        },
                    },
                },
            },
        },
    };

    // parallel functions
    let stage1Functs = [
        // attach help text to page elements
        {
            fn: processElementTreeWithHelp,
            args: [pageElements, pageHelpTextElements]
        },
        // render initial canvas elements and hide loader
        {
            fn: renderCanvasEles,
            args: [commonElements, pageElements]
        },
    ];

    // check page data
    let pgProm = await pageDataPromise;
    if (!pgProm) return;

    // run visuals
    await callParallelFunctions(stage1Functs);

    return true;
}
